/**
 * App-wide strings (EN / ES / FR). Merged into `dictionaries.ts`.
 *
 * Voice: contractor-first, calm, direct. Short labels; helper text only for format,
 * consequence, or non-obvious behavior. Prefer “quote” in customer-facing language.
 * Avoid filler (“simply”, “seamless”), stacked synonyms, and internal product names in
 * customer paths unless they fix a real support issue. ES/FR = same meaning, natural UI
 * phrasing—not literal calque.
 */

export const EXTENDED_EN: Record<string, string> = {
  "common.tryAgain": "Try again",
  "common.requestFailed": "Request failed",
  "common.sending": "Sending…",
  "common.signingIn": "Signing in…",
  "common.redirecting": "Redirecting…",
  "common.loadingEllipsis": "Loading…",
  "common.account": "Account",
  "common.or": "Or",
  "common.add": "Add",
  "common.name": "Name",
  "common.email": "Email",
  "common.password": "Password",
  "common.phone": "Phone",
  "common.save": "Save",
  "common.delete": "Delete",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.back": "Back",
  "common.next": "Next",
  "common.optionalSignIn": "(optional sign-in)",

  "auth.signIn": "Sign in",
  "auth.signInSubtitle": "",
  "auth.signInTitleFallback": "Sign in",
  "auth.tabPassword": "Password",
  "auth.tabMagic": "Magic link",
  "auth.forgotPassword": "Forgot password?",
  "auth.signInButton": "Sign in",
  "auth.emailMagicLink": "Email me a magic link",
  "auth.continueGoogle": "Continue with Google",
  "auth.newHere": "New here?",
  "auth.createAccount": "Create an account",
  "auth.moreOptions": "More options",
  "auth.crewMember": "Crew or subcontractor calendar access code?",
  "auth.enterTeamCode": "Open team calendar",
  "auth.crewInviteLead": "Crew member or subcontractor?",
  "auth.crewInviteCta": "Invite code, then sign in",
  "auth.inviteCodeLead": "Co-operator invite code?",
  "auth.joinWithCode": "Join workspace",
  "auth.homeownerPortal": "Customer with a quote link?",
  "auth.customerPortal": "Customer portal",
  "auth.checkEmail": "Check your email",
  "auth.checkEmailCodeHint":
    "On another device? Skip the email button and enter the {{n}}-digit code below (numbers only).",
  "auth.magicEnterCodeTitle": "Enter your sign-in code",
  "auth.magicEnterCodeLeadPrefix": "We emailed a verification code to",
  "auth.magicEnterCodeLeadSuffix":
    "Enter all {{n}} digits in the box below (numbers only). Use this if the link won’t open—another device or an in-app mail browser.",
  "auth.magicCodePlaceholder": "Code from email",
  "auth.magicCodeFieldHint": "{{n}} digits, numbers only",
  "auth.magicVerifySubmit": "Verify and sign in",
  "auth.magicResend": "Email a new code",
  "auth.magicBack": "Use a different email",
  "auth.magicInvalidCode": "Enter all {{n}} digits from the email (numbers only).",
  "auth.deviceOtpLeadStart": "Enter the {{n}}-digit verification code we sent to",
  "auth.deviceOtpToastDesc": "We sent an {{n}}-digit code to verify this device.",
  "auth.deviceOtpEnterFull": "Enter all {{n}} digits (numbers only).",
  "auth.idleSessionTitle": "Session ended from inactivity",
  "auth.idleSessionBody": "For security, please sign in again.",
  "auth.signInFailed": "Sign in failed",
  "auth.googleSignInFailed":
    "Google sign-in didn’t finish. Try again, or sign in with email.",
  "auth.callbackErrorTitle": "We couldn't complete that sign-in link.",
  "auth.callbackErrorBody":
    "Try Magic link again and enter the code from your email. If this came from an email app preview, open {{origin}}/login in Safari or Chrome and sign in with code or password.",
  "auth.validEmail": "Enter a valid email",
  "auth.enterPassword": "Enter your password",
  "auth.placeholderEmail": "you@company.com",
  "auth.forgotTitle": "Reset password",
  "auth.forgotSubtitle": "We'll email a reset link to the address you enter below.",
  "auth.forgotSend": "Send email",
  "auth.updatePasswordTitle": "Set new password",
  "auth.updatePasswordSubtitle": "Choose a strong password for your account.",
  "auth.passwordUpdated": "Password updated",
  "auth.passwordUpdatedDesc": "You're signed in. Redirecting…",
  "auth.newPassword": "New password",
  "auth.confirmPassword": "Confirm password",
  "auth.forgotCardDescription":
    "We’ll email a link to set a new password.",
  "auth.forgotSentDescription":
    "Check your inbox and spam. Open the reset link in Safari, Chrome, or Edge.",
  "auth.forgotAfterText":
    "If that email is in our system, you’ll get a link shortly. After you reset, sign in from the login page.",
  "auth.backToSignIn": "Back to sign in",
  "auth.updatePasswordCardDescription": "Choose a new password for your account.",

  "signup.passwordTab": "Password",
  "signup.magicTab": "Magic link",
  "signup.createButton": "Create account",
  "signup.fullName": "Full name",
  "signup.companyName": "Company name",
  "signup.addressLine": "Address (optional)",
  "signup.city": "City",
  "signup.state": "State",
  "signup.zip": "ZIP",
  "signup.passwordMin": "At least 8 characters",
  "signup.passwordsMustMatch": "Passwords must match",
  "signup.nameRequired": "Name is required",
  "signup.companyRequired": "Company name is required",
  "signup.selectTrade": "Select your trade",
  "signup.tradeOtherHint": "Enter your type of business or specialty",
  "signup.hasAccount": "Already have an account?",
  "signup.signInLink": "Sign in",

  "topbar.settings": "Settings",
  "topbar.signOut": "Sign out",
  "topbar.accountMenu": "Account menu",
  "topbar.openHelp": "Help center",
  "topbar.takeTour": "Take a tour",
  "topbar.fullAppTour": "Full app tour",

  "customers.title": "Customers",
  "customers.subtitle": "One contact record per person you quote.",
  "customers.searchPlaceholder": "Search by name, email, or phone…",
  "customers.searchAria": "Search customers",
  "customers.searchEmpty": "No customers match \u201C{{query}}\u201D.",
  "customers.searchEmptyHint": "Try a different name, email, or phone number.",
  "customers.searchClear": "Clear search",
  "customers.emptyDesc": "Add your first customer to start quoting.",
  "customers.addFailed": "Could not add customer",
  "customers.emailRequired": "Enter an email address for this customer.",
  "customers.emailLabelRequired": "Email (required)",
  "customers.phoneLabelOptional": "Phone (optional)",
  "customers.added": "Customer added",
  "customers.phonePlaceholder": "(555) 123-4567",
  "customers.emailPlaceholder": "jane@email.com",
  "customers.namePlaceholder": "Jane Smith",
  "customers.deleteFailed": "Could not delete customer",
  "customers.removed": "Customer removed",
  "customers.actionsAria": "Customer actions",
  "customers.saveFailed": "Could not save",
  "customers.saved": "Saved",
  "customers.deleteCustomerFailed": "Could not delete",
  "customers.detailTitle": "Customer",
  "customers.backToList": "Back to customers",
  "customers.quoteCountNone": "No quotes",
  "customers.quoteCountOne": "1 quote",
  "customers.quoteCountMany": "{{count}} quotes",
  "customers.viewDetails": "View details",
  "customers.delete": "Delete",
  "customers.noContactOnFile": "No contact on file",
  "customers.deleteConfirmTitle": "Delete customer?",
  "customers.deleteConfirmDesc":
    "Remove {{name}} from your list. Quotes linked to this customer will keep their history; only the customer record is deleted.",
  "customers.deleting": "Deleting…",
  "customers.addCustomerShow": "Add customer",
  "customers.addCustomerHide": "Hide form",

  "templates.title": "Templates",
  "templates.subtitle": "Reusable quotes without customer details",
  "templates.createFailed": "Could not create quote",
  "templates.confirmDeleteMsg": "Delete this template? This does not affect existing quotes.",
  "templates.emptyDescription":
    "Open a quote → Save as template in the editor to store a structure you reuse without customer-specific details.",
  "templates.createAQuote": "Create a quote",
  "templates.startersSectionTitle": "Starter templates",
  "templates.startersIntro": "Add one to your list, then open it to edit lines and scope.",
  "templates.starterHvac": "HVAC starter",
  "templates.starterPlumbing": "Plumbing starter",
  "templates.starterElectrical": "Electrical starter",
  "templates.starterTrade": "{{trade}} starter",
  "templates.starterAdding": "Adding…",
  "templates.empty": "No templates yet",
  "templates.deleteFailed": "Could not delete",
  "templates.deleted": "Template deleted",
  "templates.notFound": "Template not found",
  "templates.saveFailed": "Save failed",
  "templates.saved": "Template saved",
  "templates.alreadyHaveThisStarter": "You already have this starter",
  "templates.alreadyHaveThisStarterBody":
    "Open it from the list below to edit, or rename your existing copy before adding another.",
  "templates.lineItemsTypicalTotal": "{{count}} line items · Typical total: {{total}}",
  "templates.cardHint":
    "Open the title to edit, start a draft from this template, or delete it.",
  "templates.useForNewQuote": "Use for new quote",
  "templates.creating": "Creating…",
  "templates.edit": "Edit",
  "templates.deleting": "Deleting…",
  "templates.createBlank": "Create template",
  "templates.createBlankWorking": "Creating…",
  "templates.addAnotherHint": "Build another reusable quote structure for different job types.",
  "templates.premadeSectionTitle": "Add another starter",
  "templates.premadeSectionDescription":
    "Same shells as when your list was empty. Pick a trade; you can edit anytime.",
  "auth.savePassword": "Save password",

  "templates.editBack": "← Back to templates",
  "templates.editDeleteButton": "Delete template",
  "templates.editHeading": "Edit template",
  "templates.editQuoteTitleLabel": "Quote title used in new drafts",
  "templates.editQuoteTitlePlaceholder": "e.g. Kitchen remodel — interior",
  "templates.editSummaryScopeCard": "Summary & scope",
  "templates.editSummaryLabel": "Summary",
  "templates.editSummaryPlaceholder": "Short overview the customer sees at the top of the quote.",
  "templates.editScopeLabel": "Scope of work",
  "templates.editScopePlaceholder": "Describe the work, materials, and anything the customer should know.",
  "templates.editSummaryHint":
    "New quotes started from this template start with this summary and scope.",
  "templates.editLineItemsCard": "Line items",
  "templates.editTermsCard": "Terms & notes",
  "templates.editTaxRate": "Tax rate",
  "templates.editDiscount": "Discount ($)",
  "templates.editValidUntil": "Valid until",
  "templates.editDeposit": "Deposit / first payment (%)",
  "templates.editPaymentTerms": "Payment terms",
  "templates.editWarranty": "Warranty",
  "templates.editNotes": "Notes",
  "templates.saveChanges": "Save changes",

  "quotePaymentTerms.fullWithTotal": "Pay {{total}} in one secure checkout below.",
  "quotePaymentTerms.fullGeneric":
    "The full estimate total is due in one secure payment when you complete checkout below.",
  "quotePaymentTerms.splitLine1WithAmounts":
    "{{pct}}% of the estimate total ({{total}}) is due now — {{deposit}}. This checkout only charges that {{pct}}% — not the full {{total}}.",
  "quotePaymentTerms.splitLine2WithAmounts":
    "The remaining {{rest}}% ({{balance}}) is due in a separate payment later (for example when the work is finished). You'll use this same estimate page for the balance when your contractor is ready.",
  "quotePaymentTerms.splitLine1Generic":
    "{{pct}}% of the estimate total is due now. This checkout only charges that {{pct}}% — not the full amount.",
  "quotePaymentTerms.splitLine2Generic":
    "The remaining {{rest}}% is due in a separate payment later (for example when the work is finished). You'll use this same estimate page for the balance when your contractor is ready.",

  "decision.waitCostPara1":
    "Waiting to approve can move the next start window—another job may take that slot.",
  "decision.waitCostPara2WithTotal":
    "This total ({{total}}) matches this scope. If scope or market pricing shifts before you accept, the next revision may change.",
  "decision.waitCostPara2NoTotal":
    "If scope or material pricing shifts before you accept, the next revision may show different numbers.",
  "decision.waitCostExpiry":
    "If this quote has an expiration date, waiting past it may require an updated estimate.",

  "calendarPage.title": "Calendar",
  "calendarPage.subtitle":
    "Tentative quotes and confirmed jobs in one calendar.",

  "calendar.previous": "Previous",
  "calendar.today": "Today",
  "calendar.addEntry": "Add calendar entry",
  "calendar.next": "Next",
  "calendar.loadingEvents": "Loading events…",
  "calendar.noJobsDay": "No jobs scheduled for this day.",
  "calendar.dayPeekEmpty": "Nothing scheduled this day.",
  "calendar.dayTitleFallback": "Day",
  "calendar.eventFallback": "Event",
  "calendar.moreCount": "+{{count}} more",
  "calendar.peekSrOnly": "View jobs and crew for this day",
  "calendar.legendTentative": "Job tentative",
  "calendar.legendConfirmed": "Job confirmed",
  "calendar.legendMaterial": "Material delivery",
  "calendar.legendSub": "Subcontractor",
  "calendar.legendCancelled": "Cancelled",
  "calendar.teamTitle": "Team & assignments",
  "calendar.teamDesc":
    "Create a crew code in Settings, then use these names when you assign quotes or calendar jobs.",
  "calendar.teamSettings": "Team code & settings",
  "calendar.teamQuotes": "Open quotes to assign crew",
  "calendar.rosterHint": "Signed in before (use exact spelling)",
  "calendar.rosterEmpty": "Nobody has used your team code yet — share it from Settings.",
  "calendar.openQuote": "Open quote",
  "calendar.editEntry": "Edit",
  "calendar.dateRangeTo": "to",
  "calendar.dateRangeArrow": "→",
  "calendar.linkedQuoteStatusHint": "Status for quote jobs follows the quote and payment state.",
  "calendar.notesLabel": "Notes (optional)",
  "calendar.notesPlaceholder": "Anything your team should remember",
  "calendar.statusLabel": "Status",
  "calendar.statusTentative": "Tentative",
  "calendar.statusConfirmed": "Confirmed",
  "calendar.statusCancelled": "Cancelled",
  "calendar.titleLabel": "Title",
  "calendar.startDate": "Start date",
  "calendar.endDate": "End date",
  "calendar.startTime": "Start time",
  "calendar.endTime": "End time",
  "calendar.toastTitleRequired": "Add a title",
  "calendar.toastPickStart": "Pick a start date",
  "calendar.toastEndAfterStart": "End date must be on or after the start date",
  "calendar.toastInvalidStart": "Pick a valid date and start time",
  "calendar.toastInvalidEndTime": "Pick a valid end time",
  "calendar.toastInvalidEndDate": "Pick a valid end date",
  "calendar.materialBadge": "Material",
  "calendar.subBadge": "Subcontractor",
  "calendar.crewPrefix": "Crew:",
  "calendar.crewNoneQuote": "No one assigned on the quote yet.",
  "calendar.crewNotOnQuote": "not assigned on the quote",
  "calendar.officeOnly": "Office calendar only — not shown on crew calendars.",
  "calendar.materialFromLine": "Material delivery (from quote line)",
  "calendar.subFromCost": "Subcontractor (from cost line)",
  "calendar.customerPrefix": "Customer:",
  "calendar.savingEntry": "Saving…",
  "calendar.saveEntry": "Save",
  "calendar.rawStatusTentative": "tentative",
  "calendar.rawStatusConfirmed": "confirmed",
  "calendar.rawStatusCancelled": "cancelled",
  "calendar.dialogMaterialTitle": "Material delivery (from quote)",
  "calendar.dialogSubTitle": "Subcontractor (from quote)",
  "calendar.dialogEditQuoteSchedule": "Edit schedule (quote job)",
  "calendar.dialogEditEntry": "Edit calendar entry",
  "calendar.dialogNewEntry": "New calendar entry",
  "calendar.hintMaterialSync":
    "This row is synced from a material line’s Expected by date on the quote. Changing date/time here only updates the calendar — edit the line item on the quote to change it permanently.",
  "calendar.hintSubSync":
    "Synced from a cost line with a linked subcontractor. Times are spaced from the quote’s job start; update the quote’s schedule or cost lines to refresh these entries.",
  "calendar.hintQuoteJob":
    "Date and times update the quote’s scheduled start and end. You can set who this job shows for on the team calendar below. Change the title, payment lines, or quote notes on the quote itself.",
  "calendar.titlePlaceholder": "e.g. Site walkthrough, Permit pickup",
  "calendar.assigneeLabel": "Crew / assign to (optional)",
  "calendar.assigneeHintQuote":
    "Comma-separated names (e.g. Mike, Sarah). Spell each name the way they use it with your team code.",
  "calendar.assigneeHintManual":
    "Shows on a crew member’s calendar when the name matches how they signed in with your team code. Use commas between names.",
  "calendar.assigneeHintAux":
    "Who sees this on crew calendars comes from the quote’s Assign to field—edit the quote to change it. Sub visits stay on the office calendar only.",
  "calendar.endTimeOptional": "End time (optional)",
  "calendar.durationHint":
    "Same day + no end time = 1 hour. Spanning days = shows each day in range; last day defaults to 5:00 PM if end time is blank.",
  "calendar.toastEntryAdded": "Entry added",
  "calendar.deleteConfirmJob":
    "Remove this job from the calendar and clear scheduled start/end times on the quote?",
  "calendar.deleteConfirmEntry": "Delete this calendar entry?",
  "calendar.toastDeleteFailed": "Could not delete",
  "calendar.toastEntryRemoved": "Entry removed",
  "calendar.toastCouldNotUpdate": "Could not update entry",
  "calendar.toastCouldNotAdd": "Could not add entry",

  "billing.title": "Billing",
  "billing.subtitle":
    "Your plan, sent quotes this month (email, PDF, or share link), payment method, and subscription status.",
  "billing.introOfferTitle": "Free 7-day trial on every plan",
  "billing.introOfferBody":
    "Starter, Solo, Pro, and Agency all start with a 7-day free trial — no card required. After day 7, add a card and pick a plan to keep creating quotes.",
  "billing.startPlanFirst":
    "Pick a plan to create quotes. You can browse the rest of the app anytime.",
  "billing.ownerBypass": "Owner account detected. Billing gates are bypassed here for testing.",
  "billing.checkoutSuccess":
    "Your subscription update went through. Use Manage subscription below to change your card, view invoices, or cancel anytime.",
  "billing.checkoutCanceled": "Checkout was canceled. Your current plan has not changed.",
  "billing.manageSubscription": "Manage subscription",
  "billing.currentPlan": "Current plan",
  "billing.choosePlan": "Choose a plan",

  "logo.useImageFile": "Use an image file",
  "logo.pngJpgWebp": "PNG, JPG, or WebP.",
  "logo.fileTooLarge": "File too large",
  "logo.max2mb": "Max size is 2 MB.",
  "logo.updated": "Logo updated",

  "diagram.useImage": "Use an image file",
  "diagram.max12": "Max 12 MB.",
  "diagram.max4": "Maximum 4 diagram images",
  "diagram.removeOne": "Remove one to add another.",
  "diagram.added": "Diagram added",
  "diagram.readFailed": "Could not read image",

  "markup.useImage": "Use an image file",
  "markup.previewFailed": "Could not load preview",
  "markup.dimensionsFailed": "Could not read image dimensions",
  "markup.photoAdded": "Photo added",

  "proposalForm.loadCustomersFailed": "Could not load customers",
  "proposalForm.networkError": "Network error.",
  "proposalForm.unsupportedImage": "Unsupported image",
  "proposalForm.useJpgPngWebp": "Use a JPG, PNG, or WebP photo.",
  "proposalForm.imageTooLarge": "Image too large",
  "proposalForm.under6mb": "Use a photo under 6 MB.",
  "proposalForm.transcribeFailed": "Could not transcribe notes",
  "proposalForm.noTextFound": "No text found",
  "proposalForm.clearerPhoto": "Try a clearer photo of your notes.",
  "proposalForm.notesAdded": "Notes added to job description",
  "proposalForm.readPhotoFailed": "Could not read the photo",
  "proposalForm.generated": "Quote generated",
  "proposalForm.choosePlaceholder": "Choose…",
  "proposalForm.customerNamePh": "John Smith",
  "proposalForm.customerEmailPh": "john@email.com",
  "proposalForm.taxUseProfile": "Use profile / AI default",
  "proposalForm.uploadNotesAria": "Upload photo of handwritten notes",
  "proposalForm.jobDescriptionPlaceholder":
    "e.g. Replace 40-gal water heater, copper to PEX, expansion tank, permit. Say labor (hours × rate or flat), subs, access, code. Price parts in the table below.",

  "assistant.placeholder": "What should change on this quote?",

  "quoteHelper.title": "Quote assistant",
  "quoteHelper.errorTitle": "Quote assistant error",
  "quoteHelper.hint":
    "Can adjust lines, scope, totals, terms, notes, and dates. Review changes before you send to the customer.",
  "quoteHelper.opening":
    "Example: change one line, fix a date, or set deposit to 25%.",
  "quoteHelper.placeholder": "What should we update?",
  "quoteHelper.apply": "Apply",
  "quoteHelper.hide": "Hide assistant",
  "quoteHelper.show": "Show quote assistant",
  "quoteHelper.undoLast": "Undo last change",
  "quoteHelper.undoOk": "Restored the previous quote fields.",
  "quoteHelper.moreActionsArchiveNote":
    "Quotes stay in your history. To remove one for good, delete it from Archived quotes under Settings.",
  "quoteHelper.upgradeBlurb":
    "AI quote drafting and the quote assistant (edits on an existing quote) are included on every plan — Starter and Solo get a few AI actions a day, Pro and Agency are unlimited. Start a plan for help drafting from a sentence and adjusting scope, lines, schedule, terms, and pricing.",
  "quoteHelper.liveQuoteBanner":
    "This quote has been shared with the customer. Send again when you want them to get an updated email.",
  "quoteHelper.liveQuoteCalloutTitle": "Customer copy of this quote",
  "quoteHelper.thinking": "Working…",

  "dashboard.team.crewCodeModalTitle": "Team calendar code",
  "dashboard.team.crewCodeModalHint":
    "Crew enter this at /crew to open your company calendar. Rotate or remove access in Settings → Team.",
  "dashboard.team.notConfiguredYet": "No code is set up yet. Generate one under Settings → Team.",
  "dashboard.team.openTeamSettings": "Open Team settings",

  "lineItem.categoryTitle": "Category on the customer estimate (labor, materials, sub, fees).",
  "lineItem.leadTimeTitle":
    "Lead time in days (your planning only; not shown to the customer).",

  "progress.generatingQuote": "Generating quote",

  "brand.homeAria": "Jorvio Quotes home",
  "brand.nameAria": "Jorvio Quotes",

  "archived.title": "Archived quotes",
  "archived.deleteFailed": "Could not delete quote",
  "archived.deleted": "Quote deleted",
  "archived.allDeleted": "All archived quotes deleted",
  "archived.emptyTitle": "No archived quotes",
  "archived.deleteTitle": "Delete archived quote now",

  "followUp.agencyRequired": "Agency required",
  "followUp.agencyOnly": "Custom follow-up automation is available on Agency.",
  "followUp.checkSteps": "Check your automation steps",
  "followUp.stepInvalid": "Each step needs a valid delay and message.",
  "followUp.saveFailed": "Save failed",
  "followUp.saved": "Automation saved",

  "portal.loadFailed": "Could not load quotes",
  "portal.enterEmail": "Enter your email",
  "portal.sendCodeFailed": "Could not send code",
  "portal.codeSent": "Code sent",
  "portal.checkEmail": "Check your email.",
  "portal.enterEmailAndCode": "Enter email and code",
  "portal.signInFailed": "Sign-in failed",
  "portal.invalidCode": "Invalid code",
  "portal.signedIn": "Signed in",
  "portal.signedInDesc": "You can see all quotes sent to this email.",
  "portal.signedOut": "Signed out",

  "publicQuote.paymentReceived": "Payment received",
  "publicQuote.paymentThanks": "Thank you. Your payment was successfully processed.",
  "publicQuote.paymentCanceled": "Payment canceled",
  "publicQuote.noCharge": "No charge was made.",
  "publicQuote.paymentConfirmingTitle": "Confirming your payment…",
  "publicQuote.paymentConfirmingBody":
    "Your card has been charged. We're waiting on the final confirmation from Stripe — this usually takes only a few seconds. Please don't try to pay again.",
  "publicQuote.paymentConfirmingTakingLongerTitle": "Still confirming your payment",
  "publicQuote.paymentConfirmingTakingLongerBody":
    "Stripe is taking a little longer than usual. Your card has only been charged once. Refresh this page in a minute, or contact your contractor if it doesn't update.",
  "publicQuote.couldNotSend": "Could not send",
  "publicQuote.requestSent": "Request sent",
  "publicQuote.contractorFollowUp": "The contractor will follow up with you.",
  "publicQuote.couldNotAnswer": "Could not answer right now",
  "publicQuote.acceptAgreements": "Please accept the platform terms and Stripe payment terms below.",
  "publicQuote.signatureBeforePayTitle": "Sign to authorize payment",
  "publicQuote.signatureBeforePayHint":
    "Draw your signature, tap “Use signature”, then continue to checkout. This applies when you pay the deposit or full amount.",
  "publicQuote.signatureRequired": "Please save your signature before paying.",
  "publicQuote.signatureCaptured": "Signature saved. You can clear and redo if needed.",
  "publicQuote.signatureClear": "Clear",
  "publicQuote.signatureUse": "Use signature",
  "publicQuote.signatureStepIntro":
    "Draw your signature, tap “Use signature”, then continue to Stripe. Clearing the pad clears your saved signature until you save again.",
  "publicQuote.continueToSignatureButton": "Continue to signature — {{amount}}",
  "publicQuote.continueToCheckoutButton": "Continue to secure checkout — {{amount}}",
  "publicQuote.backFromSignatureStep": "Back",
  "publicQuote.paymentStartFailed": "Could not start payment",
  "publicQuote.askPlaceholder":
    "Ask why something is included, what the cheaper option changes, or which option best fits your priorities.",
  "publicQuote.adjustPlaceholder": "What would you like adjusted?",
  "publicQuote.languageViewUpdated": "Page language updated. Quote text is translated for viewing only.",
  "publicQuote.bannerP1":
    "No account needed — you can read this estimate, accept, pay, and message your contractor from this page alone.",
  "publicQuote.bannerPortalLink": "Sign in to the customer portal",
  "publicQuote.bannerP2":
    " with your email (optional) to see every quote contractors have sent you and stay on top of updates in one place.",
  "publicQuote.scopeHeading": "Scope of work",
  "publicQuote.datesHeading": "Important dates & timing",
  "publicQuote.datesIntro":
    "The on-site window below is tentative until you accept this quote and the contractor confirms the schedule.",
  "publicQuote.validUntilPrefix": "Prices offered through ",
  "publicQuote.validUntilSuffix":
    ". This is the last day these line-item prices and totals are offered as written — it is not the same as a job completion date.",
  "publicQuote.tentativeOnsite": "Tentative on-site window: ",
  "publicQuote.startTbd": "Start to be confirmed",
  "publicQuote.to": " to ",
  "publicQuote.contractorNotePrefix": "From your contractor: ",
  "publicQuote.jobSiteHeading": "Job site",
  "publicQuote.photosHeading": "Site photos & plans",
  "publicQuote.pricingHeading": "Pricing",
  "publicOptions.heading": "Choose an option",
  "publicOptions.intro": "Pick the option that works best for you. Your contractor will follow up to finalize the details.",
  "publicOptions.recommended": "Recommended",
  "publicOptions.timeframeLabel": "Time frame",
  "publicOptions.choose": "Choose this option",
  "publicOptions.selected": "Selected",
  "publicOptions.chosenBadge": "Your choice",
  "publicOptions.confirm": "Confirm my choice",
  "publicOptions.confirming": "Saving…",
  "publicOptions.confirmHint": "No payment or signature needed — this just lets your contractor know which option you want.",
  "publicOptions.confirmError": "We couldn't save your choice. Please try again.",
  "publicOptions.confirmedTitle": "Choice sent",
  "publicOptions.confirmedBody": "Thanks! Your contractor has been notified of your selection.",
  "publicOptions.lockedTitle": "You've made your choice — your contractor will follow up.",
  "publicOptions.lockedBody": "You've already chosen an option on this quote.",
  "publicQuote.addonHint":
    "Use Include to add or remove optional upgrades. Your total and checkout update automatically.",
  "publicQuote.itemizedHidden":
    "Itemized line items are not shown on this estimate. The total below reflects your contractor's full quote for this scope.",
  "publicQuote.subtotalLabel": "Subtotal",
  "publicQuote.discountLabel": "Discount",
  "publicQuote.taxLineLabel": "Sales tax ({{rate}}%)",
  "publicQuote.markupLineLabel": "Markup",
  "publicQuote.totalLabel": "Total",
  "publicQuote.paymentRefunded": "Payment was refunded. Contact the contractor if you have questions.",
  "publicQuote.paymentPaidFull": "Paid in full. Thank you!",
  "publicQuote.paymentBelowDecision": "Acceptance and payment are below the comparison section.",
  "publicQuote.statusAcceptedTitle": "Accepted",
  "publicQuote.statusPaidInFull": "Paid in full",
  "publicQuote.statusAcceptedNoPaymentYet": "You've accepted this estimate. Your contractor has been notified.",
  "publicQuote.statusAcceptedBalanceDue": "First payment of {{paid}} received. Balance of {{balance}} is due in a separate payment when the work nears completion — come back to this same page to pay it.",
  "publicQuote.statusAcceptedReturnLater": "You can always come back to this page to view the estimate, pay the next installment, or message your contractor.",
  "publicQuote.changeOrdersHeading": "Change orders",
  "publicQuote.changeOrdersIntro":
    "Additional scope your contractor sent after the original estimate. Accepting adds the amount to your total.",
  "publicQuote.coAwaiting": "Awaiting your approval",
  "publicQuote.coAccepted": "Accepted",
  "publicQuote.coAcceptBtn": "Accept change order",
  "publicQuote.coSaving": "Saving…",
  "publicQuote.decisionHeading": "Compare your options",
  "publicQuote.decisionAskTitle": "Ask about this estimate",
  "publicQuote.decisionGoalClarity": "Help me understand this",
  "publicQuote.decisionGoalBudget": "Best for lower upfront cost",
  "publicQuote.decisionGoalLongevity": "Best long-term value",
  "publicQuote.decisionGoalSpeed": "Best for fastest completion",
  "publicQuote.decisionGoalConfidence": "Help me feel confident choosing",
  "publicQuote.decisionGoalHint": "Pick what matters most, then type or edit your question below.",
  "publicQuote.decisionThinking": "Thinking…",
  "publicQuote.decisionGetGuidance": "Get guidance",
  "publicQuote.decisionSuggestedNext": "Suggested next step:",
  "publicQuote.regretHeading": "Trust and reassurance",
  "publicQuote.regretProtects": "What protects you",
  "publicQuote.regretWhatNext": "What happens next",
  "publicQuote.warrantyHeading": "Warranty",
  "publicQuote.notesHeading": "Notes",
  "publicQuote.acceptHeading": "Accept & payment",
  "publicQuote.acceptIntro": "Review the terms below, then accept the estimate and pay.",
  "publicQuote.acceptRecommended": "Recommended",
  "publicQuote.acceptedTitle": "You accepted this estimate. Thank you!",
  "publicQuote.acceptedRecommended": "Recommended path chosen:",
  "publicQuote.requestChanges": "Request changes",
  "publicQuote.requestChangesTitle": "Request changes",
  "publicQuote.changeRequestDescription": "Tell your contractor what to update on this estimate.",
  "publicQuote.changeRequestFieldLabel": "Requested changes",
  "publicQuote.sendToContractor": "Send to contractor",
  "publicQuote.colInclude": "Include",
  "publicQuote.colDescription": "Description",
  "publicQuote.colQty": "Qty",
  "publicQuote.colUnit": "Unit",
  "publicQuote.transactionFeeLineLabel": "Stripe transaction fee",
  "publicQuote.colPrice": "Price ($)",
  "publicQuote.colTotal": "Total ($)",
  "publicQuote.colType": "Type",
  "publicQuote.colReadyBy": "Ready by date",
  "publicQuote.optionalAddon": "Optional add-on",
  "publicQuote.lineKindLabor": "Labor",
  "publicQuote.lineKindMaterial": "Materials & equipment",
  "publicQuote.lineKindSubcontractor": "Subcontractor",
  "publicQuote.lineKindFee": "Fees & permits",
  "publicQuote.lineKindOther": "Other",
  "publicQuote.lineItemsFallback": "Line items",
  "publicQuote.nextPayFull": "Pay in full",
  "publicQuote.nextPayDeposit": "Pay {{pct}}% deposit",
  "publicQuote.nextPayBalance": "Pay balance",
  "publicQuote.estimatedTotal": "Estimated total {{amount}}",
  "publicQuote.loadTitle": "We couldn't open this estimate",
  "publicQuote.loadNotFound":
    "We do not have a quote for this link. Check the link for typos, or ask your contractor to send a new link.",
  "publicQuote.loadServer":
    "We couldn’t load this quote. Wait a moment and try the link again. If it keeps failing, ask your contractor to resend it or check their side.",
  "publicQuote.loadNetwork": "We could not reach the server. Check your internet connection, then try the link again.",
  "publicQuote.notPayingFullTitle": "You are not paying the full total today",
  "publicQuote.notPayingFullLi1":
    "This checkout only: {{amount}} ({{depositPct}}% of {{total}}). Your card is charged for that amount only — not the full estimate.",
  "publicQuote.notPayingFullLi2":
    "Later: The remaining {{remainder}} ({{balancePct}}%) is paid in a second checkout from this same page, when your contractor is ready.",
  "publicQuote.balanceDueTitle": "Balance due",
  "publicQuote.balanceDueBody":
    "You already paid {{paid}}. This checkout is only for the remaining {{remaining}} — nothing more.",
  "publicQuote.partialPaidSummary": "Paid so far: {{paid}}. Remaining: {{remaining}}.",
  "publicQuote.paymentTermsHeading": "Payment terms",
  "publicQuote.additionalTermsHeading": "Additional terms",
  "publicQuote.agreeTermsPrefix": "I have read and agree to the",
  "publicQuote.termsOfService": "Terms of Service",
  "publicQuote.agreePrivacyPrefix": "I have read and agree to the",
  "publicQuote.privacyPolicy": "Privacy Policy",
  "publicQuote.agreeJorvioPortalLead": "I have read and agree to the",
  "publicQuote.agreeJorvioPortalMid": " and ",
  "publicQuote.agreeJorvioPortalTail":
    " for the Jorvio Quotes platform that hosts this estimate page (not the same as your agreement with the contractor for the job itself).",
  "publicQuote.agreeStripePaymentLead": "For card payment, I understand Stripe will process the charge and I agree to",
  "publicQuote.stripePaymentTermsLink": "Stripe’s terms for this checkout",
  "publicQuote.agreeStripePaymentTail": " (in addition to the amount shown on the Pay button).",
  "publicQuote.payConfirmShort":
    "You confirm you understand the amount on the payment button and the contractor's scope on this estimate.",
  "publicQuote.payConfirmWithProcessingShort":
    "You confirm you understand the checkout total on the button (includes a Stripe transaction fee shown as a rough estimate) and the contractor's scope on this estimate.",
  "publicQuote.processingFeeTitle": "Stripe transaction fee",
  "publicQuote.processingFeeRoughHint": "rough estimate",
  "publicQuote.processingFeeBody":
    "A Stripe transaction fee line (rough estimate) is added at checkout — about {{processing}} on this payment. Your card is charged {{total}} total; actual Stripe fees can differ.",
  "publicQuote.processingFeeBasisNote":
    "This fee estimate uses your total for this estimate (including tax when shown), then adds it on top at checkout—it is not part of the work subtotal above.",
  "publicQuote.redirectingCheckout": "Redirecting to checkout…",
  "publicQuote.acceptPayButton": "Accept estimate & pay — {{amount}}",
  "publicQuote.coLineDefault": "Line",
  "publicQuote.photoAlt": "Job site or plan {{n}}",
  "publicQuote.tryAgain": "Try again.",
  "publicQuote.couldNotUpdateAddon": "Could not update add-on",
  "publicQuote.couldNotAcceptCo": "Could not accept change order",
  "publicQuote.coAcceptedToast": "Change order accepted",
  "publicQuote.coAcceptedToastDesc": "Your estimate total has been updated.",

  "accept.signFirst": "Sign first",
  "accept.accepted": "Quote accepted!",

  "calendar.entryUpdated": "Entry updated",
  "calendar.loadFailed": "Could not load calendar",

  "editor.toast.couldNotSend": "Could not send",
  "editor.toast.saveFailed": "Save failed",
  "editor.toast.saved": "Saved",
  "editor.toast.sendFixSave": "Please fix the save error first.",
  "editor.toast.sendFailed": "Send failed",
  "editor.toast.emailSent": "Email sent",
  "editor.toast.duplicateFailed": "Duplicate failed",
  "editor.toast.updateFailed": "Update failed",
  "editor.toast.markedDeclined": "Marked as declined",
  "editor.toast.archiveFailed": "Archive failed",
  "editor.toast.enterCustomerName": "Enter a customer name",
  "editor.toast.customerAttached": "Customer attached",
  "editor.toast.templateSaveFailed": "Could not save template",
  "editor.toast.templateSaved": "Template saved",
  "editor.toast.enterName": "Enter a name",
  "editor.toast.directoryNameHint": "Supplier, sub, vendor, or any contact you pay.",
  "editor.toast.addedDirectory": "Added to directory",
  "editor.toast.assistantUpdated": "Quote assistant updated the quote",
  "editor.toast.quoteHelperSaveOk": "Update saved",
  "editor.previewPdf": "Preview PDF",
  "editor.backToQuotes": "Back to quotes",
  "editor.customerShowItemizedPricing": "Show line-by-line pricing on the customer estimate",
  "editor.customerShowItemizedPricingHelp":
    "Off: customer sees the total only on the link, preview, and PDF (add-on toggles hidden). Your saved add-on defaults still affect the amount.",
  "editor.customerAcceptanceIntro": "Control how the customer link handles payment and signatures.",
  "editor.collectPayment": "Collect payment on this quote (through Stripe)",
  "editor.collectPaymentHelp":
    "On: customers can pay on the link when a payout account is connected in Settings. Off: they can accept without paying online; you collect payment separately.",
  "editor.collectPaymentBlocked":
    "Connect a payout account to enable checkout on the quote link.",
  "editor.collectPaymentBlockedSettingsLink": "Open payout settings",
  "editor.collectPaymentBlockedTail":
    "You can still email the quote or send a PDF without a payout account.",
  "editor.collectPaymentDepositTiming":
    "Deposits land in your bank about 2 business days after each payment (Stripe's standard schedule). The very first payout can take 7–14 days while Stripe verifies your account.",
  "editor.processingFeeBasisNote":
    "The fee estimate is based on the customer total (your quoted work, tax, and discounts as shown), then added at checkout on top—it does not come out of your margin.",
  "editor.requireCustomerSignature": "Require customer signature",
  "editor.requireCustomerSignatureHelp":
    "On: they must draw a signature before accepting. Off: they can accept with a tap.",
  "editor.duplicate": "Duplicate",
  "editor.saveTemplate": "Save as template",
  "editor.archiveTitleTip":
    "Archive this quote to remove it from the main Quotes list. Archived quotes are auto-deleted after 60 days.",
  "editor.declineTitleTip":
    "Use when the customer turns down this quote. Updates status for your records only; it does not email them.",
  "editor.saveTitleTip": "Quote also saves automatically a moment after you stop editing",
  "editor.save": "Save",
  "editor.sendToCustomer": "Send to customer",
  "editor.markDeclined": "Mark declined",
  "editor.archive": "Archive",
  "editor.customerAcceptance": "Customer acceptance",
  "editor.customer": "Customer",
  "editor.noCustomer": "No customer",
  "editor.createAttach": "Create & attach",
  "editor.sitePhotos": "Site photos & plan markup",
  "editor.quoteDetails": "Quote details",
  "editor.schedule": "Schedule",
  "editor.decisionMode": "Decision mode",
  "editor.decisionModeHelp":
    "Turn on to add up to three customer options (for example Good / Better / Best). They see a comparison on their quote plus the ask-about-this-quote helper. Best when you are offering real packages, not a single flat price.",
  "editor.regretProof": "Trust and reassurance",
  "editor.trustReassuranceHelp":
    "Short reassurance your customer sees near pricing: why the work is a safe yes, optional wait or cost note, trust bullets, and next steps. Use on larger jobs or when they are comparing bids; skip for small fixes.",
  "editor.missingCompanyNameBanner":
    "Customers see your company on the quote. Add it in",
  "editor.missingCompanyNameSettingsLink": "Business details",
  "editor.internalCosts": "Internal: costs, margin & supply",
  "editor.addCostLine": "Add cost line",
  "editor.quickAddContact": "Quick add contact",
  "editor.lineItemsCheckout": "Line items, pricing & checkout",
  "editor.warrantyNotes": "Warranty & other notes",
  "editor.removeCostLine": "Remove cost line",
  "editor.ph.customerName": "Jane Doe",
  "editor.ph.customerEmail": "jane@example.com",
  "editor.ph.customerPhone": "(555) 123-4567",
  "editor.ph.summaryCustomer": "They'll see this on their estimate link — keep it clear and actionable.",
  "editor.ph.signedNote": "Usually filled when the customer accepts. You can edit it here.",
  "editor.ph.scheduleNote":
    "Plain language for the customer: when materials usually arrive, when work can start after they accept, that on-site dates are tentative until confirmed.",
  "editor.ph.optionLabel": "Good / Better / Best",
  "editor.ph.optionBestFor": "What this option is best for",
  "editor.ph.optionTotal": "Option total",
  "editor.ph.recommendedSummary": "Recommended summary shown to the customer",
  "editor.ph.tradeoff": "Tradeoff summary: price vs durability vs speed",
  "editor.ph.budgetGuidance": "Budget-minded guidance",
  "editor.ph.longevityGuidance": "Longevity-minded guidance",
  "editor.ph.faq": "FAQs, one per line: Question | Answer",
  "editor.ph.comparison": "Comparison lines: Label | Option | Value",
  "editor.ph.confidence": "Why this choice makes sense and what the customer can feel confident about",
  "editor.ph.waitRisk": "What changes if they wait (scheduling, pricing, scope)",
  "editor.ph.trustSignals": "Trust signals, one per line",
  "editor.ph.whatNext": "What happens next, one step per line",
  "editor.ph.supplierName": "Supplier, subcontractor, vendor, or company name",
  "editor.ph.costDescription": "Short description of this cost",
  "editor.ph.internalNotes": "Internal notes only — not on customer PDF",
  "editor.nav.quickNotes": "Quick notes",
  "editor.quickNotes.title": "Quick notes",
  "editor.quickNotes.description":
    "Short timestamped reminders for your team on this quote. Not shown to customers.",
  "editor.quickNotes.placeholder": "Add a quick reminder…",
  "editor.quickNotes.add": "Add note",
  "editor.quickNotes.adding": "Adding…",
  "editor.quickNotes.empty": "No quick notes yet.",
  "editor.quickNotes.delete": "Delete",
  "editor.quickNotes.deleteTitle": "Remove this note",
  "editor.quickNotes.toastAdded": "Saved to internal notes",
  "editor.quickNotes.toastDeleted": "Note removed",
  "editor.quickNotes.toastSaveFailed": "Could not add note",
  "editor.quickNotes.toastDeleteFailed": "Could not remove note",
  "editor.quickNotes.inlinePlaceholder": "Type a note, press Enter or Add",
  "editor.quickNotes.removeTitle": "Remove note",
  "editor.ph.depositNotSet": "Not set",
  "editor.ph.warranty": "Labor warranty, manufacturer coverage, etc.",
  "editor.ph.finePrint": "Fine print, exclusions, or internal context you want on the customer view",
  "editor.invalidDateYmd": "Use YYYY-MM-DD for expected-by date.",

  "help.title": "Help",
  "help.subtitle":
    "General how-to about the product. It doesn’t see your quotes, customers, or account data.",
  "help.contactTitle": "Contact us",
  "help.contactBody": "Need someone from our team? Use the form below.",
  "help.contactButton": "Send message",
  "help.escalateBanner":
    "We could not answer that from general help alone. Open Contact us (the form on this page) to reach our team.",
  "help.aiUnavailable":
    "The help assistant is not available right now. Use the contact form below to reach our team.",
  "help.placeholder": "e.g. Where do I send a quote to my customer?",
  "help.send": "Ask",
  "help.sending": "Thinking…",
  "help.clear": "Clear chat",
  "help.you": "You",
  "help.assistant": "Help assistant",
  "help.assistantCardDescription":
    "Ask how features work or where to find things in the app. This assistant only has general product knowledge. It doesn’t open your quotes or account data.",
  "help.assistantTyping": "Answering…",
  "help.disclaimer":
    "Don’t paste passwords or confidential customer or payment details here.",

  "help.bugReport.title": "Report a bug",
  "help.bugReport.subtitle":
    "Describe what broke and what you were doing. Optional photos help us figure it out faster. We read every submission.",
  "help.bugReport.descriptionLabel": "What went wrong",
  "help.bugReport.descriptionPlaceholder": "Describe what went wrong — the more detail, the better.",
  "help.bugReport.contextLabel": "What were you doing when it happened?",
  "help.bugReport.contextPlaceholder": "What were you doing right before it happened? Walk through the steps.",
  "help.bugReport.screenshotsLabel": "Photos (optional)",
  "help.bugReport.screenshotsHint": "Up to 4 images (PNG, JPEG, WebP, or GIF), 4 MB each.",
  "help.bugReport.submit": "Submit report",
  "help.bugReport.submitting": "Sending…",
  "help.bugReport.successTitle": "Thank you — we received your report",
  "help.bugReport.successBody":
    "Our team will review the details you sent. You do not need to do anything else; we will follow up by email if we need more information.",
  "help.bugReport.sendAnother": "Submit another report",
  "help.bugReport.descTooShort": "Please add a bit more detail (at least 10 characters) in “What went wrong.”",
  "help.bugReport.contextTooShort": "Please tell us a little more about what you were doing (at least 5 characters).",
  "help.bugReport.fileTooLarge": "Each image must be 4 MB or smaller.",
  "help.bugReport.fileTypeInvalid": "Only PNG, JPEG, WebP, or GIF images are allowed.",
  "help.bugReport.failedTitle": "Could not send report",

  "help.attachments.dropTitle": "Add photos",
  "help.attachments.dropSubtitle": "Drag and drop or click — PNG, JPEG, WebP, or GIF.",
  "help.attachments.removePhoto": "Remove photo",

  "help.contactForm.jumpToForm": "Go to contact form",
  "help.contactForm.subjectLabel": "Subject (optional)",
  "help.contactForm.subjectPlaceholder": "e.g. Billing / login issue",
  "help.contactForm.messageLabel": "Message",
  "help.contactForm.messagePlaceholder":
    "Please be as detailed as you can. Tell us exactly what happened leading up to the issue and the steps you took.",
  "help.contactForm.hint":
    "We read every message. Add photos below if they help. For broken flows you can also use Report a bug above.",
  "help.contactForm.attachmentsLabel": "Photos (optional)",
  "help.contactForm.attachmentsHint": "Up to 4 images (PNG, JPEG, WebP, or GIF), 4 MB each.",
  "help.contactForm.submit": "Send to team",
  "help.contactForm.submitting": "Sending…",
  "help.contactForm.successTitle": "Message sent",
  "help.contactForm.successBody":
    "Our team will review it. If we need anything else, we’ll reply to your account email.",
  "help.contactForm.successBodyGuest":
    "Our team will review it. If we need anything else, we’ll reply to the email address you entered above.",
  "help.contactForm.sendAnother": "Send another message",
  "help.contactForm.failedTitle": "Could not send — try again shortly.",

  "contactPage.signedInNote":
    "You’re contacting us from your signed-in account: {{name}}, {{email}}. We’ll use this email to reply.",
  "contactPage.guestNameLabel": "Your name",
  "contactPage.guestEmailLabel": "Your email",
  "contactPage.guestNamePlaceholder": "Full name",
  "contactPage.guestEmailPlaceholder": "your@email.com",
  "marketing.contact.pageTitle": "Contact us",
  "marketing.contact.pageSubtitle":
    "Send a message to the Jorvio team. We’ll get back at the email you signed in with, or at the email you enter below.",
  "help.agencyMailtoIntro":
    "Your plan includes priority support. For the fastest response, email us directly from your mail app.",
  "help.agencyMailtoCta": "Open email to contact support",

  "help.triage.title": "What is your issue?",
  "help.triage.subtitle": "Answer a few questions — we’ll suggest a fix you can try before escalating.",
  "help.triage.back": "Back",
  "help.triage.startOver": "Start over",
  "help.triage.option.other": "Other",
  "help.triage.pendingTopicLabel": "Next message will include topic:",
  "help.triage.chatTopicWrap": "[Help topic: {{topic}}]",
  "help.triage.tryThisTitle": "Try this first",
  "help.triage.didNotWork": "That didn’t work — get help from the assistant",
  "help.triage.contactInstead": "Email the team instead",
  "help.triage.reportBugInstead": "Open Report a bug with screenshots",
  "help.triage.describeIssuePlaceholder":
    "Describe your issue in your own words — what you tried, what you expected, and what happened instead.",
  "help.triage.cta.ai": "Continue to help assistant",
  "help.triage.cta.contact": "Open contact form",
  "help.triage.cta.bug": "Open Report a bug",

  "help.triage.prompt.root": "What best describes your issue?",
  "help.triage.prompt.account": "Which sign-in or account topic fits best?",
  "help.triage.prompt.quotes": "What part of quotes or sending is the issue?",
  "help.triage.prompt.payments": "What payment topic is it?",
  "help.triage.prompt.subscription": "What billing topic is it?",
  "help.triage.prompt.calendar": "What calendar or crew topic is it?",
  "help.triage.prompt.customers": "What customer directory issue is it?",
  "help.triage.prompt.settings": "Which settings area?",
  "help.triage.prompt.portal": "What customer portal issue is it?",
  "help.triage.prompt.bug": "What kind of problem is it?",

  "help.triage.opt.account": "Sign-in, password, or workspace access",
  "help.triage.opt.quotes": "Creating, editing, or sending quotes",
  "help.triage.opt.payments": "Customer checkout or Stripe payouts",
  "help.triage.opt.subscription": "Your Jorvio plan or invoice",
  "help.triage.opt.calendar": "Company calendar or crew access",
  "help.triage.opt.customers": "Customer records",
  "help.triage.opt.settings": "Profile, branding, Stripe Connect, team",
  "help.triage.opt.portal": "Customer quote link or portal login",
  "help.triage.opt.bug": "Something looks broken",

  "help.triage.opt.acct.login": "Login, magic link, or verification email",
  "help.triage.opt.acct.invite": "Inviting co-operators or team access",
  "help.triage.opt.acct.security": "Password change or device / session issues",
  "help.triage.opt.acct.access": "Locked out, wrong workspace, or account access I can’t fix in-app",

  "help.triage.opt.quotes.send": "Send to customer, queue, or “not sent”",
  "help.triage.opt.quotes.email": "Customer didn’t receive the estimate email",
  "help.triage.opt.quotes.pdf": "PDF or preview looks wrong",
  "help.triage.opt.quotes.editor": "Editing line items, prices, or draft saving",
  "help.triage.opt.quotes.pay": "Deposit / pay on the public quote page",

  "help.triage.opt.pay.checkout": "Customer paying on the quote link (card checkout)",
  "help.triage.opt.pay.connect": "Connecting Stripe or finishing onboarding",
  "help.triage.opt.pay.payout": "Payouts, bank account, or verification with Stripe",
  "help.triage.opt.pay.dispute": "Refund, chargeback, or payment dispute",

  "help.triage.opt.sub.plan": "Change plan, upgrade/downgrade, or cancel",
  "help.triage.opt.sub.charge": "Unexpected charge or billing amount looks wrong",
  "help.triage.opt.sub.receipt": "Receipt, invoice copy, or payment history",

  "help.triage.opt.cal.crew": "Crew code, /crew sign-in, or team calendar access",
  "help.triage.opt.cal.events": "Events, dates, or calendar not showing right",
  "help.triage.opt.cal.schedule": "Scheduling info on quotes or calendar",

  "help.triage.opt.cust.directory": "Adding or editing customers",
  "help.triage.opt.cust.wrong": "Wrong customer info tied to a quote",

  "help.triage.opt.set.stripe": "Stripe Connect, payouts, or payment settings",
  "help.triage.opt.set.brand": "Logo, language/locale, or company details",
  "help.triage.opt.set.reviews": "Google review request emails",
  "help.triage.opt.set.team": "Co-operator codes, crew, or invites",

  "help.triage.opt.port.otp": "Customer portal email code or login",
  "help.triage.opt.port.view": "What they see on the quote link or portal",

  "help.triage.opt.bug.crash": "Error message, blank screen, or obvious bug",
  "help.triage.opt.bug.data": "Wrong numbers or missing quotes / data",
  "help.triage.opt.bug.slow": "Slow loading or timeouts",

  "help.triage.summary.other": "Other issue",
  "help.triage.summary.acctLogin": "Login / magic link / verification email",
  "help.triage.summary.acctInvite": "Invites / team access",
  "help.triage.summary.acctSecurity": "Password / sessions",
  "help.triage.summary.acctAccess": "Account access / workspace",
  "help.triage.summary.quotesSend": "Sending quote to customer",
  "help.triage.summary.quotesEmail": "Customer didn’t get email",
  "help.triage.summary.quotesPdf": "PDF or preview",
  "help.triage.summary.quotesEditor": "Quote editor / line items",
  "help.triage.summary.quotesPay": "Pay on public quote",
  "help.triage.summary.payCheckout": "Customer checkout",
  "help.triage.summary.payConnect": "Stripe Connect setup",
  "help.triage.summary.payPayout": "Stripe payouts / bank",
  "help.triage.summary.payDispute": "Refund / dispute",
  "help.triage.summary.subPlan": "Plan change / cancel",
  "help.triage.summary.subCharge": "Unexpected subscription charge",
  "help.triage.summary.subReceipt": "Receipt / invoice",
  "help.triage.summary.calCrew": "Crew / team calendar access",
  "help.triage.summary.calEvents": "Calendar events",
  "help.triage.summary.calSchedule": "Scheduling",
  "help.triage.summary.custDirectory": "Customer records",
  "help.triage.summary.custWrong": "Wrong customer data",
  "help.triage.summary.setStripe": "Stripe / payouts settings",
  "help.triage.summary.setBrand": "Branding / locale",
  "help.triage.summary.setReviews": "Google review requests",
  "help.triage.summary.setTeam": "Team / invites",
  "help.triage.summary.portOtp": "Portal OTP / login",
  "help.triage.summary.portView": "Customer view",
  "help.triage.summary.bugCrash": "Bug / error",
  "help.triage.summary.bugData": "Wrong or missing data",
  "help.triage.summary.bugSlow": "Slow / timeout",

  "help.triage.solution.other":
    "- Refresh the page or try another browser (Chrome or Edge usually behave best).\n- Confirm you’re in the correct workspace / account.\n- Note any exact **error text** or URL — you’ll use it if you need the assistant.",
  "help.triage.solution.acctLogin":
    "- Check **Spam/Junk** for magic-link emails from Jorvio.\n- Wait ~60 seconds between requesting new links (rapid retries can fail).\n- Try an Incognito/private window to rule out extensions.\n- Use **Forgot password** only if you sign in with a password (not magic link alone).",
  "help.triage.solution.acctInvite":
    "- Confirm the invite email matches how they’ll sign in.\n- Ask them to check spam/promotions.\n- Resend the invite from your team settings when available.",
  "help.triage.solution.acctSecurity":
    "- Sign out of other sessions from security/account settings if something feels off.\n- Change your password only through the app’s change-password flow.\n- Never send passwords or codes by email or chat.",
  "help.triage.solution.acctContact":
    "- Verify you’re using the **same email** your workspace owner expects.\n- Try another browser or device once.\n- If you’re completely locked out with no self-serve path, use **Email the team instead** — never paste passwords.",
  "help.triage.solution.quotesSend":
    "- Open the quote and check whether it’s **draft vs sent**.\n- Use **Resend** / send again if the UI offers it.\n- Reconnect Wi‑Fi — queued sends often finish after you’re back online.",
  "help.triage.solution.quotesEmail":
    "- Double-check the customer email on the quote for typos.\n- Ask them to search spam/promotions.\n- Resend from the quote when possible.",
  "help.triage.solution.quotesPdf":
    "- Use your browser’s **Print → Save as PDF** if the in-app PDF misbehaves.\n- Try desktop Chrome if mobile preview looks wrong.",
  "help.triage.solution.quotesEditor":
    "- Wait for autosave to finish before leaving the page.\n- Refresh once if a field won’t stick.\n- Duplicate as a new draft if something looks corrupted.",
  "help.triage.solution.quotesPay":
    "- Confirm **Stripe Connect** is finished under Settings → Payments.\n- Open the public quote link in a private window to reproduce what the customer sees.",
  "help.triage.solution.payCheckout":
    "- Ensure payments are enabled and the quote link is shareable.\n- Try another card or browser; copy any **Stripe error text** you see.\n- Confirm the quote isn’t expired or withdrawn.",
  "help.triage.solution.payConnect":
    "- Complete every Stripe Connect onboarding step (yellow/red banners).\n- Check the Stripe Dashboard for identity or bank account prompts.\n- Retry after any Stripe outage clears.",
  "help.triage.solution.payPayout":
    "- In Stripe Dashboard, verify **bank account** and identity checks are complete.\n- Confirm payouts aren’t paused for compliance.\n- Gather **dates and amounts** before contacting — never paste full card numbers.",
  "help.triage.solution.payDispute":
    "- Collect charge ID, date, and amount from Stripe or your records.\n- Don’t share full card numbers in messages.\n- Use **Email the team instead** for coordinated refunds or disputes.",
  "help.triage.solution.subPlan":
    "- Review your current plan and renewal date in Billing / Subscription.\n- Decide whether you want upgrade, downgrade, or cancel before contacting.\n- Note invoice emails when disputing a charge.",
  "help.triage.solution.subCharge":
    "- Compare the charge date to recent invoices and plan changes (**proration** is common).\n- Search your inbox for the billing receipt.\n- Use **Email the team instead** if something still doesn’t match.",
  "help.triage.solution.subReceipt":
    "- Open Billing / invoice history in the app if available.\n- Search email for “invoice” or “receipt” from your billing sender.\n- Say which document type you need (PDF vs portal link).",
  "help.triage.solution.calCrew":
    "- Regenerate or re-share the crew code from calendar / crew settings.\n- Confirm crew opens the correct `/crew` URL and workspace.\n- Clear browser cache only if sign-in loops persist.",
  "help.triage.solution.calEvents":
    "- Check **timezone** on events vs quote dates.\n- Toggle calendar filters if entries seem missing.\n- Refresh after editing linked quotes.",
  "help.triage.solution.calSchedule":
    "- Compare scheduled dates on the quote to what the calendar shows.\n- Update the quote and reopen the calendar.\n- Note one concrete quote + date that disagree.",
  "help.triage.solution.custDirectory":
    "- Edit the customer from **Customers**, then confirm the quote picks up changes.\n- Re-link the quote to the customer record if the UI allows.",
  "help.triage.solution.custWrong":
    "- Fix the customer record first, then refresh the quote.\n- Create a new quote from the corrected customer if old data won’t refresh.\n- Capture screenshots if numbers disagree across pages.",
  "help.triage.solution.setStripe":
    "- Open Settings → Payments / Stripe and resolve any banners.\n- Re-open Connect onboarding if something looks unfinished.\n- Check Stripe Dashboard for verification holds.",
  "help.triage.solution.setBrand":
    "- Save logo and company details, then hard-refresh the public quote preview.\n- Confirm language/locale saved before testing again.",
  "help.triage.solution.setReviews":
    "- Open Settings → Reviews and add your Google Business review link.\n- Set default timing and message; toggle off per quote in the editor if needed.\n- Review emails send only after the customer pays in full via checkout.",
  "help.triage.solution.setTeam":
    "- Resend co-operator or crew invites from team settings.\n- Revoke stale codes if someone shouldn’t have access anymore.",
  "help.triage.solution.portOtp":
    "- Ask the customer to check spam for the login code.\n- Wait ~60 seconds between new code requests.\n- Confirm you’re sending to the email they actually use.",
  "help.triage.solution.portView":
    "- Open the quote link in an **Incognito** window to see what they see.\n- Confirm the quote is published and not expired.\n- Try another browser if layout looks broken.",
  "help.triage.solution.bugReport":
    "- Hard refresh (**Ctrl+Shift+R** / **Cmd+Shift+R**) and retry once.\n- Note the exact URL and any error text.\n- Use **Open Report a bug with screenshots** if it keeps breaking.",
  "help.triage.solution.bugWrong":
    "- Export or screenshot wrong totals with timestamps.\n- Check filters and date ranges before assuming data loss.\n- Use **Email the team instead** with examples (no secrets).",
  "help.triage.solution.bugSlow":
    "- Note whether it’s Wi‑Fi vs cellular, browser, and rough time of day.\n- Disable heavy extensions temporarily.\n- Try a smaller quote or page to see if it’s data volume.",

  "owner.bugReports.title": "Bug reports",
  "owner.bugReports.subtitle": "Submissions from Help → Report a bug (with optional photos).",
  "owner.bugReports.when": "When",
  "owner.bugReports.from": "From",
  "owner.bugReports.page": "Page",
  "owner.bugReports.issue": "Issue",
  "owner.bugReports.context": "What they were doing",
  "owner.bugReports.shots": "Photos",
  "owner.bugReports.openImage": "Open",
  "owner.bugReports.status": "Status",
  "owner.bugReports.notes": "Notes",
  "owner.bugReports.save": "Save",
  "owner.bugReports.empty": "No bug reports yet.",
  "owner.bugReports.loadError": "Could not load bug reports",
  "owner.bugReports.saveError": "Could not save",
  "owner.bugReports.migrationTitle": "Migration required",
  "owner.bugReports.migrationBody": "Run 057_platform_bug_reports.sql in Supabase SQL Editor.",

  "owner.issuesInbox.title": "Support & bug inbox",
  "owner.issuesInbox.subtitle": "All contact and bug submissions in one list (newest first). Expand a row for full details, notes, and status.",
  "owner.issuesInbox.typeBug": "Bug",
  "owner.issuesInbox.typeSupport": "Support",
  "owner.issuesInbox.empty": "No support messages or bug reports yet.",
  "owner.issuesInbox.loadError": "Could not load inbox",

  "owner.supportContact.title": "Support messages",
  "owner.supportContact.subtitle": "In-app contact submissions from Help or Settings (also emailed to you when configured).",
  "owner.supportContact.when": "When",
  "owner.supportContact.from": "From",
  "owner.supportContact.source": "Source",
  "owner.supportContact.page": "Page",
  "owner.supportContact.subject": "Subject",
  "owner.supportContact.message": "Message",
  "owner.supportContact.attachments": "Photos",
  "owner.supportContact.status": "Status",
  "owner.supportContact.notes": "Notes",
  "owner.supportContact.save": "Save",
  "owner.supportContact.empty": "No support messages yet.",
  "owner.supportContact.loadError": "Could not load support messages",
  "owner.supportContact.saveError": "Could not save",
  "owner.supportContact.migrationTitle": "Migration required",
  "owner.supportContact.migrationBody":
    "Run 060_platform_support_contact.sql and 061_platform_support_message_attachments.sql in Supabase SQL Editor.",

  "tour.spotlight.proposals.step1Title": "Quotes list",
  "tour.spotlight.proposals.step1Body":
    "Search and sort help you find a quote quickly. Status filters narrow the list. Use New quote when you are ready to start another estimate.",
  "tour.spotlight.jobs.step1Title": "Jobs",
  "tour.spotlight.jobs.step1Body":
    "Track work linked to one or more quotes—schedules, payment totals across linked estimates, and completion. Use the tabs for Active, Completed, or Archived.",
  "tour.spotlight.followUps.step1Title": "Follow-ups & analytics",
  "tour.spotlight.followUps.step1Body":
    "See which quotes may need a nudge and how much money is still open with customers. Pro and Agency unlock the full board; upgrade from Settings → Billing if you see a lock.",
  "tour.spotlight.customers.step1Title": "Customers",
  "tour.spotlight.customers.step1Body":
    "Search your saved contacts. Add someone here so you can link them when you build a quote.",
  "tour.spotlight.customers.step2Title": "Quick add",
  "tour.spotlight.customers.step2Body":
    "Name and email are required so you can send quotes. Phone is optional but useful on customer-facing documents.",
  "tour.spotlight.calendar.step1Title": "Company calendar",
  "tour.spotlight.calendar.step1Body":
    "See jobs and manual entries together. Open a day or use the panel to add internal notes, deliveries, or subcontractor visits.",
  "tour.spotlight.templates.step1Title": "Templates",
  "tour.spotlight.templates.step1Body":
    "Save a winning quote structure as a template, then start a new draft from it in one tap. Edit or delete templates anytime.",
  "tour.spotlight.settings.step1Title": "Settings tabs",
  "tour.spotlight.settings.step1Body":
    "Use these links to switch sections: company profile and branding, billing and plans, team and crew access, and your personal account.",
  "tour.spotlight.settings.step2Title": "Profile",
  "tour.spotlight.settings.step2Body": "Language, business details, logo, and defaults that appear on customer quotes.",
  "tour.spotlight.settings.step3Title": "Billing",
  "tour.spotlight.settings.step3Body":
    "Your Jorvio subscription and payment method. Stripe payouts are only for card checkout on the quote link—you can still email a quote or send a PDF without connecting a payout account.",
  "tour.spotlight.settings.step4Title": "Team",
  "tour.spotlight.settings.step4Body": "Invite co-operators, crew app access, and roster visibility for scheduling.",
  "tour.spotlight.settings.step5Title": "Account",
  "tour.spotlight.settings.step5Body": "Password, security, and account deletion — personal to your login.",
  "tour.spotlight.firstQuote.step1Title": "Pick or add a customer",
  "tour.spotlight.firstQuote.step1Body":
    "Choose an existing customer or New customer. For a new contact, add name and email (phone optional), then describe the job. Jorvio will draft line items you can edit next.",
  "tour.spotlight.firstQuote.step2Title": "Generate your draft",
  "tour.spotlight.firstQuote.step2Body":
    "When the form looks right, generate the quote. You will land in the editor to review pricing, photos, and send options—you can email or PDF without setting up card payments.",
  "tour.spotlight.firstQuoteChooser.step1Title": "Choose how to quote",
  "tour.spotlight.firstQuoteChooser.step1Body":
    "Build with AI, start blank, use a quick job-site quote, or open a template — pick what fits this job.",

  "tour.app.next": "Next",
  "tour.app.done": "Done",
  "tour.app.progress": "Step {{current}} of {{total}}",
  "tour.app.step0Title": "Welcome",
  "tour.app.step0Body":
    "A full walkthrough of every area in the sidebar, top to bottom: where quotes live, how jobs and calendar connect, and where settings and help live. Use Next to move step by step, or close anytime to skip.",
  "tour.app.intro.welcomeTitle": "Welcome",
  "tour.app.intro.welcomeBody":
    "A quick look at the main sidebar tabs: Dashboard, Quotes, Jobs, Calendar, and Follow-ups. Use Next to continue, or close to skip.",
  "tour.app.intro.jobsBody":
    "When a customer accepts a quote, it becomes a job here. Track active work, schedules, and payment rollups in one place.",
  "tour.app.intro.calendarBody": "Scheduled work and manual calendar entries in one place.",
  "tour.app.intro.followUpsBody": "Open-quote signals and follow-up priorities live here.",
  "tour.app.navDashboardTitle": "Dashboard",
  "tour.app.navDashboardBody": "Your home base: activity, open quotes, and what needs attention today.",
  "tour.app.full.navDashboardBody":
    "Your home base when you sign in. See recent activity, quotes still waiting on a customer, and quick counts for what needs attention today. Most owners start here each morning before opening Quotes or Follow-ups.",
  "tour.app.navQuotesTitle": "Quotes",
  "tour.app.navQuotesBody": "Every estimate you have created, sent, or archived. Open one to edit, send, or follow up.",
  "tour.app.full.navQuotesBody":
    "Every estimate you have created, sent, viewed, or archived. Filter by status, open one to edit lines or send again, resend after changes, or turn an accepted quote into a job. This is your full quote history in one list.",
  "tour.app.step1Title": "New quote",
  "tour.app.step1Body":
    "Start a new estimate from here. Choose AI assist, a blank quote, a quick job-site flow, or a saved template. Everything you build lands in Quotes until you send it or the customer accepts.",
  "tour.app.step2Title": "Customers",
  "tour.app.step2Body":
    "Your customer directory: names, emails, phones, and addresses tied to past quotes. Pick a contact when you build a quote so estimates and message threads stay linked to the right person.",
  "tour.app.step3Title": "Jobs",
  "tour.app.step3Body":
    "When a customer accepts on the portal or you mark a quote accepted, it becomes a job here. Track active work, job status, schedules tied to calendar, and payment rollups without digging through old PDFs.",
  "tour.app.step4Title": "Company calendar",
  "tour.app.step4Body":
    "Company-wide schedule: accepted jobs with dates, assignees, and job sites, plus manual entries you add yourself. Use it to see what is on the board this week and avoid double-booking crews.",
  "tour.app.step5Title": "Follow-ups",
  "tour.app.step5Body":
    "Quotes you have sent but have not heard back on, sorted so stale ones surface first. Use this when you want a follow-up list without scrolling the full Quotes table.",
  "tour.app.step6Title": "Messages",
  "tour.app.step6Body":
    "Message threads from the customer portal and in-quote conversations in one inbox. Turn on email alerts in Settings if you want a ping when someone replies on a sent quote.",
  "tour.app.step7Title": "Settings",
  "tour.app.step7Body":
    "Company profile, logo and colors on PDFs, billing and plan limits, optional card payouts on the quote link, and team invites. Payouts are optional: you can send quotes by email or PDF without them.",
  "tour.app.navTemplatesTitle": "Templates",
  "tour.app.navTemplatesBody":
    "Save line items, sections, and wording you use on repeat jobs. Start new quotes from a template instead of retyping the same interior package or cabinet scope every time.",
  "tour.app.navHelpTitle": "Help",
  "tour.app.navHelpBody":
    "Guides and FAQs when you are stuck on a feature. You can also reopen this full sidebar tour anytime from the question mark menu at the bottom of the sidebar.",

  "tour.setup.fabLabel": "Finish setup",
  "tour.setup.title": "Setup checklist",
  "tour.setup.openSettings": "Open Settings",
  "tour.setup.dismiss": "Dismiss reminder",
  "tour.setup.restartTour": "Tour",
  "tour.setup.required": "Recommended first",
  "tour.setup.optional": "Recommended polish",
  "tour.setup.coopHint":
    "Quotes and calendar use your team workspace. Complete your personal profile so teammates recognize you.",
  "tour.setup.item.companyName": "Add your business name",
  "tour.setup.item.contactEmail": "Add a quote contact email (or keep your login email)",
  "tour.setup.item.quotePhone": "Add the phone number shown on customer quotes",
  "tour.setup.item.address": "Complete your business address (street, city, state, ZIP)",
  "tour.setup.item.logo": "Upload a logo for customer-facing quotes",
  "tour.setup.item.stripePayout":
    "Optional: connect Stripe for card checkout on the customer link. Email and PDF quotes work fine without it.",
  "tour.setup.item.coopProfile": "Add your display name",
  "tour.setup.item.coopPhone": "Add a personal phone (optional)",

  "tour.quoteEditor.step1Title": "Line items & pricing",
  "tour.quoteEditor.step1Body":
    "Use Next—no need to add lines yet. This table is what customers see on the estimate, PDF, and link. Add rows when you work a real quote.",
  "tour.quoteEditor.step2Title": "Quote assistant & AI review",
  "tour.quoteEditor.step2Body":
    "Ask the assistant for rewrites or pricing tweaks—apply what looks right and the quote saves itself. Click the sparkle button for an AI review of pricing, scope, terms, and what a comparing customer might object to. Both are included on every plan (a few AI actions a day on Starter and Solo; unlimited on Pro and Agency).",
  "tour.quoteEditor.step3Title": "Site photos & plan markup",
  "tour.quoteEditor.step3Body": "Upload photos and mark up plans so crews and customers share the same visual context.",
  "tour.quoteEditor.step4Title": "Pass card processing fee",
  "tour.quoteEditor.step4Body":
    "When available for your region, you can add an estimated card-processing line to the customer total. You are responsible for surcharge rules where you operate.",
  "tour.quoteEditor.step5Title": "Send to your customer",
  "tour.quoteEditor.step5Body":
    "Email the quote, share the link, or download a PDF—no payout account required for those. Add Stripe payouts in Settings only if you want customers to pay by card on the link. Preview and PDF always match your last save.",
  "publicQuote.onlinePaymentsUnavailableTitle": "Online card payment isn’t available on this link yet",
  "publicQuote.onlinePaymentsUnavailableBody":
    "Card checkout isn’t set up yet. You can still read the estimate and pay another way—ask your contractor. They can enable cards in their settings.",
  "publicQuote.acceptWithoutPayHeading": "Accept this estimate",
  "publicQuote.acceptWithoutPayIntro":
    "Accept the terms below to approve this estimate.",
  "publicQuote.acceptOnlySignatureTitle": "Your signature",
  "publicQuote.acceptOnlySignatureIntro": "Draw your signature, then tap “Use signature” before accepting.",
  "publicQuote.acceptOnlyNoSignatureNote": "A drawn signature is not required on this quote.",
  "publicQuote.acceptWithoutPayButton": "Accept estimate",
  "publicQuote.acceptOnlySubmitting": "Submitting…",
  "publicQuote.acceptOnlyFailedTitle": "Could not accept",
  "publicQuote.acceptOnlySuccessTitle": "Estimate accepted",
  "publicQuote.acceptOnlySuccessBody": "Thank you — the contractor has been notified.",
};

/** ES: misma intención que EN; español de UI natural, no calco literal. */
export const EXTENDED_ES: Record<string, string> = {
  "common.tryAgain": "Inténtelo de nuevo",
  "common.requestFailed": "Error en la solicitud",
  "common.sending": "Enviando…",
  "common.signingIn": "Iniciando sesión…",
  "common.redirecting": "Redirigiendo…",
  "common.loadingEllipsis": "Cargando…",
  "common.account": "Cuenta",
  "common.or": "O",
  "common.add": "Añadir",
  "common.name": "Nombre",
  "common.email": "Correo",
  "common.password": "Contraseña",
  "common.phone": "Teléfono",
  "common.save": "Guardar",
  "common.delete": "Eliminar",
  "common.cancel": "Cancelar",
  "common.close": "Cerrar",
  "common.back": "Volver",
  "common.next": "Siguiente",
  "common.optionalSignIn": "(inicio opcional)",

  "auth.signIn": "Iniciar sesión",
  "auth.signInSubtitle": "",
  "auth.signInTitleFallback": "Iniciar sesión",
  "auth.tabPassword": "Contraseña",
  "auth.tabMagic": "Enlace mágico",
  "auth.forgotPassword": "¿Olvidó su contraseña?",
  "auth.signInButton": "Iniciar sesión",
  "auth.emailMagicLink": "Enviarme un enlace mágico",
  "auth.continueGoogle": "Continuar con Google",
  "auth.newHere": "¿Nuevo aquí?",
  "auth.createAccount": "Crear una cuenta",
  "auth.moreOptions": "Más opciones",
  "auth.crewMember": "¿Código para calendario de cuadrilla?",
  "auth.enterTeamCode": "Abrir calendario de cuadrilla",
  "auth.crewInviteLead": "¿Miembro de cuadrilla?",
  "auth.crewInviteCta": "Código, luego inicio de sesión",
  "auth.inviteCodeLead": "¿Código de invitación de co-operador?",
  "auth.joinWithCode": "Unirse al espacio",
  "auth.homeownerPortal": "¿Cliente con enlace de presupuesto?",
  "auth.customerPortal": "Portal del cliente",
  "auth.checkEmail": "Revise su correo",
  "auth.checkEmailCodeHint":
    "¿En otro dispositivo? Omita el botón del correo e introduzca el código de {{n}} dígitos abajo (solo números).",
  "auth.magicEnterCodeTitle": "Introduzca el código",
  "auth.magicEnterCodeLeadPrefix": "Enviamos un código de verificación a",
  "auth.magicEnterCodeLeadSuffix":
    "Escriba los {{n}} dígitos en el cuadro (solo números). Use esto si el enlace no abre: otro dispositivo o navegador integrado del correo.",
  "auth.magicCodePlaceholder": "Código del correo",
  "auth.magicCodeFieldHint": "{{n}} dígitos, solo números",
  "auth.magicVerifySubmit": "Verificar e iniciar sesión",
  "auth.magicResend": "Enviar un código nuevo",
  "auth.magicBack": "Usar otro correo",
  "auth.magicInvalidCode": "Introduzca los {{n}} dígitos del correo (solo números).",
  "auth.deviceOtpLeadStart": "Introduzca el código de verificación de {{n}} dígitos que enviamos a",
  "auth.deviceOtpToastDesc": "Enviamos un código de {{n}} dígitos para verificar este dispositivo.",
  "auth.deviceOtpEnterFull": "Introduzca los {{n}} dígitos (solo números).",
  "auth.idleSessionTitle": "Sesión cerrada por inactividad",
  "auth.idleSessionBody": "Por seguridad, vuelva a iniciar sesión.",
  "auth.signInFailed": "Error al iniciar sesión",
  "auth.googleSignInFailed":
    "No se completó el inicio con Google. Inténtelo de nuevo o use correo.",
  "auth.callbackErrorTitle": "No pudimos completar ese enlace de inicio de sesión.",
  "auth.callbackErrorBody":
    "Pruebe Enlace mágico otra vez e introduzca el código del correo. Si vino de una vista previa del correo, abra {{origin}}/login en Safari o Chrome y entre con código o contraseña.",
  "auth.validEmail": "Introduzca un correo válido",
  "auth.enterPassword": "Introduzca su contraseña",
  "auth.placeholderEmail": "usted@empresa.com",
  "auth.forgotTitle": "Restablecer contraseña",
  "auth.forgotSubtitle": "Enviaremos un enlace de restablecimiento al correo que escriba abajo.",
  "auth.forgotSend": "Enviar correo",
  "auth.updatePasswordTitle": "Nueva contraseña",
  "auth.updatePasswordSubtitle": "Elija una contraseña segura.",
  "auth.passwordUpdated": "Contraseña actualizada",
  "auth.passwordUpdatedDesc": "Ha iniciado sesión. Redirigiendo…",
  "auth.newPassword": "Nueva contraseña",
  "auth.confirmPassword": "Confirmar contraseña",
  "auth.forgotCardDescription":
    "Le enviaremos un enlace para establecer una contraseña nueva.",
  "auth.forgotSentDescription":
    "Revise el correo y la carpeta de spam. Abra el enlace en Safari, Chrome o Edge.",
  "auth.forgotAfterText":
    "Si ese correo está en nuestro sistema, recibirá un enlace en breve. Tras restablecer, inicie sesión desde la página de acceso.",
  "auth.backToSignIn": "Volver al inicio de sesión",
  "auth.updatePasswordCardDescription": "Elija una contraseña nueva para su cuenta.",

  "signup.passwordTab": "Contraseña",
  "signup.magicTab": "Enlace mágico",
  "signup.createButton": "Crear cuenta",
  "signup.fullName": "Nombre completo",
  "signup.companyName": "Empresa",
  "signup.addressLine": "Dirección (opcional)",
  "signup.city": "Ciudad",
  "signup.state": "Estado",
  "signup.zip": "Código postal",
  "signup.passwordMin": "Al menos 8 caracteres",
  "signup.passwordsMustMatch": "Las contraseñas deben coincidir",
  "signup.nameRequired": "El nombre es obligatorio",
  "signup.companyRequired": "El nombre de la empresa es obligatorio",
  "signup.selectTrade": "Seleccione su oficio",
  "signup.tradeOtherHint": "Indique su tipo de negocio o especialidad",
  "signup.hasAccount": "¿Ya tiene cuenta?",
  "signup.signInLink": "Iniciar sesión",

  "topbar.settings": "Ajustes",
  "topbar.signOut": "Cerrar sesión",
  "topbar.accountMenu": "Menú de cuenta",
  "topbar.openHelp": "Centro de ayuda",
  "topbar.takeTour": "Hacer un recorrido",
  "topbar.fullAppTour": "Recorrido completo",

  "customers.title": "Clientes",
  "customers.subtitle": "Un registro de contacto por persona a la que cotiza.",
  "customers.searchPlaceholder": "Buscar por nombre, correo o teléfono…",
  "customers.searchEmpty": "Ningún cliente coincide con \u201C{{query}}\u201D.",
  "customers.searchEmptyHint": "Pruebe con otro nombre, correo o teléfono.",
  "customers.searchClear": "Borrar búsqueda",
  "customers.emptyDesc": "Añada su primer cliente para empezar a cotizar.",
  "customers.searchAria": "Buscar clientes",
  "customers.addFailed": "No se pudo añadir el cliente",
  "customers.emailRequired": "Introduzca un correo para este cliente.",
  "customers.emailLabelRequired": "Correo (obligatorio)",
  "customers.phoneLabelOptional": "Teléfono (opcional)",
  "customers.added": "Cliente añadido",
  "customers.phonePlaceholder": "(555) 123-4567",
  "customers.emailPlaceholder": "juan@email.com",
  "customers.namePlaceholder": "María López",
  "customers.deleteFailed": "No se pudo eliminar el cliente",
  "customers.removed": "Cliente eliminado",
  "customers.actionsAria": "Acciones del cliente",
  "customers.saveFailed": "No se pudo guardar",
  "customers.saved": "Guardado",
  "customers.deleteCustomerFailed": "No se pudo eliminar",
  "customers.detailTitle": "Cliente",
  "customers.backToList": "Volver a clientes",
  "customers.quoteCountNone": "Sin cotizaciones",
  "customers.quoteCountOne": "1 cotización",
  "customers.quoteCountMany": "{{count}} cotizaciones",
  "customers.viewDetails": "Ver detalles",
  "customers.delete": "Eliminar",
  "customers.noContactOnFile": "Sin datos de contacto",
  "customers.deleteConfirmTitle": "¿Eliminar cliente?",
  "customers.deleteConfirmDesc":
    "Quitar a {{name}} de la lista. Las cotizaciones vinculadas conservan el historial; solo se borra el registro del cliente.",
  "customers.deleting": "Eliminando…",
  "customers.addCustomerShow": "Añadir cliente",
  "customers.addCustomerHide": "Ocultar formulario",

  "templates.title": "Plantillas",
  "templates.subtitle": "Cotizaciones reutilizables sin datos del cliente",
  "templates.createFailed": "No se pudo crear la cotización",
  "templates.confirmDeleteMsg": "¿Eliminar esta plantilla? No afecta a cotizaciones existentes.",
  "templates.emptyDescription":
    "Abra una cotización → Guardar como plantilla en el editor para guardar una estructura reutilizable sin datos del cliente.",
  "templates.createAQuote": "Crear una cotización",
  "templates.startersSectionTitle": "Plantillas de inicio",
  "templates.startersIntro": "Añádala a la lista y ábrala para editar partidas y alcance.",
  "templates.starterHvac": "Inicio HVAC",
  "templates.starterPlumbing": "Inicio plomería",
  "templates.starterElectrical": "Inicio electricidad",
  "templates.starterTrade": "Plantilla {{trade}}",
  "templates.starterAdding": "Añadiendo…",
  "templates.empty": "Aún no hay plantillas",
  "templates.deleteFailed": "No se pudo eliminar",
  "templates.deleted": "Plantilla eliminada",
  "templates.notFound": "Plantilla no encontrada",
  "templates.saveFailed": "Error al guardar",
  "templates.saved": "Plantilla guardada",
  "templates.alreadyHaveThisStarter": "Ya tienes esta plantilla inicial",
  "templates.alreadyHaveThisStarterBody":
    "Ábrela en la lista para editarla, o renombra tu copia existente antes de añadir otra.",
  "templates.lineItemsTypicalTotal": "{{count}} partidas · Total típico: {{total}}",
  "templates.cardHint":
    "Abra el título para editar, iniciar un borrador desde esta plantilla o eliminarla.",
  "templates.useForNewQuote": "Usar para nueva cotización",
  "templates.creating": "Creando…",
  "templates.edit": "Editar",
  "templates.deleting": "Eliminando…",
  "templates.createBlank": "Crear plantilla",
  "templates.createBlankWorking": "Creando…",
  "templates.addAnotherHint": "Cree otra estructura reutilizable para distintos tipos de trabajo.",
  "templates.premadeSectionTitle": "Añadir otro inicio",
  "templates.premadeSectionDescription":
    "Las mismas bases que al principio. Elija un oficio; puede editar cuando quiera.",
  "auth.savePassword": "Guardar contraseña",

  "templates.editBack": "← Volver a plantillas",
  "templates.editDeleteButton": "Eliminar plantilla",
  "templates.editHeading": "Editar plantilla",
  "templates.editQuoteTitleLabel": "Título de la cotización en borradores nuevos",
  "templates.editQuoteTitlePlaceholder": "p. ej. Remodelación de cocina — interior",
  "templates.editSummaryPlaceholder": "Resumen breve que el cliente ve al principio del presupuesto.",
  "templates.editScopePlaceholder": "Describa el trabajo, los materiales y todo lo que el cliente deba saber.",
  "templates.editSummaryScopeCard": "Resumen y alcance",
  "templates.editSummaryLabel": "Resumen",
  "templates.editScopeLabel": "Alcance de trabajos",
  "templates.editSummaryHint":
    "Las cotizaciones nuevas desde esta plantilla empiezan con este resumen y alcance.",
  "templates.editLineItemsCard": "Partidas",
  "templates.editTermsCard": "Condiciones y notas",
  "templates.editTaxRate": "Tipo impositivo",
  "templates.editDiscount": "Descuento ($)",
  "templates.editValidUntil": "Válido hasta",
  "templates.editDeposit": "Anticipo / primer pago (%)",
  "templates.editPaymentTerms": "Condiciones de pago",
  "templates.editWarranty": "Garantía",
  "templates.editNotes": "Notas",
  "templates.saveChanges": "Guardar cambios",

  "quotePaymentTerms.fullWithTotal": "Pague {{total}} en un único pago seguro abajo.",
  "quotePaymentTerms.fullGeneric":
    "El total del presupuesto se paga en un único pago seguro al completar el pago abajo.",
  "quotePaymentTerms.splitLine1WithAmounts":
    "El {{pct}}% del total del presupuesto ({{total}}) vence ahora: {{deposit}}. Este cobro solo corresponde a ese {{pct}}%, no al total de {{total}}.",
  "quotePaymentTerms.splitLine2WithAmounts":
    "El {{rest}}% restante ({{balance}}) se paga después en otro pago (por ejemplo, al terminar el trabajo). El cliente usará esta misma página del presupuesto para el saldo cuando usted esté listo.",
  "quotePaymentTerms.splitLine1Generic":
    "El {{pct}}% del total del presupuesto vence ahora. Este cobro solo toma ese {{pct}}%, no el importe completo.",
  "quotePaymentTerms.splitLine2Generic":
    "El {{rest}}% restante se paga después en otro pago (por ejemplo, al terminar el trabajo). El cliente volverá a esta misma página para el saldo cuando usted esté listo.",

  "decision.waitCostPara1":
    "Esperar a aprobar puede mover la próxima ventana de inicio—otro trabajo puede ocupar ese hueco.",
  "decision.waitCostPara2WithTotal":
    "Este total ({{total}}) corresponde a este alcance. Si el alcance o el mercado cambian antes de aceptar, la siguiente versión puede variar.",
  "decision.waitCostPara2NoTotal":
    "Si el alcance o el precio de materiales cambian antes de aceptar, la siguiente versión puede mostrar cifras distintas.",
  "decision.waitCostExpiry":
    "Si esta cotización tiene fecha de vencimiento, esperar demasiado puede requerir un presupuesto actualizado.",

  "calendarPage.title": "Calendario",
  "calendarPage.subtitle":
    "Cotizaciones tentativas y trabajos confirmados en un solo calendario.",

  "calendar.previous": "Anterior",
  "calendar.today": "Hoy",
  "calendar.addEntry": "Añadir entrada al calendario",
  "calendar.next": "Siguiente",
  "calendar.loadingEvents": "Cargando eventos…",
  "calendar.noJobsDay": "No hay trabajos programados este día.",
  "calendar.dayPeekEmpty": "No hay nada programado este día.",
  "calendar.dayTitleFallback": "Día",
  "calendar.eventFallback": "Evento",
  "calendar.moreCount": "+{{count}} más",
  "calendar.peekSrOnly": "Ver trabajos y equipo de este día",
  "calendar.legendTentative": "Trabajo tentativo",
  "calendar.legendConfirmed": "Trabajo confirmado",
  "calendar.legendMaterial": "Entrega de material",
  "calendar.legendSub": "Subcontratista",
  "calendar.legendCancelled": "Cancelado",
  "calendar.teamTitle": "Equipo y asignaciones",
  "calendar.teamDesc":
    "Cree un código de equipo en Ajustes y use esos nombres al asignar cotizaciones o trabajos del calendario.",
  "calendar.teamSettings": "Código de equipo y ajustes",
  "calendar.teamQuotes": "Abrir cotizaciones para asignar equipo",
  "calendar.rosterHint": "Han iniciado sesión antes (use la misma ortografía)",
  "calendar.rosterEmpty": "Nadie ha usado aún su código de equipo — compártalo desde Ajustes.",
  "calendar.openQuote": "Abrir cotización",
  "calendar.editEntry": "Editar",
  "calendar.dateRangeTo": "a",
  "calendar.dateRangeArrow": "→",
  "calendar.linkedQuoteStatusHint": "El estado de trabajos de cotización sigue la cotización y el pago.",
  "calendar.notesLabel": "Notas (opcional)",
  "calendar.notesPlaceholder": "Lo que el equipo deba recordar",
  "calendar.statusLabel": "Estado",
  "calendar.statusTentative": "Tentativo",
  "calendar.statusConfirmed": "Confirmado",
  "calendar.statusCancelled": "Cancelado",
  "calendar.titleLabel": "Título",
  "calendar.startDate": "Fecha de inicio",
  "calendar.endDate": "Fecha de fin",
  "calendar.startTime": "Hora de inicio",
  "calendar.endTime": "Hora de fin",
  "calendar.toastTitleRequired": "Añada un título",
  "calendar.toastPickStart": "Elija fecha de inicio",
  "calendar.toastEndAfterStart": "La fecha de fin debe ser igual o posterior al inicio",
  "calendar.toastInvalidStart": "Elija fecha y hora de inicio válidas",
  "calendar.toastInvalidEndTime": "Elija hora de fin válida",
  "calendar.toastInvalidEndDate": "Elija fecha de fin válida",
  "calendar.materialBadge": "Material",
  "calendar.subBadge": "Subcontratista",
  "calendar.crewPrefix": "Equipo:",
  "calendar.crewNoneQuote": "Aún nadie asignado en la cotización.",
  "calendar.crewNotOnQuote": "sin asignar en la cotización",
  "calendar.officeOnly": "Solo calendario de oficina — no aparece en la app del equipo.",
  "calendar.materialFromLine": "Entrega de material (desde línea de cotización)",
  "calendar.subFromCost": "Subcontratista (desde línea de coste)",
  "calendar.customerPrefix": "Cliente:",
  "calendar.savingEntry": "Guardando…",
  "calendar.saveEntry": "Guardar",
  "calendar.rawStatusTentative": "tentativo",
  "calendar.rawStatusConfirmed": "confirmado",
  "calendar.rawStatusCancelled": "cancelado",
  "calendar.dialogMaterialTitle": "Entrega de material (desde cotización)",
  "calendar.dialogSubTitle": "Subcontratista (desde cotización)",
  "calendar.dialogEditQuoteSchedule": "Editar planificación (trabajo de cotización)",
  "calendar.dialogEditEntry": "Editar entrada del calendario",
  "calendar.dialogNewEntry": "Nueva entrada del calendario",
  "calendar.hintMaterialSync":
    "Esta fila viene de la fecha «Listo para» de una línea de material en la cotización. Cambiar fecha u hora aquí solo actualiza el calendario — edite la línea en la cotización para cambiarlo de forma permanente.",
  "calendar.hintSubSync":
    "Sincronizada con una línea de coste con subcontratista vinculado. Los horarios se espacian desde el inicio del trabajo en la cotización; actualice la planificación o líneas de coste para refrescar.",
  "calendar.hintQuoteJob":
    "Fecha y horas actualizan el inicio y fin programados de la cotización. Puede definir para quién aparece el trabajo en el calendario del equipo abajo. El título, pagos o notas se cambian en la cotización.",
  "calendar.titlePlaceholder": "p. ej. Visita a obra, Recoger permiso",
  "calendar.assigneeLabel": "Equipo / asignar a (opcional)",
  "calendar.assigneeHintQuote":
    "Nombres separados por comas (p. ej. Mike, Sarah). Igual escritura que con el código de equipo.",
  "calendar.assigneeHintManual":
    "Aparece en el calendario del miembro si el nombre coincide con cómo inició sesión con su código. Use comas entre nombres.",
  "calendar.assigneeHintAux":
    "Quién lo ve en calendarios de equipo viene del «Asignar a» de la cotización—edítela para cambiarlo. Visitas de sub: solo calendario de oficina.",
  "calendar.endTimeOptional": "Hora de fin (opcional)",
  "calendar.durationHint":
    "Mismo día sin hora de fin = 1 hora. Varios días = aparece cada día; último día a las 17:00 si falta hora de fin.",
  "calendar.toastEntryAdded": "Entrada añadida",
  "calendar.deleteConfirmJob":
    "¿Quitar este trabajo del calendario y borrar inicio/fin programados en la cotización?",
  "calendar.deleteConfirmEntry": "¿Eliminar esta entrada del calendario?",
  "calendar.toastDeleteFailed": "No se pudo eliminar",
  "calendar.toastEntryRemoved": "Entrada eliminada",
  "calendar.toastCouldNotUpdate": "No se pudo actualizar la entrada",
  "calendar.toastCouldNotAdd": "No se pudo añadir la entrada",

  "billing.title": "Facturación",
  "billing.subtitle":
    "Su plan, cotizaciones enviadas este mes (correo, PDF o enlace), método de pago y estado de la suscripción.",
  "billing.introOfferTitle": "Prueba gratis de 7 días en cada plan",
  "billing.introOfferBody":
    "Starter, Solo, Pro y Agency comienzan con una prueba gratuita de 7 días — sin tarjeta. Después del día 7, agrega una tarjeta y elige un plan para seguir creando cotizaciones.",
  "billing.startPlanFirst":
    "Elija un plan para crear cotizaciones. Puede seguir navegando el resto de la app.",
  "billing.ownerBypass": "Cuenta propietario: omitidas restricciones de facturación para pruebas.",
  "billing.checkoutSuccess":
    "Suscripción actualizada. Usa Gestionar suscripción abajo para cambiar tu tarjeta, ver facturas o cancelar cuando quieras.",
  "billing.checkoutCanceled": "Pago cancelado. Su plan actual no cambió.",
  "billing.manageSubscription": "Gestionar suscripción",
  "billing.currentPlan": "Plan actual",
  "billing.choosePlan": "Elegir plan",

  "logo.useImageFile": "Use un archivo de imagen",
  "logo.pngJpgWebp": "PNG, JPG o WebP.",
  "logo.fileTooLarge": "Archivo demasiado grande",
  "logo.max2mb": "Máximo 2 MB.",
  "logo.updated": "Logotipo actualizado",

  "diagram.useImage": "Use un archivo de imagen",
  "diagram.max12": "Máx. 12 MB.",
  "diagram.max4": "Máximo 4 diagramas",
  "diagram.removeOne": "Quite uno para añadir otro.",
  "diagram.added": "Diagrama añadido",
  "diagram.readFailed": "No se pudo leer la imagen",

  "markup.useImage": "Use un archivo de imagen",
  "markup.previewFailed": "No se pudo cargar la vista previa",
  "markup.dimensionsFailed": "No se pudieron leer las dimensiones",
  "markup.photoAdded": "Foto añadida",

  "proposalForm.loadCustomersFailed": "No se pudieron cargar los clientes",
  "proposalForm.networkError": "Error de red.",
  "proposalForm.unsupportedImage": "Imagen no admitida",
  "proposalForm.useJpgPngWebp": "Use JPG, PNG o WebP.",
  "proposalForm.imageTooLarge": "Imagen demasiado grande",
  "proposalForm.under6mb": "Use una foto de menos de 6 MB.",
  "proposalForm.transcribeFailed": "No se pudo transcribir las notas",
  "proposalForm.noTextFound": "No se encontró texto",
  "proposalForm.clearerPhoto": "Pruebe una foto más nítida de sus notas.",
  "proposalForm.notesAdded": "Notas añadidas a la descripción del trabajo",
  "proposalForm.readPhotoFailed": "No se pudo leer la foto",
  "proposalForm.generated": "Cotización generada",
  "proposalForm.choosePlaceholder": "Elija…",
  "proposalForm.customerNamePh": "Juan Pérez",
  "proposalForm.customerEmailPh": "juan@email.com",
  "proposalForm.taxUseProfile": "Usar perfil / valor por defecto IA",
  "proposalForm.uploadNotesAria": "Subir foto de notas manuscritas",
  "proposalForm.jobDescriptionPlaceholder":
    "p. ej. Cambiar termo 150 l, cobre a multicapa, permiso. Mano de obra (horas × tarifa o precio cerrado), subcontratas, acceso, normativa. Precie repuestos en la tabla abajo.",

  "assistant.placeholder": "¿Qué debe cambiar en esta cotización?",

  "quoteHelper.title": "Asistente de cotización",
  "quoteHelper.errorTitle": "Error del asistente de cotización",
  "quoteHelper.hint":
    "Puede ajustar partidas, alcance, totales, condiciones, notas y fechas. Revise antes de enviar al cliente.",
  "quoteHelper.opening":
    "Ejemplo: cambiar una línea, una fecha o poner el depósito al 25%.",
  "quoteHelper.placeholder": "¿Qué debe actualizarse?",
  "quoteHelper.apply": "Aplicar",
  "quoteHelper.hide": "Ocultar asistente",
  "quoteHelper.show": "Mostrar asistente de cotización",
  "quoteHelper.undoLast": "Deshacer último cambio",
  "quoteHelper.undoOk": "Se restauraron los campos anteriores de la cotización.",
  "quoteHelper.moreActionsArchiveNote":
    "Las cotizaciones quedan en su historial. Para borrar una de forma permanente, elimínela desde Cotizaciones archivadas en Ajustes.",
  "quoteHelper.upgradeBlurb":
    "La redacción de cotizaciones con IA y el asistente de cotización (cambios en una cotización existente) están incluidos en todos los planes: Starter y Solo reciben algunas acciones de IA al día, y Pro y Agency son ilimitados. Inicie un plan para ayuda redactando a partir de una frase y ajustando alcance, partidas, calendario, condiciones y precios.",
  "quoteHelper.liveQuoteBanner":
    "Esta cotización ya se compartió con el cliente. Envíela de nuevo cuando quiera que reciba un correo actualizado.",
  "quoteHelper.liveQuoteCalloutTitle": "Copia del cliente de esta cotización",
  "quoteHelper.thinking": "Trabajando…",

  "dashboard.team.crewCodeModalTitle": "Código del calendario del equipo",
  "dashboard.team.crewCodeModalHint":
    "El personal lo ingresa en /crew para abrir el calendario de la empresa. Cámbielo o quite el acceso en Ajustes → Equipo.",
  "dashboard.team.notConfiguredYet": "Aún no hay código. Genérelo en Ajustes → Equipo.",
  "dashboard.team.openTeamSettings": "Abrir ajustes del equipo",

  "lineItem.categoryTitle": "Categoría en el presupuesto al cliente (mano de obra, materiales, sub, tasas).",
  "lineItem.leadTimeTitle": "Plazo en días (solo su planificación; no se muestra al cliente).",

  "progress.generatingQuote": "Generando cotización",

  "brand.homeAria": "Inicio Jorvio Quotes",
  "brand.nameAria": "Jorvio Quotes",

  "archived.title": "Cotizaciones archivadas",
  "archived.deleteFailed": "No se pudo eliminar la cotización",
  "archived.deleted": "Cotización eliminada",
  "archived.allDeleted": "Se eliminaron todas las archivadas",
  "archived.emptyTitle": "No hay cotizaciones archivadas",
  "archived.deleteTitle": "Eliminar cotización archivada",

  "followUp.agencyRequired": "Requiere Agency",
  "followUp.agencyOnly": "La automatización personalizada está en el plan Agency.",
  "followUp.checkSteps": "Revise los pasos",
  "followUp.stepInvalid": "Cada paso necesita retraso y mensaje válidos.",
  "followUp.saveFailed": "Error al guardar",
  "followUp.saved": "Automatización guardada",

  "portal.loadFailed": "No se pudieron cargar las cotizaciones",
  "portal.enterEmail": "Introduzca su correo",
  "portal.sendCodeFailed": "No se pudo enviar el código",
  "portal.codeSent": "Código enviado",
  "portal.checkEmail": "Revise su correo.",
  "portal.enterEmailAndCode": "Introduzca correo y código",
  "portal.signInFailed": "Error al iniciar sesión",
  "portal.invalidCode": "Código no válido",
  "portal.signedIn": "Sesión iniciada",
  "portal.signedInDesc": "Verá las cotizaciones enviadas a este correo.",
  "portal.signedOut": "Sesión cerrada",

  "publicQuote.paymentReceived": "Pago recibido",
  "publicQuote.paymentThanks": "Gracias. Su pago se procesó correctamente.",
  "publicQuote.paymentCanceled": "Pago cancelado",
  "publicQuote.noCharge": "No se realizó ningún cargo.",
  "publicQuote.paymentConfirmingTitle": "Confirmando su pago…",
  "publicQuote.paymentConfirmingBody":
    "Se realizó el cargo a su tarjeta. Estamos esperando la confirmación final de Stripe; normalmente tarda solo unos segundos. Por favor, no intente pagar de nuevo.",
  "publicQuote.paymentConfirmingTakingLongerTitle": "Todavía confirmando su pago",
  "publicQuote.paymentConfirmingTakingLongerBody":
    "Stripe está tardando un poco más de lo habitual. Solo se cobró su tarjeta una vez. Actualice esta página en un minuto, o comuníquese con su contratista si no se actualiza.",
  "publicQuote.couldNotSend": "No se pudo enviar",
  "publicQuote.requestSent": "Solicitud enviada",
  "publicQuote.contractorFollowUp": "El contratista le responderá.",
  "publicQuote.couldNotAnswer": "No se pudo responder ahora",
  "publicQuote.acceptAgreements": "Acepte los términos de la plataforma y los de pago de Stripe abajo.",
  "publicQuote.signatureBeforePayTitle": "Firme para autorizar el pago",
  "publicQuote.signatureBeforePayHint":
    "Dibuje su firma, pulse «Usar firma» y luego continúe al pago. Se aplica al pagar el depósito o el total.",
  "publicQuote.signatureRequired": "Guarde su firma antes de pagar.",
  "publicQuote.signatureCaptured": "Firma guardada. Puede borrarla y volver a firmar si lo necesita.",
  "publicQuote.signatureClear": "Borrar",
  "publicQuote.signatureUse": "Usar firma",
  "publicQuote.signatureStepIntro":
    "Dibuje su firma, pulse «Usar firma» y luego continúe a Stripe. Si borra el lienzo, la firma guardada se borra hasta que la guarde otra vez.",
  "publicQuote.continueToSignatureButton": "Continuar a la firma — {{amount}}",
  "publicQuote.continueToCheckoutButton": "Continuar al pago seguro — {{amount}}",
  "publicQuote.backFromSignatureStep": "Atrás",
  "publicQuote.paymentStartFailed": "No se pudo iniciar el pago",
  "publicQuote.askPlaceholder":
    "Pregunte por qué va algo incluido, qué cambia la opción más barata o qué opción encaja mejor.",
  "publicQuote.adjustPlaceholder": "¿Qué le gustaría ajustar?",
  "publicQuote.languageViewUpdated": "Idioma de la página actualizado. El texto se traduce solo para verlo aquí.",
  "publicQuote.bannerP1":
    "No necesita cuenta: puede leer esta cotización, aceptar, pagar y escribir a su contratista desde esta misma página.",
  "publicQuote.bannerPortalLink": "Entrar al portal del cliente",
  "publicQuote.bannerP2":
    " con su correo (opcional) para ver todas las cotizaciones que le hayan enviado y seguir las novedades en un solo lugar.",
  "publicQuote.scopeHeading": "Alcance del trabajo",
  "publicQuote.datesHeading": "Fechas importantes y plazos",
  "publicQuote.datesIntro":
    "La ventana en obra indicada es tentativa hasta que acepte esta cotización y el contratista confirme la agenda.",
  "publicQuote.validUntilPrefix": "Precios vigentes hasta el ",
  "publicQuote.validUntilSuffix":
    ". Es el último día en que se ofrecen estas partidas y totales tal como aparecen; no es la fecha de fin de obra.",
  "publicQuote.tentativeOnsite": "Ventana tentativa en obra: ",
  "publicQuote.startTbd": "Inicio por confirmar",
  "publicQuote.to": " a ",
  "publicQuote.contractorNotePrefix": "De su contratista: ",
  "publicQuote.jobSiteHeading": "Obra / ubicación",
  "publicQuote.photosHeading": "Fotos del sitio y planos",
  "publicQuote.pricingHeading": "Precios",
  "publicOptions.heading": "Elija una opción",
  "publicOptions.intro": "Elija la opción que más le convenga. Su contratista se comunicará para concretar los detalles.",
  "publicOptions.recommended": "Recomendada",
  "publicOptions.timeframeLabel": "Plazo",
  "publicOptions.choose": "Elegir esta opción",
  "publicOptions.selected": "Seleccionada",
  "publicOptions.chosenBadge": "Su elección",
  "publicOptions.confirm": "Confirmar mi elección",
  "publicOptions.confirming": "Guardando…",
  "publicOptions.confirmHint": "No necesita pago ni firma: esto solo le indica a su contratista qué opción prefiere.",
  "publicOptions.confirmError": "No pudimos guardar su elección. Inténtelo de nuevo.",
  "publicOptions.confirmedTitle": "Elección enviada",
  "publicOptions.confirmedBody": "¡Gracias! Se ha notificado a su contratista de su selección.",
  "publicOptions.lockedTitle": "Ya hizo su elección: su contratista se comunicará con usted.",
  "publicOptions.lockedBody": "Ya eligió una opción en esta cotización.",
  "publicQuote.addonHint":
    "Use Incluir para sumar o quitar mejoras opcionales. El total y el pago se actualizan solos.",
  "publicQuote.itemizedHidden":
    "No se muestran las líneas de detalle en esta cotización. El total refleja el alcance completo presupuestado por su contratista.",
  "publicQuote.subtotalLabel": "Subtotal",
  "publicQuote.discountLabel": "Descuento",
  "publicQuote.taxLineLabel": "Impuesto sobre las ventas ({{rate}}%)",
  "publicQuote.markupLineLabel": "Margen",
  "publicQuote.totalLabel": "Total",
  "publicQuote.paymentRefunded": "El pago fue reembolsado. Si tiene dudas, contacte a su contratista.",
  "publicQuote.paymentPaidFull": "Pagado en su totalidad. ¡Gracias!",
  "publicQuote.statusAcceptedTitle": "Aceptado",
  "publicQuote.statusPaidInFull": "Pagado en su totalidad",
  "publicQuote.statusAcceptedNoPaymentYet": "Has aceptado este presupuesto. Tu contratista ha sido notificado.",
  "publicQuote.statusAcceptedBalanceDue": "Primer pago de {{paid}} recibido. El saldo de {{balance}} se debe en un pago separado cuando el trabajo se acerque a su finalización — vuelve a esta misma página para pagarlo.",
  "publicQuote.statusAcceptedReturnLater": "Siempre puedes volver a esta página para ver el presupuesto, pagar la siguiente cuota o enviar un mensaje a tu contratista.",
  "publicQuote.paymentBelowDecision": "La aceptación final y el pago están debajo de la guía de decisión.",
  "publicQuote.changeOrdersHeading": "Órdenes de cambio",
  "publicQuote.changeOrdersIntro":
    "Trabajo adicional que su contratista envió después de la cotización original. Al aceptar, el monto se suma a su total.",
  "publicQuote.coAwaiting": "Esperando su aprobación",
  "publicQuote.coAccepted": "Aceptada",
  "publicQuote.coAcceptBtn": "Aceptar orden de cambio",
  "publicQuote.coSaving": "Guardando…",
  "publicQuote.decisionHeading": "Guía de decisión",
  "publicQuote.decisionAskTitle": "Pregunte sobre esta cotización",
  "publicQuote.decisionGoalClarity": "Ayúdeme a entenderla",
  "publicQuote.decisionGoalBudget": "Mejor costo inicial más bajo",
  "publicQuote.decisionGoalLongevity": "Mejor valor a largo plazo",
  "publicQuote.decisionGoalSpeed": "Mejor para terminar antes",
  "publicQuote.decisionGoalConfidence": "Ayúdeme a decidir con confianza",
  "publicQuote.decisionGoalHint": "Elija lo que más importa y luego escriba o edite su pregunta abajo.",
  "publicQuote.decisionThinking": "Pensando…",
  "publicQuote.decisionGetGuidance": "Obtener orientación",
  "publicQuote.decisionSuggestedNext": "Siguiente paso sugerido:",
  "publicQuote.regretHeading": "Confianza y tranquilidad",
  "publicQuote.regretProtects": "Qué le protege",
  "publicQuote.regretWhatNext": "Qué sigue después",
  "publicQuote.warrantyHeading": "Garantía",
  "publicQuote.notesHeading": "Notas",
  "publicQuote.acceptHeading": "Aceptar y pago",
  "publicQuote.acceptIntro": "Revise este último paso y use un solo botón para aceptar la cotización y completar el pago.",
  "publicQuote.acceptRecommended": "Recomendada",
  "publicQuote.acceptedTitle": "Aceptó esta cotización. ¡Gracias!",
  "publicQuote.acceptedRecommended": "Opción recomendada elegida:",
  "publicQuote.requestChanges": "Pedir cambios",
  "publicQuote.requestChangesTitle": "Pedir cambios",
  "publicQuote.changeRequestDescription": "Indique a su contratista qué debe actualizar en esta estimación.",
  "publicQuote.changeRequestFieldLabel": "Cambios solicitados",
  "publicQuote.sendToContractor": "Enviar al contratista",
  "publicQuote.colInclude": "Incluir",
  "publicQuote.colDescription": "Descripción",
  "publicQuote.colQty": "Cant.",
  "publicQuote.colUnit": "Unidad",
  "publicQuote.transactionFeeLineLabel": "Tarifa de transacción Stripe",
  "publicQuote.colPrice": "Precio ($)",
  "publicQuote.colTotal": "Total ($)",
  "publicQuote.colType": "Tipo",
  "publicQuote.colReadyBy": "Listo para",
  "publicQuote.optionalAddon": "Opción adicional",
  "publicQuote.lineKindLabor": "Mano de obra",
  "publicQuote.lineKindMaterial": "Materiales y equipo",
  "publicQuote.lineKindSubcontractor": "Subcontratista",
  "publicQuote.lineKindFee": "Honorarios y permisos",
  "publicQuote.lineKindOther": "Otros",
  "publicQuote.lineItemsFallback": "Partidas",
  "publicQuote.nextPayFull": "Pagar el total",
  "publicQuote.nextPayDeposit": "Pagar depósito del {{pct}}%",
  "publicQuote.nextPayBalance": "Pagar saldo",
  "publicQuote.estimatedTotal": "Total estimado {{amount}}",
  "publicQuote.loadTitle": "No pudimos abrir esta cotización",
  "publicQuote.loadNotFound":
    "No tenemos una cotización para este enlace. Revise si está bien escrito o pida a su contratista que le envíe uno nuevo.",
  "publicQuote.loadServer":
    "No pudimos cargar esta cotización. Espere un momento y vuelva a intentar el enlace. Si sigue fallando, pida a su contratista que lo reenvíe o revise su configuración.",
  "publicQuote.loadNetwork": "No pudimos conectar con el servidor. Compruebe su conexión a internet e inténtelo de nuevo.",
  "publicQuote.notPayingFullTitle": "Hoy no está pagando el total completo",
  "publicQuote.notPayingFullLi1":
    "Solo en este pago: {{amount}} ({{depositPct}}% de {{total}}). Solo se carga esa cantidad en su tarjeta, no el presupuesto completo.",
  "publicQuote.notPayingFullLi2":
    "Después: Los {{remainder}} restantes ({{balancePct}}%) se pagan en un segundo pago desde esta misma página, cuando su contratista esté listo.",
  "publicQuote.balanceDueTitle": "Saldo pendiente",
  "publicQuote.balanceDueBody":
    "Ya pagó {{paid}}. Este pago es solo por el restante {{remaining}}, nada más.",
  "publicQuote.partialPaidSummary": "Pagado hasta ahora: {{paid}}. Restante: {{remaining}}.",
  "publicQuote.paymentTermsHeading": "Condiciones de pago",
  "publicQuote.additionalTermsHeading": "Condiciones adicionales",
  "publicQuote.agreeTermsPrefix": "He leído y acepto los",
  "publicQuote.termsOfService": "Términos del servicio",
  "publicQuote.agreePrivacyPrefix": "He leído y acepto la",
  "publicQuote.privacyPolicy": "Política de privacidad",
  "publicQuote.agreeJorvioPortalLead": "He leído y acepto los",
  "publicQuote.agreeJorvioPortalMid": " y la ",
  "publicQuote.agreeJorvioPortalTail":
    " de la plataforma Jorvio Quotes que muestra esta cotización (no es lo mismo que su acuerdo con el contratista por el trabajo).",
  "publicQuote.agreeStripePaymentLead": "Para pago con tarjeta, entiendo que Stripe procesará el cargo y acepto",
  "publicQuote.stripePaymentTermsLink": "los términos de Stripe para este pago",
  "publicQuote.agreeStripePaymentTail": " (además del importe del botón Pagar).",
  "publicQuote.payConfirmShort":
    "Confirma que entiende el importe del botón de pago y el alcance del contratista en esta cotización.",
  "publicQuote.payConfirmWithProcessingShort":
    "Confirma que entiende el total en el botón (incluye una tarifa de transacción Stripe como estimación aproximada) y el alcance del contratista en esta cotización.",
  "publicQuote.processingFeeTitle": "Tarifa de transacción Stripe",
  "publicQuote.processingFeeRoughHint": "estimación aproximada",
  "publicQuote.processingFeeBody":
    "Se añade una línea de tarifa de transacción Stripe (estimación aproximada) en el pago: unos {{processing}} en este cargo. Se cobra {{total}} en total; las comisiones reales pueden variar.",
  "publicQuote.processingFeeBasisNote":
    "Esta tarifa estimada usa el total de esta cotización (impuestos incluidos si corresponde) y se suma al pagar con tarjeta; no forma parte del subtotal del trabajo arriba.",
  "publicQuote.redirectingCheckout": "Redirigiendo al pago…",
  "publicQuote.acceptPayButton": "Aceptar cotización y pagar — {{amount}}",
  "publicQuote.coLineDefault": "Línea",
  "publicQuote.photoAlt": "Obra o plano {{n}}",
  "publicQuote.tryAgain": "Inténtelo de nuevo.",
  "publicQuote.couldNotUpdateAddon": "No se pudo actualizar el complemento",
  "publicQuote.couldNotAcceptCo": "No se pudo aceptar la orden de cambio",
  "publicQuote.coAcceptedToast": "Orden de cambio aceptada",
  "publicQuote.coAcceptedToastDesc": "Se actualizó el total de su cotización.",

  "accept.signFirst": "Firme primero",
  "accept.accepted": "¡Cotización aceptada!",

  "calendar.entryUpdated": "Entrada actualizada",
  "calendar.loadFailed": "No se pudo cargar el calendario",

  "editor.toast.couldNotSend": "No se pudo enviar",
  "editor.toast.saveFailed": "Error al guardar",
  "editor.toast.saved": "Guardado",
  "editor.toast.sendFixSave": "Corrija primero el error de guardado.",
  "editor.toast.sendFailed": "Error al enviar",
  "editor.toast.emailSent": "Correo enviado",
  "editor.toast.duplicateFailed": "Error al duplicar",
  "editor.toast.updateFailed": "Error al actualizar",
  "editor.toast.markedDeclined": "Marcada como rechazada",
  "editor.toast.archiveFailed": "Error al archivar",
  "editor.toast.enterCustomerName": "Introduzca el nombre del cliente",
  "editor.toast.customerAttached": "Cliente vinculado",
  "editor.toast.templateSaveFailed": "No se pudo guardar la plantilla",
  "editor.toast.templateSaved": "Plantilla guardada",
  "editor.toast.enterName": "Introduzca un nombre",
  "editor.toast.directoryNameHint": "Proveedor, sub, proveedor o contacto a pagar.",
  "editor.toast.addedDirectory": "Añadido al directorio",
  "editor.toast.assistantUpdated": "El asistente de cotización actualizó la cotización",
  "editor.toast.quoteHelperSaveOk": "Cambios guardados",
  "editor.previewPdf": "Vista previa PDF",
  "editor.backToQuotes": "Volver a cotizaciones",
  "editor.customerShowItemizedPricing": "Mostrar precios línea por línea en la cotización del cliente",
  "editor.customerShowItemizedPricingHelp":
    "Desactivado: el cliente solo ve el total en el enlace, vista previa y PDF (los interruptores de opciones no se muestran). Sus opciones guardadas siguen afectando el importe.",
  "editor.customerAcceptanceIntro": "Controle cómo el enlace del cliente maneja el pago y las firmas.",
  "editor.collectPayment": "Cobrar en esta cotización (con Stripe)",
  "editor.collectPaymentHelp":
    "Activado: pueden pagar en el enlace si hay una cuenta de pagos conectada en Ajustes. Desactivado: pueden aceptar sin pagar en línea; usted cobra aparte.",
  "editor.collectPaymentBlocked":
    "Conecte una cuenta de pagos para activar el cobro en el enlace de la cotización.",
  "editor.collectPaymentBlockedSettingsLink": "Abrir ajustes de pagos",
  "editor.collectPaymentBlockedTail":
    "Puede enviar la cotización por correo o PDF sin cuenta de pagos.",
  "editor.collectPaymentDepositTiming":
    "Los fondos llegan a su banco unos 2 días hábiles después de cada pago (calendario estándar de Stripe). El primer depósito puede tardar de 7 a 14 días mientras Stripe verifica la cuenta.",
  "editor.processingFeeBasisNote":
    "La estimación se basa en el total al cliente (su trabajo cotizado, impuestos y descuentos según se muestran) y se añade en el pago encima; no reduce su margen.",
  "editor.requireCustomerSignature": "Exigir firma del cliente",
  "editor.requireCustomerSignatureHelp":
    "Activado: deben dibujar una firma antes de aceptar. Desactivado: pueden aceptar con un toque.",
  "editor.duplicate": "Duplicar",
  "editor.saveTemplate": "Guardar como plantilla",
  "editor.archiveTitleTip":
    "Archive para quitarla de la lista principal. Las archivadas se borran automáticamente a los 60 días.",
  "editor.declineTitleTip":
    "Si el cliente rechaza. Solo actualiza el estado; no envía correo al cliente.",
  "editor.saveTitleTip": "También se guarda solo unos segundos tras dejar de editar",
  "editor.save": "Guardar",
  "editor.sendToCustomer": "Enviar al cliente",
  "editor.markDeclined": "Marcar rechazada",
  "editor.archive": "Archivar",
  "editor.customerAcceptance": "Aceptación del cliente",
  "editor.customer": "Cliente",
  "editor.noCustomer": "Sin cliente",
  "editor.createAttach": "Crear y vincular",
  "editor.sitePhotos": "Fotos del sitio y plano",
  "editor.quoteDetails": "Detalles de la cotización",
  "editor.schedule": "Calendario / plazos",
  "editor.decisionMode": "Modo decisión",
  "editor.decisionModeHelp":
    "Actívelo para añadir hasta tres opciones al cliente (por ejemplo Básico / Mejor / Óptimo). Verán una comparación en su cotización y el asistente para preguntar. Ideal cuando ofrece paquetes reales, no un solo precio fijo.",
  "editor.regretProof": "Confianza y tranquilidad",
  "editor.trustReassuranceHelp":
    "Texto breve que el cliente ve cerca del precio: por qué decir sí con tranquilidad, nota opcional de espera o coste, viñetas de confianza y siguientes pasos. Úselo en trabajos grandes o si comparan ofertas; omítalo en arreglos pequeños.",
  "editor.missingCompanyNameBanner":
    "Los clientes ven su empresa en la cotización. Añádalo en",
  "editor.missingCompanyNameSettingsLink": "Datos del negocio",
  "editor.internalCosts": "Interno: costes, margen y suministro",
  "editor.addCostLine": "Añadir línea de coste",
  "editor.quickAddContact": "Añadir contacto rápido",
  "editor.lineItemsCheckout": "Partidas, precios y pago",
  "editor.warrantyNotes": "Garantía y otras notas",
  "editor.removeCostLine": "Quitar línea de coste",
  "editor.ph.customerName": "María García",
  "editor.ph.customerEmail": "maria@email.com",
  "editor.ph.customerPhone": "(555) 123-4567",
  "editor.ph.summaryCustomer": "Lo verán en su enlace de presupuesto — claro y accionable.",
  "editor.ph.signedNote": "Suele rellenarse al aceptar. Puede editarlo aquí.",
  "editor.ph.scheduleNote":
    "Lenguaje claro: cuándo llegan materiales, cuándo empieza el trabajo, que las fechas in situ son tentativas.",
  "editor.ph.optionLabel": "Básico / Mejor / Óptimo",
  "editor.ph.optionBestFor": "Para qué encaja esta opción",
  "editor.ph.optionTotal": "Total de la opción",
  "editor.ph.recommendedSummary": "Resumen recomendado para el cliente",
  "editor.ph.tradeoff": "Equilibrio: precio vs durabilidad vs plazo",
  "editor.ph.budgetGuidance": "Orientación para presupuesto ajustado",
  "editor.ph.longevityGuidance": "Orientación para máxima durabilidad",
  "editor.ph.faq": "FAQs, una por línea: Pregunta | Respuesta",
  "editor.ph.comparison": "Comparación: Etiqueta | Opción | Valor",
  "editor.ph.confidence": "Por qué tiene sentido y en qué puede confiar",
  "editor.ph.waitRisk": "Qué cambia si espera (plazos, precio, alcance)",
  "editor.ph.trustSignals": "Señales de confianza, una por línea",
  "editor.ph.whatNext": "Qué sigue, un paso por línea",
  "editor.ph.supplierName": "Proveedor, subcontratista, empresa…",
  "editor.ph.costDescription": "Breve descripción de este coste",
  "editor.ph.internalNotes": "Solo interno — no en el PDF al cliente",
  "editor.nav.quickNotes": "Notas rápidas",
  "editor.quickNotes.title": "Notas rápidas",
  "editor.quickNotes.description":
    "Recordatorios breves con fecha y hora para su equipo en esta cotización. No visibles para el cliente.",
  "editor.quickNotes.placeholder": "Añadir un recordatorio…",
  "editor.quickNotes.add": "Añadir nota",
  "editor.quickNotes.adding": "Añadiendo…",
  "editor.quickNotes.empty": "Aún no hay notas rápidas.",
  "editor.quickNotes.delete": "Eliminar",
  "editor.quickNotes.deleteTitle": "Quitar esta nota",
  "editor.quickNotes.toastAdded": "Guardado en notas internas",
  "editor.quickNotes.toastDeleted": "Nota eliminada",
  "editor.quickNotes.toastSaveFailed": "No se pudo añadir la nota",
  "editor.quickNotes.toastDeleteFailed": "No se pudo eliminar la nota",
  "editor.quickNotes.inlinePlaceholder": "Escriba una nota; Enter o Añadir",
  "editor.quickNotes.removeTitle": "Quitar nota",
  "editor.ph.depositNotSet": "Sin definir",
  "editor.ph.warranty": "Garantía de mano de obra, fabricante, etc.",
  "editor.ph.finePrint": "Letra pequeña, exclusiones o contexto en la vista cliente",
  "editor.invalidDateYmd": "Use AAAA-MM-DD para la fecha prevista.",

  "help.title": "Ayuda",
  "help.subtitle":
    "Ayuda general sobre el producto. No ve sus cotizaciones, clientes ni datos de cuenta.",
  "help.contactTitle": "Contáctenos",
  "help.contactBody": "¿Necesita a alguien del equipo? Use el formulario de abajo.",
  "help.contactButton": "Enviar mensaje",
  "help.escalateBanner":
    "No pudimos responder solo con la ayuda general. Abra Contáctenos (el formulario en esta página).",
  "help.aiUnavailable":
    "El asistente de ayuda no está disponible ahora. Use el formulario de contacto abajo.",
  "help.placeholder": "p.ej. ¿Dónde envío la cotización al cliente?",
  "help.send": "Preguntar",
  "help.sending": "Pensando…",
  "help.clear": "Borrar chat",
  "help.you": "Usted",
  "help.assistant": "Asistente de ayuda",
  "help.assistantCardDescription":
    "Pregunte cómo funcionan las funciones o dónde encontrarlas. Este asistente solo tiene conocimiento general del producto—no ve sus cotizaciones ni datos de cuenta.",
  "help.assistantTyping": "Respondiendo…",
  "help.disclaimer":
    "No pegue contraseñas ni datos confidenciales de clientes o pagos aquí.",

  "help.bugReport.title": "Informar de un bug",
  "help.bugReport.subtitle":
    "Describa qué falló y qué estaba haciendo. Las fotos opcionales ayudan a reproducirlo. Leemos cada envío.",
  "help.bugReport.descriptionLabel": "Qué salió mal",
  "help.bugReport.descriptionPlaceholder": "Describa qué falló — cuanto más detalle, mejor.",
  "help.bugReport.contextLabel": "¿Qué estaba haciendo cuando ocurrió?",
  "help.bugReport.contextPlaceholder": "¿Qué estaba haciendo justo antes? Explique los pasos.",
  "help.bugReport.screenshotsLabel": "Fotos (opcional)",
  "help.bugReport.screenshotsHint": "Hasta 4 imágenes (PNG, JPEG, WebP o GIF), 4 MB cada una.",
  "help.bugReport.submit": "Enviar informe",
  "help.bugReport.submitting": "Enviando…",
  "help.bugReport.successTitle": "Gracias — hemos recibido su informe",
  "help.bugReport.successBody":
    "Nuestro equipo revisará los datos enviados. No necesita hacer nada más; si hace falta más información, nos pondremos en contacto por correo.",
  "help.bugReport.sendAnother": "Enviar otro informe",
  "help.bugReport.descTooShort": "Añada un poco más de detalle (al menos 10 caracteres) en «Qué salió mal».",
  "help.bugReport.contextTooShort": "Indique un poco más qué estaba haciendo (al menos 5 caracteres).",
  "help.bugReport.fileTooLarge": "Cada imagen debe ser de 4 MB o menos.",
  "help.bugReport.fileTypeInvalid": "Solo se permiten imágenes PNG, JPEG, WebP o GIF.",
  "help.bugReport.failedTitle": "No se pudo enviar el informe",

  "help.attachments.dropTitle": "Añadir fotos",
  "help.attachments.dropSubtitle": "Arrastre y suelte o pulse — PNG, JPEG, WebP o GIF.",
  "help.attachments.removePhoto": "Quitar foto",

  "help.contactForm.jumpToForm": "Ir al formulario de contacto",
  "help.contactForm.subjectLabel": "Asunto (opcional)",
  "help.contactForm.subjectPlaceholder": "p.ej. Facturación / inicio de sesión",
  "help.contactForm.messageLabel": "Mensaje",
  "help.contactForm.messagePlaceholder":
    "Sea lo más detallado posible. Cuéntenos exactamente qué pasó justo antes del problema y los pasos que siguió.",
  "help.contactForm.hint":
    "Leemos todos los mensajes. Puede añadir fotos abajo. Si algo falló, también puede usar Informar de un bug arriba.",
  "help.contactForm.attachmentsLabel": "Fotos (opcional)",
  "help.contactForm.attachmentsHint": "Hasta 4 imágenes (PNG, JPEG, WebP o GIF), 4 MB cada una.",
  "help.contactForm.submit": "Enviar al equipo",
  "help.contactForm.submitting": "Enviando…",
  "help.contactForm.successTitle": "Mensaje enviado",
  "help.contactForm.successBody":
    "Nuestro equipo lo revisará. Si necesitamos algo más, responderemos al correo de su cuenta.",
  "help.contactForm.successBodyGuest":
    "Nuestro equipo lo revisará. Si necesitamos algo más, responderemos al correo que indicó arriba.",
  "help.contactForm.sendAnother": "Enviar otro mensaje",
  "help.contactForm.failedTitle": "No se pudo enviar. Inténtelo de nuevo.",

  "contactPage.signedInNote":
    "Nos escribe desde su cuenta iniciada: {{name}}, {{email}}. Usaremos este correo para responder.",
  "contactPage.guestNameLabel": "Su nombre",
  "contactPage.guestEmailLabel": "Su correo",
  "contactPage.guestNamePlaceholder": "Nombre completo",
  "contactPage.guestEmailPlaceholder": "su@correo.com",
  "marketing.contact.pageTitle": "Contacto",
  "marketing.contact.pageSubtitle":
    "Envíe un mensaje al equipo de Jorvio. Responderemos en el correo con el que inició sesión o el que indique abajo.",
  "help.agencyMailtoIntro":
    "Su plan incluye soporte prioritario. Para una respuesta rápida, escríbanos directamente desde su app de correo.",
  "help.agencyMailtoCta": "Abrir correo para contactar soporte",

  "help.triage.title": "¿Cuál es su problema?",
  "help.triage.subtitle": "Responda unas preguntas: le sugeriremos un arreglo antes de escalar.",
  "help.triage.back": "Atrás",
  "help.triage.startOver": "Empezar de nuevo",
  "help.triage.option.other": "Otra",
  "help.triage.pendingTopicLabel": "El próximo mensaje incluirá el tema:",
  "help.triage.chatTopicWrap": "[Tema de ayuda: {{topic}}]",
  "help.triage.tryThisTitle": "Pruebe esto primero",
  "help.triage.didNotWork": "No funcionó — hablar con el asistente",
  "help.triage.contactInstead": "Escribir al equipo",
  "help.triage.reportBugInstead": "Abrir Informar de un bug con capturas",
  "help.triage.describeIssuePlaceholder":
    "Describa el problema: qué probó, qué esperaba y qué ocurrió en su lugar.",
  "help.triage.cta.ai": "Continuar con el asistente de ayuda",
  "help.triage.cta.contact": "Abrir formulario de contacto",
  "help.triage.cta.bug": "Abrir Informar de un bug",

  "help.triage.prompt.root": "¿Qué describe mejor su problema?",
  "help.triage.prompt.account": "¿Qué tema de inicio de sesión o cuenta encaja mejor?",
  "help.triage.prompt.quotes": "¿Qué parte de las cotizaciones o el envío falla?",
  "help.triage.prompt.payments": "¿Qué tema de pagos es?",
  "help.triage.prompt.subscription": "¿Qué tema de facturación es?",
  "help.triage.prompt.calendar": "¿Qué tema de calendario o equipo es?",
  "help.triage.prompt.customers": "¿Qué problema con los clientes es?",
  "help.triage.prompt.settings": "¿Qué área de configuración?",
  "help.triage.prompt.portal": "¿Qué problema con el portal del propietario?",
  "help.triage.prompt.bug": "¿Qué tipo de problema es?",

  "help.triage.opt.account": "Inicio de sesión, contraseña o acceso al espacio de trabajo",
  "help.triage.opt.quotes": "Crear, editar o enviar cotizaciones",
  "help.triage.opt.payments": "Pago del cliente o pagos de Stripe",
  "help.triage.opt.subscription": "Su plan o factura de Jorvio",
  "help.triage.opt.calendar": "Calendario de la empresa o acceso del equipo",
  "help.triage.opt.customers": "Registros de clientes",
  "help.triage.opt.settings": "Perfil, marca, Stripe Connect, equipo",
  "help.triage.opt.portal": "Enlace de cotización o inicio en el portal",
  "help.triage.opt.bug": "Algo parece roto",

  "help.triage.opt.acct.login": "Inicio de sesión, enlace mágico o correo de verificación",
  "help.triage.opt.acct.invite": "Invitar co-operadores o acceso del equipo",
  "help.triage.opt.acct.security": "Cambio de contraseña o sesiones / dispositivo",
  "help.triage.opt.acct.access": "Bloqueado, espacio equivocado o acceso que no se arregla en la app",

  "help.triage.opt.quotes.send": "Enviar al cliente, cola o «no enviado»",
  "help.triage.opt.quotes.email": "El cliente no recibió el correo del presupuesto",
  "help.triage.opt.quotes.pdf": "PDF o vista previa incorrectos",
  "help.triage.opt.quotes.editor": "Editar partidas, precios o guardado del borrador",
  "help.triage.opt.quotes.pay": "Depósito / pago en la página pública del presupuesto",

  "help.triage.opt.pay.checkout": "El propietario paga en el enlace (tarjeta)",
  "help.triage.opt.pay.connect": "Conectar Stripe o terminar el alta",
  "help.triage.opt.pay.payout": "Pagos a cuenta, banco o verificación en Stripe",
  "help.triage.opt.pay.dispute": "Reembolso, contracargo o disputa",

  "help.triage.opt.sub.plan": "Cambiar plan, subir/bajar o cancelar",
  "help.triage.opt.sub.charge": "Cargo inesperado o importe incorrecto",
  "help.triage.opt.sub.receipt": "Recibo, factura o historial de pagos",

  "help.triage.opt.cal.crew": "Código de equipo, /crew o acceso al calendario",
  "help.triage.opt.cal.events": "Eventos, fechas o calendario que no cuadra",
  "help.triage.opt.cal.schedule": "Programación en cotizaciones o calendario",

  "help.triage.opt.cust.directory": "Añadir o editar clientes",
  "help.triage.opt.cust.wrong": "Datos de cliente incorrectos en una cotización",

  "help.triage.opt.set.stripe": "Stripe Connect, pagos o ajustes de cobro",
  "help.triage.opt.set.brand": "Logo, idioma o datos de la empresa",
  "help.triage.opt.set.reviews": "Correos para reseñas de Google",
  "help.triage.opt.set.team": "Códigos de co-operador, equipo o invitaciones",

  "help.triage.opt.port.otp": "Código por correo del portal o inicio de sesión",
  "help.triage.opt.port.view": "Lo que ven en el enlace o el portal",

  "help.triage.opt.bug.crash": "Mensaje de error, pantalla en blanco o bug claro",
  "help.triage.opt.bug.data": "Cifras incorrectas o cotizaciones que faltan",
  "help.triage.opt.bug.slow": "Carga lenta o tiempo de espera",

  "help.triage.summary.other": "Otro problema",
  "help.triage.summary.acctLogin": "Inicio / enlace mágico / correo de verificación",
  "help.triage.summary.acctInvite": "Invitaciones / acceso del equipo",
  "help.triage.summary.acctSecurity": "Contraseña / sesiones",
  "help.triage.summary.acctAccess": "Acceso a cuenta / espacio de trabajo",
  "help.triage.summary.quotesSend": "Enviar cotización al cliente",
  "help.triage.summary.quotesEmail": "Cliente no recibe correo",
  "help.triage.summary.quotesPdf": "PDF o vista previa",
  "help.triage.summary.quotesEditor": "Editor / partidas",
  "help.triage.summary.quotesPay": "Pago en enlace público",
  "help.triage.summary.payCheckout": "Pago del propietario",
  "help.triage.summary.payConnect": "Configuración Stripe Connect",
  "help.triage.summary.payPayout": "Pagos Stripe / banco",
  "help.triage.summary.payDispute": "Reembolso / disputa",
  "help.triage.summary.subPlan": "Cambio de plan / cancelación",
  "help.triage.summary.subCharge": "Cargo de suscripción inesperado",
  "help.triage.summary.subReceipt": "Recibo / factura",
  "help.triage.summary.calCrew": "Equipo / calendario",
  "help.triage.summary.calEvents": "Eventos del calendario",
  "help.triage.summary.calSchedule": "Programación",
  "help.triage.summary.custDirectory": "Clientes",
  "help.triage.summary.custWrong": "Datos incorrectos",
  "help.triage.summary.setStripe": "Stripe / pagos",
  "help.triage.summary.setBrand": "Marca / idioma",
  "help.triage.summary.setReviews": "Solicitudes de reseñas de Google",
  "help.triage.summary.setTeam": "Equipo / invitaciones",
  "help.triage.summary.portOtp": "OTP del portal",
  "help.triage.summary.portView": "Vista del propietario",
  "help.triage.summary.bugCrash": "Bug / error",
  "help.triage.summary.bugData": "Datos incorrectos o faltantes",
  "help.triage.summary.bugSlow": "Lento / tiempo de espera",

  "help.triage.solution.other":
    "- Actualice la página o pruebe otro navegador (Chrome o Edge suelen ir mejor).\n- Confirme que está en el espacio de trabajo correcto.\n- Anote el **texto exacto del error** o la URL si necesita el asistente.",
  "help.triage.solution.acctLogin":
    "- Revise **Spam** en busca de correos de Jorvio.\n- Espere ~60 s entre nuevos enlaces mágicos.\n- Pruebe una ventana de incógnito para descartar extensiones.\n- Use **Olvidé la contraseña** solo si entra con contraseña.",
  "help.triage.solution.acctInvite":
    "- Confirme que el correo de invitación coincide con cómo iniciará sesión.\n- Pídale que revise spam.\n- Reenvíe la invitación desde el equipo si la app lo permite.",
  "help.triage.solution.acctSecurity":
    "- Cierre otras sesiones en los ajustes de seguridad si algo parece raro.\n- Cambie la contraseña solo con el flujo de la app.\n- Nunca envíe contraseñas ni códigos por correo o chat.",
  "help.triage.solution.acctContact":
    "- Verifique el **mismo correo** que espera el administrador.\n- Pruebe otro navegador o dispositivo una vez.\n- Si sigue bloqueado, use **Escribir al equipo** — sin pegar contraseñas.",
  "help.triage.solution.quotesSend":
    "- Abra la cotización y vea si está **borrador o enviada**.\n- Use **Reenviar** si aparece.\n- Recupere la red: los envíos en cola a veces terminan al reconectar.",
  "help.triage.solution.quotesEmail":
    "- Revise que el correo del cliente no tenga errores.\n- Pídale que busque en spam.\n- Reenvíe desde la cotización cuando sea posible.",
  "help.triage.solution.quotesPdf":
    "- Use **Imprimir → Guardar como PDF** si el PDF de la app falla.\n- Pruebe Chrome en escritorio si el móvil se ve mal.",
  "help.triage.solution.quotesEditor":
    "- Espere a que termine el guardado automático antes de salir.\n- Actualice una vez si un campo no se queda.\n- Duplique como borrador nuevo si algo parece corrupto.",
  "help.triage.solution.quotesPay":
    "- Confirme que **Stripe Connect** está terminado en Pagos.\n- Abra el enlace público en una ventana privada para reproducir lo que ve el cliente.",
  "help.triage.solution.payCheckout":
    "- Compruebe que los cobros están activados y el enlace es válido.\n- Pruebe otra tarjeta o navegador; copie el **mensaje de Stripe** si aparece.",
  "help.triage.solution.payConnect":
    "- Complete todos los pasos del alta de Stripe Connect.\n- Revise el panel de Stripe por verificación de identidad o banco.",
  "help.triage.solution.payPayout":
    "- En Stripe verifique cuenta bancaria e identidad.\n- Confirme que los pagos no estén en pausa.\n- Reúna fechas e importes — **nunca** pegue el número completo de tarjeta.",
  "help.triage.solution.payDispute":
    "- Anote ID de cargo, fecha e importe.\n- Para coordinar reembolsos use **Escribir al equipo**.",
  "help.triage.solution.subPlan":
    "- Revise plan y renovación en Facturación.\n- Decida si quiere cambiar o cancelar antes de escribir.",
  "help.triage.solution.subCharge":
    "- Compare la fecha del cargo con facturas y cambios de plan (prorrateos).\n- Busque el recibo en el correo.",
  "help.triage.solution.subReceipt":
    "- Abra el historial de facturación en la app si existe.\n- Busque “factura” o “recibo” en el correo.",
  "help.triage.solution.calCrew":
    "- Regener o comparta el código de equipo desde ajustes.\n- Confirme la URL `/crew` y el espacio correcto.",
  "help.triage.solution.calEvents":
    "- Revise **zona horaria** en eventos vs cotización.\n- Active filtros si faltan entradas.",
  "help.triage.solution.calSchedule":
    "- Compare fechas programadas en la cotización con el calendario.\n- Actualice la cotización y vuelva a abrir el calendario.",
  "help.triage.solution.custDirectory":
    "- Edite el cliente en **Clientes** y confirme que la cotización se actualiza.",
  "help.triage.solution.custWrong":
    "- Corrija la ficha del cliente y actualice la cotización.\n- Cree una cotización nueva desde el cliente corregido si no se refresca.",
  "help.triage.solution.setStripe":
    "- Abra Pagos / Stripe y resuelva avisos.\n- Reabra el alta de Connect si falta algo.",
  "help.triage.solution.setBrand":
    "- Guarde logo y datos y fuerce actualización del enlace público.",
  "help.triage.solution.setReviews":
    "- Abra Ajustes → Reseñas y añada el enlace de reseñas de Google Business.\n- Configure el retraso y el mensaje; puede desactivarlo por presupuesto en el editor.\n- Los correos se envían solo después del pago completo en checkout.",
  "help.triage.solution.setTeam":
    "- Reenvíe invitaciones desde el equipo.\n- Revoca códigos antiguos si ya no deben acceder.",
  "help.triage.solution.portOtp":
    "- Pida que revise spam para el código.\n- Espere entre solicitudes de nuevo código.\n- Confirme el correo correcto.",
  "help.triage.solution.portView":
    "- Abra el enlace en **incógnito** para ver lo mismo que el cliente.\n- Confirme que la cotización está publicada.",
  "help.triage.solution.bugReport":
    "- Recargue en forzado (**Ctrl+Mayús+R**). Anote URL y error.\n- Use **Informar de un bug con capturas** si sigue fallando.",
  "help.triage.solution.bugWrong":
    "- Exporte o capture totales incorrectos con fecha.\n- Revise filtros antes de pensar en pérdida de datos.\n- **Escribir al equipo** con ejemplos (sin secretos).",
  "help.triage.solution.bugSlow":
    "- Indique Wi‑Fi vs datos, navegador y hora aproximada.\n- Desactive extensiones pesadas temporalmente.",

  "owner.bugReports.title": "Informes de problemas",
  "owner.bugReports.subtitle": "Envíos desde Ayuda → Informar de un bug (con fotos opcionales).",
  "owner.bugReports.when": "Cuándo",
  "owner.bugReports.from": "De",
  "owner.bugReports.page": "Página",
  "owner.bugReports.issue": "Problema",
  "owner.bugReports.context": "Qué hacía",
  "owner.bugReports.shots": "Fotos",
  "owner.bugReports.openImage": "Abrir",
  "owner.bugReports.status": "Estado",
  "owner.bugReports.notes": "Notas",
  "owner.bugReports.save": "Guardar",
  "owner.bugReports.empty": "Aún no hay informes.",
  "owner.bugReports.loadError": "No se pudieron cargar los informes",
  "owner.bugReports.saveError": "No se pudo guardar",
  "owner.bugReports.migrationTitle": "Migración necesaria",
  "owner.bugReports.migrationBody": "Ejecute 057_platform_bug_reports.sql en el editor SQL de Supabase.",

  "owner.issuesInbox.title": "Bandeja de soporte y bugs",
  "owner.issuesInbox.subtitle": "Todos los envíos en una sola lista (más recientes primero). Expanda una fila para ver detalles, notas y estado.",
  "owner.issuesInbox.typeBug": "Bug",
  "owner.issuesInbox.typeSupport": "Soporte",
  "owner.issuesInbox.empty": "Aún no hay mensajes ni informes de bugs.",
  "owner.issuesInbox.loadError": "No se pudo cargar la bandeja",

  "owner.supportContact.title": "Mensajes de soporte",
  "owner.supportContact.subtitle": "Envíos desde Ayuda o Ajustes (también le llegan por correo si está configurado).",
  "owner.supportContact.when": "Cuándo",
  "owner.supportContact.from": "De",
  "owner.supportContact.source": "Origen",
  "owner.supportContact.page": "Página",
  "owner.supportContact.subject": "Asunto",
  "owner.supportContact.message": "Mensaje",
  "owner.supportContact.attachments": "Fotos",
  "owner.supportContact.status": "Estado",
  "owner.supportContact.notes": "Notas",
  "owner.supportContact.save": "Guardar",
  "owner.supportContact.empty": "Aún no hay mensajes.",
  "owner.supportContact.loadError": "No se pudieron cargar los mensajes",
  "owner.supportContact.saveError": "No se pudo guardar",
  "owner.supportContact.migrationTitle": "Migración necesaria",
  "owner.supportContact.migrationBody":
    "Ejecute 060_platform_support_contact.sql y 061_platform_support_message_attachments.sql en el editor SQL de Supabase.",

  "tour.spotlight.proposals.step1Title": "Lista de cotizaciones",
  "tour.spotlight.proposals.step1Body":
    "Búsqueda y ordenación para encontrar rápido. Los filtros de estado acotan la lista. Use Nueva cotización para empezar otro presupuesto.",
  "tour.spotlight.jobs.step1Title": "Trabajos",
  "tour.spotlight.jobs.step1Body":
    "Seguimiento de obra vinculada a cotizaciones—calendario, totales de pagos en cotizaciones enlazadas y finalización. Use las pestañas Activos, Completados o Archivados.",
  "tour.spotlight.followUps.step1Title": "Seguimiento y análisis",
  "tour.spotlight.followUps.step1Body":
    "Señales de seguimiento prioritarias y valor pendiente con clientes. Los informes completos requieren Pro o Agency—mejore el plan en Facturación si aparece el candado.",
  "tour.spotlight.customers.step1Title": "Clientes",
  "tour.spotlight.customers.step1Body":
    "Busque contactos guardados. Añádalos aquí para vincularlos al armar una cotización.",
  "tour.spotlight.customers.step2Title": "Alta rápida",
  "tour.spotlight.customers.step2Body":
    "Nombre y correo son obligatorios para enviar cotizaciones. El teléfono es opcional pero útil en documentos al cliente.",
  "tour.spotlight.calendar.step1Title": "Calendario de la empresa",
  "tour.spotlight.calendar.step1Body":
    "Vea trabajos y entradas manuales juntos. Abra un día o use el panel para notas internas, entregas o visitas de subcontratas.",
  "tour.spotlight.templates.step1Title": "Plantillas",
  "tour.spotlight.templates.step1Body":
    "Guarde una estructura ganadora como plantilla y abra un borrador nuevo con un toque. Edite o elimine cuando quiera.",
  "tour.spotlight.settings.step1Title": "Pestañas de ajustes",
  "tour.spotlight.settings.step1Body":
    "Use estos enlaces para cambiar de sección: perfil y marca de la empresa, facturación y planes, equipo y acceso crew, y su cuenta personal.",
  "tour.spotlight.settings.step2Title": "Perfil",
  "tour.spotlight.settings.step2Body": "Idioma, datos del negocio, logotipo y valores por defecto en cotizaciones al cliente.",
  "tour.spotlight.settings.step3Title": "Facturación",
  "tour.spotlight.settings.step3Body":
    "Su suscripción a Jorvio y método de pago. Los pagos con Stripe son solo para cobrar con tarjeta en el enlace—puede enviar la cotización por correo o PDF sin conectar cuenta de pagos.",
  "tour.spotlight.settings.step4Title": "Equipo",
  "tour.spotlight.settings.step4Body": "Invitar co-operadores, acceso app crew y visibilidad del equipo en la agenda.",
  "tour.spotlight.settings.step5Title": "Cuenta",
  "tour.spotlight.settings.step5Body": "Contraseña, seguridad y eliminación de cuenta — vinculado a su inicio de sesión.",
  "tour.spotlight.firstQuote.step1Title": "Elija o añada un cliente",
  "tour.spotlight.firstQuote.step1Body":
    "Elija un cliente existente o Nuevo cliente. Para uno nuevo, nombre y correo (teléfono opcional), luego describa el trabajo. Jorvio generará partidas que podrá editar después.",
  "tour.spotlight.firstQuote.step2Title": "Genere el borrador",
  "tour.spotlight.firstQuote.step2Body":
    "Cuando el formulario esté listo, genere la cotización. Irá al editor para revisar precios, fotos y envío—puede usar correo o PDF sin configurar pagos con tarjeta.",
  "tour.spotlight.firstQuoteChooser.step1Title": "Elija cómo cotizar",
  "tour.spotlight.firstQuoteChooser.step1Body":
    "Cree con IA, empiece en blanco, use una cotización rápida en obra o abra una plantilla — elija lo que encaje con este trabajo.",

  "tour.app.next": "Siguiente",
  "tour.app.done": "Listo",
  "tour.app.progress": "Paso {{current}} de {{total}}",
  "tour.app.step0Title": "Bienvenido",
  "tour.app.step0Body":
    "Un recorrido completo por cada área de la barra lateral, de arriba abajo: dónde viven las cotizaciones, cómo se conectan trabajos y calendario, y dónde están ajustes y ayuda. Use Siguiente paso a paso o cierre cuando quiera.",
  "tour.app.intro.welcomeTitle": "Bienvenido",
  "tour.app.intro.welcomeBody":
    "Un vistazo rápido a las pestañas principales: Panel, Cotizaciones, Trabajos, Calendario y Seguimiento. Use Siguiente para continuar o cierre para omitir.",
  "tour.app.intro.jobsBody":
    "Cuando un cliente acepta una cotización, pasa a ser un trabajo aquí. Siga el trabajo activo, el calendario y los pagos en un solo lugar.",
  "tour.app.intro.calendarBody": "Trabajo programado y entradas manuales del calendario juntos.",
  "tour.app.intro.followUpsBody": "Señales de cotizaciones abiertas y seguimiento prioritario.",
  "tour.app.navDashboardTitle": "Panel",
  "tour.app.navDashboardBody": "Su base: actividad, cotizaciones abiertas y lo que necesita atención hoy.",
  "tour.app.full.navDashboardBody":
    "Su base al iniciar sesión. Vea actividad reciente, cotizaciones que aún esperan respuesta del cliente y conteos rápidos de lo que necesita atención hoy. Muchos dueños empiezan aquí cada mañana antes de abrir Cotizaciones o Seguimiento.",
  "tour.app.navQuotesTitle": "Cotizaciones",
  "tour.app.navQuotesBody": "Cada presupuesto creado, enviado o archivado. Ábralo para editar, enviar o dar seguimiento.",
  "tour.app.full.navQuotesBody":
    "Cada presupuesto creado, enviado, visto o archivado. Filtre por estado, ábralo para editar partidas o reenviar tras cambios, o convierta una cotización aceptada en trabajo. Aquí está todo su historial en una lista.",
  "tour.app.step1Title": "Nueva cotización",
  "tour.app.step1Body":
    "Inicie un presupuesto nuevo desde aquí. Elija asistencia con IA, cotización en blanco, flujo rápido en obra o una plantilla guardada. Todo lo que cree queda en Cotizaciones hasta que lo envíe o el cliente acepte.",
  "tour.app.step2Title": "Clientes",
  "tour.app.step2Body":
    "Su directorio de clientes: nombres, correos, teléfonos y direcciones ligados a cotizaciones anteriores. Elija un contacto al armar una cotización para que estimados y mensajes queden con la persona correcta.",
  "tour.app.step3Title": "Trabajos",
  "tour.app.step3Body":
    "Cuando un cliente acepta en el portal o usted marca una cotización como aceptada, pasa a ser un trabajo aquí. Siga trabajo activo, estado, fechas en el calendario y pagos sin buscar en PDFs viejos.",
  "tour.app.step4Title": "Calendario de la empresa",
  "tour.app.step4Body":
    "Agenda de toda la empresa: trabajos aceptados con fechas, asignados y sitios de obra, más entradas manuales que usted agregue. Úselo para ver la semana y evitar doble reserva de cuadrillas.",
  "tour.app.step5Title": "Seguimiento",
  "tour.app.step5Body":
    "Cotizaciones enviadas sin respuesta, ordenadas para que las más antiguas salgan primero. Úselo cuando quiera una lista de seguimiento sin recorrer toda la tabla de Cotizaciones.",
  "tour.app.step6Title": "Mensajes",
  "tour.app.step6Body":
    "Conversaciones del portal de propietarios y mensajes dentro de cotizaciones en un solo buzón. Active alertas por correo en Ajustes si quiere aviso cuando alguien responda en una cotización enviada.",
  "tour.app.step7Title": "Ajustes",
  "tour.app.step7Body":
    "Perfil de empresa, logo y colores en PDFs, facturación y límites del plan, pagos opcionales con tarjeta en el enlace y invitaciones al equipo. Los pagos son opcionales: puede enviar cotizaciones por correo o PDF sin ellos.",
  "tour.app.navTemplatesTitle": "Plantillas",
  "tour.app.navTemplatesBody":
    "Guarde partidas, secciones y textos que repite en trabajos similares. Inicie cotizaciones nuevas desde una plantilla en lugar de reescribir el mismo paquete interior o alcance de gabinetes cada vez.",
  "tour.app.navHelpTitle": "Ayuda",
  "tour.app.navHelpBody":
    "Guías y preguntas frecuentes cuando se atasque con una función. También puede volver a abrir este recorrido completo desde el menú del signo de interrogación al final de la barra lateral.",

  "tour.setup.fabLabel": "Completar configuración",
  "tour.setup.title": "Lista de configuración",
  "tour.setup.openSettings": "Abrir ajustes",
  "tour.setup.dismiss": "Ocultar recordatorio",
  "tour.setup.restartTour": "Tutorial",
  "tour.setup.required": "Recomendado primero",
  "tour.setup.optional": "Pulido recomendado",
  "tour.setup.coopHint":
    "Las cotizaciones y el calendario usan el espacio del equipo. Complete su perfil personal para que lo reconozcan.",
  "tour.setup.item.companyName": "Añada el nombre de su empresa",
  "tour.setup.item.contactEmail": "Añada un correo de contacto en cotizaciones (o use el de inicio de sesión)",
  "tour.setup.item.quotePhone": "Añada el teléfono que aparece en las cotizaciones al cliente",
  "tour.setup.item.address": "Complete la dirección (calle, ciudad, estado, CP)",
  "tour.setup.item.logo": "Suba un logotipo para las cotizaciones al cliente",
  "tour.setup.item.stripePayout":
    "Opcional: conecte Stripe para cobrar con tarjeta en el enlace al cliente. El correo y el PDF funcionan sin eso.",
  "tour.setup.item.coopProfile": "Añada su nombre para mostrar",
  "tour.setup.item.coopPhone": "Añada un teléfono personal (opcional)",

  "tour.quoteEditor.step1Title": "Partidas y precios",
  "tour.quoteEditor.step1Body":
    "Use Siguiente—no hace falta añadir partidas aún. Esta tabla es lo que el cliente ve en el enlace, PDF y vista pública. Añada filas en una cotización real.",
  "tour.quoteEditor.step2Title": "Asistente y revisión IA",
  "tour.quoteEditor.step2Body":
    "Pídale al asistente cambios de redacción o precio—aplique lo que encaje y el borrador se guarda solo. Pulse el botón de la estrella para una revisión IA de precios, alcance, condiciones y posibles objeciones del cliente. Ambos están incluidos en todos los planes (algunas acciones de IA al día en Starter y Solo; ilimitadas en Pro y Agency).",
  "tour.quoteEditor.step3Title": "Fotos y marcas en plano",
  "tour.quoteEditor.step3Body": "Suba fotos y marque planos para que equipo y cliente compartan el mismo contexto visual.",
  "tour.quoteEditor.step4Title": "Comisión de tarjeta al cliente",
  "tour.quoteEditor.step4Body":
    "Cuando esté disponible en su región, puede añadir una línea estimada de comisión por tarjeta al total. Usted cumple las normas de recargos locales.",
  "tour.quoteEditor.step5Title": "Enviar al cliente",
  "tour.quoteEditor.step5Body":
    "Envíe por correo, comparta el enlace o descargue PDF—no hace falta cuenta de pagos para eso. Conecte Stripe en Ajustes solo si quiere que paguen con tarjeta en la página pública. Vista previa y PDF usan la última guardada.",
  "publicQuote.onlinePaymentsUnavailableTitle": "El pago con tarjeta en línea aún no está disponible en este enlace",
  "publicQuote.onlinePaymentsUnavailableBody":
    "Aún no hay cobro con tarjeta. Puede leer el presupuesto y pagar de otra forma—coordine con su contratista. Pueden activar tarjetas en su cuenta.",
  "publicQuote.acceptWithoutPayHeading": "Aceptar esta cotización",
  "publicQuote.acceptWithoutPayIntro":
    "Acepte los términos siguientes para aprobar este presupuesto.",
  "publicQuote.acceptOnlySignatureTitle": "Su firma",
  "publicQuote.acceptOnlySignatureIntro": "Dibuje su firma y pulse «Usar firma» antes de aceptar.",
  "publicQuote.acceptOnlyNoSignatureNote": "No se requiere firma dibujada en esta cotización.",
  "publicQuote.acceptWithoutPayButton": "Aceptar cotización",
  "publicQuote.acceptOnlySubmitting": "Enviando…",
  "publicQuote.acceptOnlyFailedTitle": "No se pudo aceptar",
  "publicQuote.acceptOnlySuccessTitle": "Cotización aceptada",
  "publicQuote.acceptOnlySuccessBody": "Gracias — se ha notificado al contratista.",
};

/** Français — même intention qu’EN ; formulation naturelle, pas calque mot à mot. */
export const EXTENDED_FR: Record<string, string> = {
  "common.tryAgain": "Réessayez",
  "common.requestFailed": "Échec de la requête",
  "common.sending": "Envoi…",
  "common.signingIn": "Connexion…",
  "common.redirecting": "Redirection…",
  "common.loadingEllipsis": "Chargement…",
  "common.account": "Compte",
  "common.or": "Ou",
  "common.add": "Ajouter",
  "common.name": "Nom",
  "common.email": "E-mail",
  "common.password": "Mot de passe",
  "common.phone": "Téléphone",
  "common.save": "Enregistrer",
  "common.delete": "Supprimer",
  "common.cancel": "Annuler",
  "common.close": "Fermer",
  "common.back": "Retour",
  "common.next": "Suivant",
  "common.optionalSignIn": "(connexion facultative)",

  "auth.signIn": "Se connecter",
  "auth.signInSubtitle": "",
  "auth.signInTitleFallback": "Se connecter",
  "auth.tabPassword": "Mot de passe",
  "auth.tabMagic": "Lien magique",
  "auth.forgotPassword": "Mot de passe oublié ?",
  "auth.signInButton": "Se connecter",
  "auth.emailMagicLink": "M’envoyer un lien magique",
  "auth.continueGoogle": "Continuer avec Google",
  "auth.newHere": "Nouveau ?",
  "auth.createAccount": "Créer un compte",
  "auth.moreOptions": "Autres options",
  "auth.crewMember": "Code d’accès calendrier équipe ?",
  "auth.enterTeamCode": "Ouvrir le calendrier équipe",
  "auth.crewInviteLead": "Membre d’équipe terrain ?",
  "auth.crewInviteCta": "Code, puis connexion",
  "auth.inviteCodeLead": "Code d’invitation co-opérateur ?",
  "auth.joinWithCode": "Rejoindre l’espace",
  "auth.homeownerPortal": "Client avec lien de devis ?",
  "auth.customerPortal": "Portail client",
  "auth.checkEmail": "Vérifiez votre e-mail",
  "auth.checkEmailCodeHint":
    "Sur un autre appareil ? Ignorez le bouton dans le mail et saisissez le code à {{n}} chiffres ci-dessous (chiffres uniquement).",
  "auth.magicEnterCodeTitle": "Saisissez le code",
  "auth.magicEnterCodeLeadPrefix": "Un code de vérification a été envoyé à",
  "auth.magicEnterCodeLeadSuffix":
    "Saisissez les {{n}} chiffres dans le champ (chiffres uniquement). À utiliser si le lien ne s’ouvre pas — autre appareil ou navigateur intégré au mail.",
  "auth.magicCodePlaceholder": "Code dans l’e-mail",
  "auth.magicCodeFieldHint": "{{n}} chiffres, uniquement des nombres",
  "auth.magicVerifySubmit": "Vérifier et me connecter",
  "auth.magicResend": "Recevoir un nouveau code",
  "auth.magicBack": "Utiliser une autre adresse",
  "auth.magicInvalidCode": "Saisissez les {{n}} chiffres du mail (chiffres uniquement).",
  "auth.deviceOtpLeadStart": "Saisissez le code de vérification à {{n}} chiffres envoyé à",
  "auth.deviceOtpToastDesc": "Nous avons envoyé un code à {{n}} chiffres pour vérifier cet appareil.",
  "auth.deviceOtpEnterFull": "Saisissez les {{n}} chiffres (chiffres uniquement).",
  "auth.idleSessionTitle": "Session fermée pour inactivité",
  "auth.idleSessionBody": "Pour votre sécurité, reconnectez-vous.",
  "auth.signInFailed": "Échec de la connexion",
  "auth.googleSignInFailed":
    "La connexion Google n’a pas abouti. Réessayez ou utilisez l’e-mail.",
  "auth.callbackErrorTitle": "Impossible de finaliser ce lien de connexion.",
  "auth.callbackErrorBody":
    "Réessayez avec Lien magique puis saisissez le code reçu par e-mail. Si vous venez d’un aperçu dans l’app e-mail, ouvrez {{origin}}/login dans Safari ou Chrome puis connectez-vous avec code ou mot de passe.",
  "auth.validEmail": "Entrez un e-mail valide",
  "auth.enterPassword": "Entrez votre mot de passe",
  "auth.placeholderEmail": "vous@entreprise.com",
  "auth.forgotTitle": "Réinitialiser le mot de passe",
  "auth.forgotSubtitle": "Nous enverrons le lien de réinitialisation à l’adresse saisie ci-dessous.",
  "auth.forgotSend": "Envoyer l’e-mail",
  "auth.updatePasswordTitle": "Nouveau mot de passe",
  "auth.updatePasswordSubtitle": "Choisissez un mot de passe solide.",
  "auth.passwordUpdated": "Mot de passe mis à jour",
  "auth.passwordUpdatedDesc": "Vous êtes connecté. Redirection…",
  "auth.newPassword": "Nouveau mot de passe",
  "auth.confirmPassword": "Confirmer le mot de passe",
  "auth.forgotCardDescription":
    "Nous vous enverrons un lien pour définir un nouveau mot de passe.",
  "auth.forgotSentDescription":
    "Vérifiez votre boîte et les spams. Ouvrez le lien dans Safari, Chrome ou Edge.",
  "auth.forgotAfterText":
    "Si cette adresse est dans notre système, vous recevrez un lien sous peu. Après réinitialisation, connectez-vous depuis la page de connexion.",
  "auth.backToSignIn": "Retour à la connexion",
  "auth.updatePasswordCardDescription": "Choisissez un nouveau mot de passe pour votre compte.",

  "signup.passwordTab": "Mot de passe",
  "signup.magicTab": "Lien magique",
  "signup.createButton": "Créer le compte",
  "signup.fullName": "Nom complet",
  "signup.companyName": "Entreprise",
  "signup.addressLine": "Adresse (facultatif)",
  "signup.city": "Ville",
  "signup.state": "État / région",
  "signup.zip": "Code postal",
  "signup.passwordMin": "Au moins 8 caractères",
  "signup.passwordsMustMatch": "Les mots de passe doivent correspondre",
  "signup.nameRequired": "Le nom est requis",
  "signup.companyRequired": "Le nom de l’entreprise est requis",
  "signup.selectTrade": "Choisissez votre métier",
  "signup.tradeOtherHint": "Indiquez votre activité ou spécialité",
  "signup.hasAccount": "Déjà un compte ?",
  "signup.signInLink": "Se connecter",

  "topbar.settings": "Paramètres",
  "topbar.signOut": "Déconnexion",
  "topbar.accountMenu": "Menu du compte",
  "topbar.openHelp": "Centre d’aide",
  "topbar.takeTour": "Faire la visite",
  "topbar.fullAppTour": "Visite complète",

  "customers.title": "Clients",
  "customers.subtitle": "Une fiche contact par personne à laquelle vous facturez.",
  "customers.searchPlaceholder": "Rechercher par nom, e-mail ou téléphone…",
  "customers.searchEmpty": "Aucun client ne correspond à \u00AB\u202F{{query}}\u202F\u00BB.",
  "customers.searchEmptyHint": "Essayez un autre nom, e-mail ou numéro de téléphone.",
  "customers.searchClear": "Effacer la recherche",
  "customers.emptyDesc": "Ajoutez votre premier client pour commencer à établir des devis.",
  "customers.searchAria": "Rechercher des clients",
  "customers.addFailed": "Impossible d’ajouter le client",
  "customers.emailRequired": "Saisissez une adresse e-mail pour ce client.",
  "customers.emailLabelRequired": "E-mail (obligatoire)",
  "customers.phoneLabelOptional": "Téléphone (facultatif)",
  "customers.added": "Client ajouté",
  "customers.phonePlaceholder": "(555) 123-4567",
  "customers.emailPlaceholder": "marie@email.com",
  "customers.namePlaceholder": "Marie Dupont",
  "customers.deleteFailed": "Impossible de supprimer le client",
  "customers.removed": "Client supprimé",
  "customers.actionsAria": "Actions client",
  "customers.saveFailed": "Impossible d’enregistrer",
  "customers.saved": "Enregistré",
  "customers.deleteCustomerFailed": "Impossible de supprimer",
  "customers.detailTitle": "Client",
  "customers.backToList": "Retour aux clients",
  "customers.quoteCountNone": "Pas de devis",
  "customers.quoteCountOne": "1 devis",
  "customers.quoteCountMany": "{{count}} devis",
  "customers.viewDetails": "Voir les détails",
  "customers.delete": "Supprimer",
  "customers.noContactOnFile": "Aucun contact enregistré",
  "customers.deleteConfirmTitle": "Supprimer le client ?",
  "customers.deleteConfirmDesc":
    "Retirer {{name}} de la liste. Les devis liés conservent l’historique ; seul l’enregistrement client est supprimé.",
  "customers.deleting": "Suppression…",
  "customers.addCustomerShow": "Ajouter un client",
  "customers.addCustomerHide": "Masquer le formulaire",

  "templates.title": "Modèles",
  "templates.subtitle": "Devis réutilisables sans détails client",
  "templates.createFailed": "Impossible de créer le devis",
  "templates.confirmDeleteMsg": "Supprimer ce modèle ? Les devis existants ne sont pas affectés.",
  "templates.emptyDescription":
    "Ouvrez un devis → Enregistrer comme modèle dans l’éditeur pour garder une structure réutilisable sans détails client.",
  "templates.createAQuote": "Créer un devis",
  "templates.startersSectionTitle": "Modèles de démarrage",
  "templates.startersIntro": "Ajoutez-le à la liste, puis ouvrez-le pour modifier les lignes et le périmètre.",
  "templates.starterHvac": "Modèle HVAC",
  "templates.starterPlumbing": "Modèle plomberie",
  "templates.starterElectrical": "Modèle électricité",
  "templates.starterTrade": "Modèle {{trade}}",
  "templates.starterAdding": "Ajout…",
  "templates.empty": "Pas encore de modèles",
  "templates.deleteFailed": "Impossible de supprimer",
  "templates.deleted": "Modèle supprimé",
  "templates.notFound": "Modèle introuvable",
  "templates.saveFailed": "Échec de l’enregistrement",
  "templates.saved": "Modèle enregistré",
  "templates.alreadyHaveThisStarter": "Vous avez déjà ce modèle de départ",
  "templates.alreadyHaveThisStarterBody":
    "Ouvrez-le dans la liste pour le modifier, ou renommez votre copie existante avant d'en ajouter un autre.",
  "templates.lineItemsTypicalTotal": "{{count}} lignes · Total type : {{total}}",
  "templates.cardHint":
    "Ouvrez le titre pour modifier, créer un brouillon depuis ce modèle ou le supprimer.",
  "templates.useForNewQuote": "Utiliser pour un nouveau devis",
  "templates.creating": "Création…",
  "templates.edit": "Modifier",
  "templates.deleting": "Suppression…",
  "templates.createBlank": "Créer un modèle",
  "templates.createBlankWorking": "Création…",
  "templates.addAnotherHint": "Créez une autre structure réutilisable pour d’autres types de chantiers.",
  "templates.premadeSectionTitle": "Ajouter un autre modèle",
  "templates.premadeSectionDescription":
    "Les mêmes bases qu’au départ. Choisissez un métier ; vous pouvez tout modifier ensuite.",
  "auth.savePassword": "Enregistrer le mot de passe",

  "templates.editBack": "← Retour aux modèles",
  "templates.editDeleteButton": "Supprimer le modèle",
  "templates.editHeading": "Modifier le modèle",
  "templates.editQuoteTitleLabel": "Titre du devis pour les nouveaux brouillons",
  "templates.editQuoteTitlePlaceholder": "ex. Rénovation de cuisine — intérieur",
  "templates.editSummaryPlaceholder": "Résumé court que le client voit en haut du devis.",
  "templates.editScopePlaceholder": "Décrivez les travaux, les matériaux et tout ce que le client doit savoir.",
  "templates.editSummaryScopeCard": "Résumé et périmètre",
  "templates.editSummaryLabel": "Résumé",
  "templates.editScopeLabel": "Périmètre des travaux",
  "templates.editSummaryHint":
    "Les nouveaux devis à partir de ce modèle commencent avec ce résumé et ce périmètre.",
  "templates.editLineItemsCard": "Lignes",
  "templates.editTermsCard": "Conditions et notes",
  "templates.editTaxRate": "Taux de taxe",
  "templates.editDiscount": "Remise ($)",
  "templates.editValidUntil": "Valable jusqu’au",
  "templates.editDeposit": "Acompte / premier paiement (%)",
  "templates.editPaymentTerms": "Conditions de paiement",
  "templates.editWarranty": "Garantie",
  "templates.editNotes": "Notes",
  "templates.saveChanges": "Enregistrer les modifications",

  "quotePaymentTerms.fullWithTotal": "Payez {{total}} en un seul paiement sécurisé ci-dessous.",
  "quotePaymentTerms.fullGeneric":
    "Le total du devis est payable en un seul paiement sécurisé lors du règlement ci-dessous.",
  "quotePaymentTerms.splitLine1WithAmounts":
    "{{pct}}% du total du devis ({{total}}) est dû maintenant — {{deposit}}. Ce paiement ne porte que sur ces {{pct}}%, pas sur le total de {{total}}.",
  "quotePaymentTerms.splitLine2WithAmounts":
    "Les {{rest}}% restants ({{balance}}) seront payés plus tard en un second paiement (par exemple à la fin des travaux). Le client utilisera cette même page de devis pour le solde lorsque vous serez prêt.",
  "quotePaymentTerms.splitLine1Generic":
    "{{pct}}% du total du devis est dû maintenant. Ce paiement ne porte que sur ces {{pct}}%, pas sur le montant total.",
  "quotePaymentTerms.splitLine2Generic":
    "Les {{rest}}% restants seront payés plus tard en un second paiement (par exemple à la fin des travaux). Le client reviendra sur cette même page pour le solde lorsque vous serez prêt.",

  "decision.waitCostPara1":
    "Attendre pour approuver peut décaler le prochain créneau — un autre chantier peut prendre la place.",
  "decision.waitCostPara2WithTotal":
    "Ce total ({{total}}) correspond à ce périmètre. Si le périmètre ou le marché changent avant acceptation, la prochaine version peut varier.",
  "decision.waitCostPara2NoTotal":
    "Si le périmètre ou les prix matériaux changent avant acceptation, la prochaine version peut afficher d’autres montants.",
  "decision.waitCostExpiry":
    "Si ce devis a une date d’expiration, attendre trop longtemps peut nécessiter une proposition mise à jour.",

  "calendarPage.title": "Calendrier",
  "calendarPage.subtitle":
    "Devis provisoires et chantiers confirmés dans un seul calendrier.",

  "calendar.previous": "Précédent",
  "calendar.today": "Aujourd’hui",
  "calendar.addEntry": "Ajouter une entrée",
  "calendar.next": "Suivant",
  "calendar.loadingEvents": "Chargement des événements…",
  "calendar.noJobsDay": "Aucun chantier prévu ce jour.",
  "calendar.dayPeekEmpty": "Rien de prévu ce jour.",
  "calendar.dayTitleFallback": "Jour",
  "calendar.eventFallback": "Événement",
  "calendar.moreCount": "+{{count}} de plus",
  "calendar.peekSrOnly": "Voir les chantiers et l’équipe pour ce jour",
  "calendar.legendTentative": "Chantier provisoire",
  "calendar.legendConfirmed": "Chantier confirmé",
  "calendar.legendMaterial": "Livraison matériaux",
  "calendar.legendSub": "Sous-traitant",
  "calendar.legendCancelled": "Annulé",
  "calendar.teamTitle": "Équipe et affectations",
  "calendar.teamDesc":
    "Créez un code équipe dans les paramètres, puis utilisez ces noms pour les devis ou les entrées du calendrier.",
  "calendar.teamSettings": "Code équipe et paramètres",
  "calendar.teamQuotes": "Ouvrir les devis pour affecter l’équipe",
  "calendar.rosterHint": "Déjà connectés (orthographe exacte)",
  "calendar.rosterEmpty": "Personne n’a encore utilisé votre code équipe — partagez-le depuis les paramètres.",
  "calendar.openQuote": "Ouvrir le devis",
  "calendar.editEntry": "Modifier",
  "calendar.dateRangeTo": "à",
  "calendar.dateRangeArrow": "→",
  "calendar.linkedQuoteStatusHint": "L’état des chantiers liés au devis suit le devis et le paiement.",
  "calendar.notesLabel": "Notes (facultatif)",
  "calendar.notesPlaceholder": "Ce que l’équipe doit retenir",
  "calendar.statusLabel": "Statut",
  "calendar.statusTentative": "Provisoire",
  "calendar.statusConfirmed": "Confirmé",
  "calendar.statusCancelled": "Annulé",
  "calendar.titleLabel": "Titre",
  "calendar.startDate": "Date de début",
  "calendar.endDate": "Date de fin",
  "calendar.startTime": "Heure de début",
  "calendar.endTime": "Heure de fin",
  "calendar.toastTitleRequired": "Ajoutez un titre",
  "calendar.toastPickStart": "Choisissez une date de début",
  "calendar.toastEndAfterStart": "La date de fin doit être le même jour ou après le début",
  "calendar.toastInvalidStart": "Choisissez une date et heure de début valides",
  "calendar.toastInvalidEndTime": "Choisissez une heure de fin valide",
  "calendar.toastInvalidEndDate": "Choisissez une date de fin valide",
  "calendar.materialBadge": "Matériel",
  "calendar.subBadge": "Sous-traitant",
  "calendar.crewPrefix": "Équipe :",
  "calendar.crewNoneQuote": "Personne n’est encore affecté sur le devis.",
  "calendar.crewNotOnQuote": "non affecté sur le devis",
  "calendar.officeOnly": "Calendrier bureau uniquement — pas visible sur l’app équipe.",
  "calendar.materialFromLine": "Livraison matériaux (ligne du devis)",
  "calendar.subFromCost": "Sous-traitant (ligne de coût)",
  "calendar.customerPrefix": "Client :",
  "calendar.savingEntry": "Enregistrement…",
  "calendar.saveEntry": "Enregistrer",
  "calendar.rawStatusTentative": "provisoire",
  "calendar.rawStatusConfirmed": "confirmé",
  "calendar.rawStatusCancelled": "annulé",
  "calendar.dialogMaterialTitle": "Livraison matériaux (depuis le devis)",
  "calendar.dialogSubTitle": "Sous-traitant (depuis le devis)",
  "calendar.dialogEditQuoteSchedule": "Modifier le planning (chantier du devis)",
  "calendar.dialogEditEntry": "Modifier l’entrée du calendrier",
  "calendar.dialogNewEntry": "Nouvelle entrée du calendrier",
  "calendar.hintMaterialSync":
    "Cette ligne vient de la date « Prêt pour » d’une ligne matériau du devis. Modifier ici ne met à jour que le calendrier — modifiez la ligne sur le devis pour un changement définitif.",
  "calendar.hintSubSync":
    "Synchronisée avec une ligne de coût avec sous-traitant lié. Les horaires dépendent du début du chantier sur le devis ; mettez à jour le planning ou les lignes de coût pour rafraîchir.",
  "calendar.hintQuoteJob":
    "Les dates et heures mettent à jour le début et la fin planifiés du devis. Vous pouvez définir pour qui le travail apparaît sur le calendrier équipe ci-dessous. Titre, paiements et notes se modifient sur le devis.",
  "calendar.titlePlaceholder": "ex. Visite de chantier, Permis",
  "calendar.assigneeLabel": "Équipe / affecter à (facultatif)",
  "calendar.assigneeHintQuote":
    "Noms séparés par des virgules (ex. Mike, Sarah). Même orthographe qu’avec le code équipe.",
  "calendar.assigneeHintManual":
    "Visible sur le calendrier du membre si le nom correspond à la connexion avec le code équipe. Virgules entre les noms.",
  "calendar.assigneeHintAux":
    "Qui voit le chantier sur les calendriers équipe vient du champ « Affecter à » du devis — modifiez le devis pour changer. Visites sous-traitant : calendrier bureau seulement.",
  "calendar.endTimeOptional": "Heure de fin (facultatif)",
  "calendar.durationHint":
    "Même jour sans heure de fin = 1 h. Plusieurs jours = affiché chaque jour ; dernier jour à 17 h si pas d’heure de fin.",
  "calendar.toastEntryAdded": "Entrée ajoutée",
  "calendar.deleteConfirmJob":
    "Retirer ce chantier du calendrier et effacer début/fin planifiés sur le devis ?",
  "calendar.deleteConfirmEntry": "Supprimer cette entrée du calendrier ?",
  "calendar.toastDeleteFailed": "Impossible de supprimer",
  "calendar.toastEntryRemoved": "Entrée supprimée",
  "calendar.toastCouldNotUpdate": "Impossible de mettre à jour l’entrée",
  "calendar.toastCouldNotAdd": "Impossible d’ajouter l’entrée",

  "billing.title": "Facturation",
  "billing.subtitle":
    "Votre forfait, devis envoyés ce mois-ci (e-mail, PDF ou lien), moyen de paiement et statut d’abonnement.",
  "billing.introOfferTitle": "Essai gratuit de 7 jours sur chaque forfait",
  "billing.introOfferBody":
    "Starter, Solo, Pro et Agency commencent tous par un essai gratuit de 7 jours — sans carte. Après le 7ᵉ jour, ajoutez une carte et choisissez un forfait pour continuer à créer des devis.",
  "billing.startPlanFirst":
    "Choisissez un forfait pour créer des devis. Vous pouvez parcourir le reste de l’app.",
  "billing.ownerBypass": "Compte propriétaire : contournement des contrôles de facturation pour tests.",
  "billing.checkoutSuccess":
    "Abonnement mis à jour. Utilisez Gérer l’abonnement ci-dessous pour changer votre carte, voir les factures ou annuler à tout moment.",
  "billing.checkoutCanceled": "Paiement annulé. Votre forfait actuel n’a pas changé.",
  "billing.manageSubscription": "Gérer l’abonnement",
  "billing.currentPlan": "Forfait actuel",
  "billing.choosePlan": "Choisir un forfait",

  "logo.useImageFile": "Utilisez un fichier image",
  "logo.pngJpgWebp": "PNG, JPG ou WebP.",
  "logo.fileTooLarge": "Fichier trop volumineux",
  "logo.max2mb": "2 Mo maximum.",
  "logo.updated": "Logo mis à jour",

  "diagram.useImage": "Utilisez un fichier image",
  "diagram.max12": "12 Mo max.",
  "diagram.max4": "4 images de plan maximum",
  "diagram.removeOne": "Retirez-en une pour en ajouter une autre.",
  "diagram.added": "Schéma ajouté",
  "diagram.readFailed": "Impossible de lire l’image",

  "markup.useImage": "Utilisez un fichier image",
  "markup.previewFailed": "Impossible de charger l’aperçu",
  "markup.dimensionsFailed": "Impossible de lire les dimensions",
  "markup.photoAdded": "Photo ajoutée",

  "proposalForm.loadCustomersFailed": "Impossible de charger les clients",
  "proposalForm.networkError": "Erreur réseau.",
  "proposalForm.unsupportedImage": "Image non prise en charge",
  "proposalForm.useJpgPngWebp": "Utilisez JPG, PNG ou WebP.",
  "proposalForm.imageTooLarge": "Image trop lourde",
  "proposalForm.under6mb": "Photo de moins de 6 Mo.",
  "proposalForm.transcribeFailed": "Impossible de transcrire les notes",
  "proposalForm.noTextFound": "Aucun texte trouvé",
  "proposalForm.clearerPhoto": "Essayez une photo plus nette de vos notes.",
  "proposalForm.notesAdded": "Notes ajoutées à la description du chantier",
  "proposalForm.readPhotoFailed": "Impossible de lire la photo",
  "proposalForm.generated": "Devis généré",
  "proposalForm.choosePlaceholder": "Choisir…",
  "proposalForm.customerNamePh": "Jean Martin",
  "proposalForm.customerEmailPh": "jean@email.com",
  "proposalForm.taxUseProfile": "Profil / défaut IA",
  "proposalForm.uploadNotesAria": "Téléverser une photo de notes manuscrites",
  "proposalForm.jobDescriptionPlaceholder":
    "ex. Remplacer chauffe-eau 150 L, cuivre/multicouche, permis. Main-d’œuvre (h × taux ou forfait), sous-traitants, accès, normes. Prix des pièces dans le tableau ci-dessous.",

  "assistant.placeholder": "Que faut-il changer sur ce devis ?",

  "quoteHelper.title": "Assistant devis",
  "quoteHelper.errorTitle": "Erreur de l’assistant devis",
  "quoteHelper.hint":
    "Peut ajuster lignes, périmètre, totaux, conditions, notes et dates. Vérifiez avant d’envoyer au client.",
  "quoteHelper.opening":
    "Ex. : modifier une ligne, une date ou fixer l’acompte à 25 %.",
  "quoteHelper.placeholder": "Que mettre à jour ?",
  "quoteHelper.apply": "Appliquer",
  "quoteHelper.hide": "Masquer l’assistant",
  "quoteHelper.show": "Afficher l’assistant devis",
  "quoteHelper.undoLast": "Annuler la dernière modification",
  "quoteHelper.undoOk": "Les champs précédents du devis ont été restaurés.",
  "quoteHelper.moreActionsArchiveNote":
    "Les devis restent dans l’historique. Pour supprimer définitivement, supprimez-le depuis Devis archivés dans Réglages.",
  "quoteHelper.upgradeBlurb":
    "La rédaction de devis par IA et l’assistant devis (modifications sur un devis existant) sont inclus dans tous les forfaits — Starter et Solo bénéficient de quelques actions IA par jour, Pro et Agency sont illimités. Choisissez un forfait pour de l’aide à rédiger à partir d’une phrase et à ajuster le périmètre, les lignes, le planning, les conditions et les prix.",
  "quoteHelper.liveQuoteBanner":
    "Ce devis a déjà été partagé avec le client. Renvoyez-le quand vous voulez qu’il reçoive un e-mail mis à jour.",
  "quoteHelper.liveQuoteCalloutTitle": "Copie client de ce devis",
  "quoteHelper.thinking": "Traitement…",

  "dashboard.team.crewCodeModalTitle": "Code du calendrier équipe",
  "dashboard.team.crewCodeModalHint":
    "L’équipe le saisit sur /crew pour ouvrir le calendrier de l’entreprise. Regénérez-le ou retirez l’accès sous Réglages → Équipe.",
  "dashboard.team.notConfiguredYet": "Aucun code pour le moment. Créez-en un sous Réglages → Équipe.",
  "dashboard.team.openTeamSettings": "Ouvrir Réglages équipe",

  "lineItem.categoryTitle": "Catégorie sur le devis client (main-d’œuvre, matériaux, sous-traitance, frais).",
  "lineItem.leadTimeTitle": "Délai en jours (planification interne uniquement ; caché au client).",

  "progress.generatingQuote": "Génération du devis",

  "brand.homeAria": "Accueil Jorvio Quotes",
  "brand.nameAria": "Jorvio Quotes",

  "archived.title": "Devis archivés",
  "archived.deleteFailed": "Impossible de supprimer le devis",
  "archived.deleted": "Devis supprimé",
  "archived.allDeleted": "Tous les devis archivés supprimés",
  "archived.emptyTitle": "Aucun devis archivé",
  "archived.deleteTitle": "Supprimer ce devis archivé",

  "followUp.agencyRequired": "Agency requis",
  "followUp.agencyOnly": "Relances personnalisées sur le forfait Agency.",
  "followUp.checkSteps": "Vérifiez les étapes",
  "followUp.stepInvalid": "Chaque étape doit avoir un délai et un message valides.",
  "followUp.saveFailed": "Échec de l’enregistrement",
  "followUp.saved": "Automatisation enregistrée",

  "portal.loadFailed": "Impossible de charger les devis",
  "portal.enterEmail": "Entrez votre e-mail",
  "portal.sendCodeFailed": "Impossible d’envoyer le code",
  "portal.codeSent": "Code envoyé",
  "portal.checkEmail": "Vérifiez votre e-mail.",
  "portal.enterEmailAndCode": "Entrez l’e-mail et le code",
  "portal.signInFailed": "Échec de la connexion",
  "portal.invalidCode": "Code invalide",
  "portal.signedIn": "Connecté",
  "portal.signedInDesc": "Vous voyez les devis envoyés à cet e-mail.",
  "portal.signedOut": "Déconnecté",

  "publicQuote.paymentReceived": "Paiement reçu",
  "publicQuote.paymentThanks": "Merci. Votre paiement a bien été traité.",
  "publicQuote.paymentCanceled": "Paiement annulé",
  "publicQuote.noCharge": "Aucun débit effectué.",
  "publicQuote.paymentConfirmingTitle": "Confirmation de votre paiement…",
  "publicQuote.paymentConfirmingBody":
    "Votre carte a été débitée. Nous attendons la confirmation finale de Stripe — cela prend généralement quelques secondes. Veuillez ne pas essayer de payer à nouveau.",
  "publicQuote.paymentConfirmingTakingLongerTitle": "Confirmation toujours en cours",
  "publicQuote.paymentConfirmingTakingLongerBody":
    "Stripe prend un peu plus de temps que d'habitude. Votre carte n'a été débitée qu'une seule fois. Actualisez cette page dans une minute, ou contactez votre prestataire si rien ne change.",
  "publicQuote.couldNotSend": "Impossible d’envoyer",
  "publicQuote.requestSent": "Demande envoyée",
  "publicQuote.contractorFollowUp": "L’entrepreneur vous recontactera.",
  "publicQuote.couldNotAnswer": "Impossible de répondre pour le moment",
  "publicQuote.acceptAgreements": "Veuillez accepter les conditions de la plateforme et les conditions de paiement Stripe ci-dessous.",
  "publicQuote.signatureBeforePayTitle": "Signez pour autoriser le paiement",
  "publicQuote.signatureBeforePayHint":
    "Tracez votre signature, appuyez sur « Utiliser la signature », puis continuez vers le paiement. S’applique à l’acompte ou au montant total.",
  "publicQuote.signatureRequired": "Enregistrez votre signature avant de payer.",
  "publicQuote.signatureCaptured": "Signature enregistrée. Vous pouvez effacer et recommencer si besoin.",
  "publicQuote.signatureClear": "Effacer",
  "publicQuote.signatureUse": "Utiliser la signature",
  "publicQuote.signatureStepIntro":
    "Tracez votre signature, appuyez sur « Utiliser la signature », puis continuez vers Stripe. Effacer le bloc efface la signature enregistrée jusqu’à une nouvelle sauvegarde.",
  "publicQuote.continueToSignatureButton": "Continuer vers la signature — {{amount}}",
  "publicQuote.continueToCheckoutButton": "Continuer vers le paiement sécurisé — {{amount}}",
  "publicQuote.backFromSignatureStep": "Retour",
  "publicQuote.paymentStartFailed": "Impossible de démarrer le paiement",
  "publicQuote.askPlaceholder":
    "Demandez pourquoi une ligne est incluse, ce que change l’option moins chère, ou quelle option vous convient.",
  "publicQuote.adjustPlaceholder": "Que souhaitez-vous ajuster ?",
  "publicQuote.languageViewUpdated": "Langue de la page mise à jour. Le texte est traduit uniquement pour l’affichage.",
  "publicQuote.bannerP1":
    "Aucun compte requis — vous pouvez lire ce devis, accepter, payer et écrire à votre artisan depuis cette page.",
  "publicQuote.bannerPortalLink": "Ouvrir le portail client",
  "publicQuote.bannerP2":
    " avec votre e-mail (facultatif) pour voir tous les devis qui vous ont été envoyés et suivre les mises à jour au même endroit.",
  "publicQuote.scopeHeading": "Périmètre des travaux",
  "publicQuote.datesHeading": "Dates importantes et calendrier",
  "publicQuote.datesIntro":
    "La fenêtre d’intervention ci-dessous est indicative jusqu’à ce que vous acceptiez ce devis et que l’artisan confirme le planning.",
  "publicQuote.validUntilPrefix": "Prix proposés jusqu’au ",
  "publicQuote.validUntilSuffix":
    ". C’est le dernier jour où ces lignes et totaux sont proposés tels qu’écrits — ce n’est pas la date de fin des travaux.",
  "publicQuote.tentativeOnsite": "Fenêtre indicative sur site : ",
  "publicQuote.startTbd": "Début à confirmer",
  "publicQuote.to": " à ",
  "publicQuote.contractorNotePrefix": "De votre artisan : ",
  "publicQuote.jobSiteHeading": "Chantier",
  "publicQuote.photosHeading": "Photos de chantier et plans",
  "publicQuote.pricingHeading": "Tarification",
  "publicOptions.heading": "Choisissez une option",
  "publicOptions.intro": "Choisissez l'option qui vous convient le mieux. Votre entrepreneur vous recontactera pour finaliser les détails.",
  "publicOptions.recommended": "Recommandée",
  "publicOptions.timeframeLabel": "Délai",
  "publicOptions.choose": "Choisir cette option",
  "publicOptions.selected": "Sélectionnée",
  "publicOptions.chosenBadge": "Votre choix",
  "publicOptions.confirm": "Confirmer mon choix",
  "publicOptions.confirming": "Enregistrement…",
  "publicOptions.confirmHint": "Aucun paiement ni signature requis — ceci indique simplement à votre entrepreneur l'option que vous préférez.",
  "publicOptions.confirmError": "Nous n'avons pas pu enregistrer votre choix. Veuillez réessayer.",
  "publicOptions.confirmedTitle": "Choix envoyé",
  "publicOptions.confirmedBody": "Merci ! Votre entrepreneur a été informé de votre sélection.",
  "publicOptions.lockedTitle": "Vous avez fait votre choix — votre entrepreneur vous recontactera.",
  "publicOptions.lockedBody": "Vous avez déjà choisi une option sur ce devis.",
  "publicQuote.addonHint":
    "Utilisez Inclure pour ajouter ou retirer des options. Le total et le paiement se mettent à jour automatiquement.",
  "publicQuote.itemizedHidden":
    "Le détail des lignes n’apparaît pas sur ce devis. Le total reflète l’ensemble du périmètre chiffré par votre artisan.",
  "publicQuote.subtotalLabel": "Sous-total",
  "publicQuote.discountLabel": "Remise",
  "publicQuote.taxLineLabel": "TVA / taxes ({{rate}}%)",
  "publicQuote.markupLineLabel": "Marge",
  "publicQuote.totalLabel": "Total",
  "publicQuote.paymentRefunded": "Le paiement a été remboursé. Contactez votre artisan pour toute question.",
  "publicQuote.paymentPaidFull": "Payé intégralement. Merci !",
  "publicQuote.statusAcceptedTitle": "Accepté",
  "publicQuote.statusPaidInFull": "Payé intégralement",
  "publicQuote.statusAcceptedNoPaymentYet": "Vous avez accepté ce devis. Votre entrepreneur a été notifié.",
  "publicQuote.statusAcceptedBalanceDue": "Premier paiement de {{paid}} reçu. Le solde de {{balance}} est dû dans un paiement séparé lorsque les travaux approchent de leur fin — revenez sur cette même page pour le payer.",
  "publicQuote.statusAcceptedReturnLater": "Vous pouvez toujours revenir sur cette page pour consulter le devis, payer le prochain acompte ou envoyer un message à votre entrepreneur.",
  "publicQuote.paymentBelowDecision": "L’acceptation finale et le paiement figurent sous le guide de décision.",
  "publicQuote.changeOrdersHeading": "Avenants",
  "publicQuote.changeOrdersIntro":
    "Travaux supplémentaires envoyés après le devis initial. En accepter un, le montant s’ajoute à votre total.",
  "publicQuote.coAwaiting": "En attente de votre accord",
  "publicQuote.coAccepted": "Accepté",
  "publicQuote.coAcceptBtn": "Accepter l’avenant",
  "publicQuote.coSaving": "Enregistrement…",
  "publicQuote.decisionHeading": "Guide de décision",
  "publicQuote.decisionAskTitle": "Poser une question sur ce devis",
  "publicQuote.decisionGoalClarity": "M’aider à comprendre",
  "publicQuote.decisionGoalBudget": "Moins cher au départ",
  "publicQuote.decisionGoalLongevity": "Meilleure valeur sur la durée",
  "publicQuote.decisionGoalSpeed": "Le plus rapide à réaliser",
  "publicQuote.decisionGoalConfidence": "M’aider à choisir sereinement",
  "publicQuote.decisionGoalHint": "Choisissez ce qui compte le plus, puis tapez ou modifiez votre question ci-dessous.",
  "publicQuote.decisionThinking": "Réflexion…",
  "publicQuote.decisionGetGuidance": "Obtenir des conseils",
  "publicQuote.decisionSuggestedNext": "Prochaine étape suggérée :",
  "publicQuote.regretHeading": "Confiance et sérénité",
  "publicQuote.regretProtects": "Ce qui vous protège",
  "publicQuote.regretWhatNext": "La suite du processus",
  "publicQuote.warrantyHeading": "Garantie",
  "publicQuote.notesHeading": "Notes",
  "publicQuote.acceptHeading": "Accepter et payer",
  "publicQuote.acceptIntro": "Relisez cette dernière étape, puis un seul bouton pour accepter le devis et payer.",
  "publicQuote.acceptRecommended": "Recommandée",
  "publicQuote.acceptedTitle": "Vous avez accepté ce devis. Merci !",
  "publicQuote.acceptedRecommended": "Option recommandée choisie :",
  "publicQuote.requestChanges": "Demander des modifications",
  "publicQuote.requestChangesTitle": "Demander des modifications",
  "publicQuote.changeRequestDescription": "Indiquez à votre entrepreneur ce qu'il doit modifier sur cette estimation.",
  "publicQuote.changeRequestFieldLabel": "Modifications demandées",
  "publicQuote.sendToContractor": "Envoyer à l’artisan",
  "publicQuote.colInclude": "Inclure",
  "publicQuote.colDescription": "Description",
  "publicQuote.colQty": "Qté",
  "publicQuote.colUnit": "Unité",
  "publicQuote.transactionFeeLineLabel": "Frais de transaction Stripe",
  "publicQuote.colPrice": "Prix ($)",
  "publicQuote.colTotal": "Total ($)",
  "publicQuote.colType": "Type",
  "publicQuote.colReadyBy": "Prêt pour",
  "publicQuote.optionalAddon": "Optionnel",
  "publicQuote.lineKindLabor": "Main-d’œuvre",
  "publicQuote.lineKindMaterial": "Matériels et équipement",
  "publicQuote.lineKindSubcontractor": "Sous-traitance",
  "publicQuote.lineKindFee": "Frais et autorisations",
  "publicQuote.lineKindOther": "Autre",
  "publicQuote.lineItemsFallback": "Lignes de devis",
  "publicQuote.nextPayFull": "Payer la totalité",
  "publicQuote.nextPayDeposit": "Payer l’acompte de {{pct}} %",
  "publicQuote.nextPayBalance": "Payer le solde",
  "publicQuote.estimatedTotal": "Total estimé {{amount}}",
  "publicQuote.loadTitle": "Impossible d’ouvrir ce devis",
  "publicQuote.loadNotFound":
    "Nous n’avons pas de devis pour ce lien. Vérifiez l’URL ou demandez à votre artisan de vous en envoyer un nouveau.",
  "publicQuote.loadServer":
    "Impossible de charger ce devis. Attendez un instant et réessayez le lien. Si ça échoue encore, demandez à votre artisan de le renvoyer ou de vérifier sa configuration.",
  "publicQuote.loadNetwork": "Impossible de joindre le serveur. Vérifiez votre connexion internet, puis réessayez.",
  "publicQuote.notPayingFullTitle": "Vous ne payez pas la totalité aujourd’hui",
  "publicQuote.notPayingFullLi1":
    "Ce paiement seulement : {{amount}} ({{depositPct}} % de {{total}}). Votre carte est débitée de ce montant uniquement — pas du devis entier.",
  "publicQuote.notPayingFullLi2":
    "Ensuite : les {{remainder}} restants ({{balancePct}} %) sont payés lors d’un second paiement sur cette même page, quand votre artisan est prêt.",
  "publicQuote.balanceDueTitle": "Solde à payer",
  "publicQuote.balanceDueBody":
    "Vous avez déjà payé {{paid}}. Ce paiement ne concerne que le reste {{remaining}} — rien de plus.",
  "publicQuote.partialPaidSummary": "Déjà payé : {{paid}}. Reste : {{remaining}}.",
  "publicQuote.paymentTermsHeading": "Modalités de paiement",
  "publicQuote.additionalTermsHeading": "Conditions supplémentaires",
  "publicQuote.agreeTermsPrefix": "J’ai lu et j’accepte les",
  "publicQuote.termsOfService": "Conditions d’utilisation",
  "publicQuote.agreePrivacyPrefix": "J’ai lu et j’accepte la",
  "publicQuote.privacyPolicy": "Politique de confidentialité",
  "publicQuote.agreeJorvioPortalLead": "J’ai lu et j’accepte les",
  "publicQuote.agreeJorvioPortalMid": " et la ",
  "publicQuote.agreeJorvioPortalTail":
    " de la plateforme Jorvio Quotes qui héberge ce devis (ce n’est pas le même cadre que votre accord avec l’artisan pour les travaux).",
  "publicQuote.agreeStripePaymentLead": "Pour un paiement par carte, je comprends que Stripe traitera le débit et j’accepte",
  "publicQuote.stripePaymentTermsLink": "les conditions Stripe applicables à ce paiement",
  "publicQuote.agreeStripePaymentTail": " (en plus du montant affiché sur le bouton Payer).",
  "publicQuote.payConfirmShort":
    "Vous confirmez comprendre le montant sur le bouton de paiement et le périmètre de l’artisan sur ce devis.",
  "publicQuote.payConfirmWithProcessingShort":
    "Vous confirmez comprendre le total sur le bouton (frais de transaction Stripe indiqués comme estimation approximative) et le périmètre de l’artisan sur ce devis.",
  "publicQuote.processingFeeTitle": "Frais de transaction Stripe",
  "publicQuote.processingFeeRoughHint": "estimation approximative",
  "publicQuote.processingFeeBody":
    "Une ligne « frais de transaction Stripe » (estimation approximative) est ajoutée au paiement — environ {{processing}} sur ce débit. Votre carte est débitée de {{total}} au total ; les frais réels peuvent varier.",
  "publicQuote.processingFeeBasisNote":
    "Ce montant estimé repose sur le total de ce devis (taxes comprises le cas échéant) et s’ajoute au paiement par carte ; il ne fait pas partie du sous-total des travaux ci-dessus.",
  "publicQuote.redirectingCheckout": "Redirection vers le paiement…",
  "publicQuote.acceptPayButton": "Accepter le devis et payer — {{amount}}",
  "publicQuote.coLineDefault": "Ligne",
  "publicQuote.photoAlt": "Chantier ou plan {{n}}",
  "publicQuote.tryAgain": "Réessayez.",
  "publicQuote.couldNotUpdateAddon": "Impossible de mettre à jour l’option",
  "publicQuote.couldNotAcceptCo": "Impossible d’accepter l’avenant",
  "publicQuote.coAcceptedToast": "Avenant accepté",
  "publicQuote.coAcceptedToastDesc": "Le total de votre devis a été mis à jour.",

  "accept.signFirst": "Signez d’abord",
  "accept.accepted": "Devis accepté !",

  "calendar.entryUpdated": "Entrée mise à jour",
  "calendar.loadFailed": "Impossible de charger le calendrier",

  "editor.toast.couldNotSend": "Impossible d’envoyer",
  "editor.toast.saveFailed": "Échec de l’enregistrement",
  "editor.toast.saved": "Enregistré",
  "editor.toast.sendFixSave": "Corrigez d’abord l’erreur d’enregistrement.",
  "editor.toast.sendFailed": "Échec de l’envoi",
  "editor.toast.emailSent": "E-mail envoyé",
  "editor.toast.duplicateFailed": "Échec de la duplication",
  "editor.toast.updateFailed": "Échec de la mise à jour",
  "editor.toast.markedDeclined": "Marqué comme refusé",
  "editor.toast.archiveFailed": "Échec de l’archivage",
  "editor.toast.enterCustomerName": "Entrez le nom du client",
  "editor.toast.customerAttached": "Client rattaché",
  "editor.toast.templateSaveFailed": "Impossible d’enregistrer le modèle",
  "editor.toast.templateSaved": "Modèle enregistré",
  "editor.toast.enterName": "Entrez un nom",
  "editor.toast.directoryNameHint": "Fournisseur, sous-traitant ou contact payé.",
  "editor.toast.addedDirectory": "Ajouté au répertoire",
  "editor.toast.assistantUpdated": "L’assistant devis a mis à jour le devis",
  "editor.toast.quoteHelperSaveOk": "Mise à jour enregistrée",
  "editor.previewPdf": "Aperçu PDF",
  "editor.backToQuotes": "Retour aux devis",
  "editor.customerShowItemizedPricing": "Afficher le détail des lignes sur le devis client",
  "editor.customerShowItemizedPricingHelp":
    "Désactivé : le client ne voit que le total sur le lien, l’aperçu et le PDF (pas de bascules d’options). Vos options enregistrées influencent encore le montant.",
  "editor.customerAcceptanceIntro": "Contrôlez comment le lien client gère le paiement et les signatures.",
  "editor.collectPayment": "Encaisser sur ce devis (via Stripe)",
  "editor.collectPaymentHelp":
    "Activé : paiement sur le lien si un compte de versement est relié dans les paramètres. Désactivé : acceptation sans paiement en ligne ; vous encaissez autrement.",
  "editor.collectPaymentBlocked":
    "Reliez un compte de versement pour activer le paiement sur le lien du devis.",
  "editor.collectPaymentBlockedSettingsLink": "Ouvrir les paramètres de versement",
  "editor.collectPaymentBlockedTail":
    "Vous pouvez envoyer le devis par e-mail ou PDF sans compte de versement.",
  "editor.collectPaymentDepositTiming":
    "Les fonds arrivent sur votre compte bancaire environ 2 jours ouvrés après chaque paiement (calendrier standard de Stripe). Le tout premier versement peut prendre 7 à 14 jours, le temps que Stripe vérifie le compte.",
  "editor.processingFeeBasisNote":
    "L’estimation est basée sur le total client (votre travail cotisé, taxes et remises indiqués), puis ajoutée au paiement par-dessus ; elle ne réduit pas votre marge.",
  "editor.requireCustomerSignature": "Exiger la signature du client",
  "editor.requireCustomerSignatureHelp":
    "Activé : ils doivent dessiner une signature avant d’accepter. Désactivé : ils peuvent accepter d’un tap.",
  "editor.duplicate": "Dupliquer",
  "editor.saveTemplate": "Enregistrer comme modèle",
  "editor.archiveTitleTip":
    "Archivez pour retirer de la liste principale. Archivage supprimé automatiquement après 60 jours.",
  "editor.declineTitleTip":
    "Si le client refuse. Met à jour le statut seulement ; pas d’e-mail au client.",
  "editor.saveTitleTip": "Enregistrement automatique quelques secondes après l’édition",
  "editor.save": "Enregistrer",
  "editor.sendToCustomer": "Envoyer au client",
  "editor.markDeclined": "Marquer refusé",
  "editor.archive": "Archiver",
  "editor.customerAcceptance": "Acceptation client",
  "editor.customer": "Client",
  "editor.noCustomer": "Aucun client",
  "editor.createAttach": "Créer et rattacher",
  "editor.sitePhotos": "Photos chantier & annotations plan",
  "editor.quoteDetails": "Détails du devis",
  "editor.schedule": "Calendrier / délais",
  "editor.decisionMode": "Mode décision",
  "editor.decisionModeHelp":
    "Activez pour ajouter jusqu’à trois options client (par exemple Bon / Mieux / Optimal). Ils voient une comparaison sur leur devis et l’aide pour poser des questions. Idéal quand vous proposez de vrais forfaits, pas un seul prix fixe.",
  "editor.regretProof": "Confiance et sérénité",
  "editor.trustReassuranceHelp":
    "Texte court que le client voit près du prix : pourquoi dire oui sereinement, note optionnelle d’attente ou de coût, puces de confiance et prochaines étapes. À utiliser sur les gros chantiers ou s’ils comparent des offres ; à éviter pour les petites interventions.",
  "editor.missingCompanyNameBanner":
    "Les clients voient votre entreprise sur le devis. Ajoutez-le sous",
  "editor.missingCompanyNameSettingsLink": "Infos entreprise",
  "editor.internalCosts": "Interne : coûts, marge et approvisionnement",
  "editor.addCostLine": "Ajouter une ligne de coût",
  "editor.quickAddContact": "Ajout rapide de contact",
  "editor.lineItemsCheckout": "Lignes, tarifs et paiement",
  "editor.warrantyNotes": "Garantie et autres notes",
  "editor.removeCostLine": "Supprimer la ligne de coût",
  "editor.ph.customerName": "Marie Dupont",
  "editor.ph.customerEmail": "marie@email.com",
  "editor.ph.customerPhone": "(555) 123-4567",
  "editor.ph.summaryCustomer": "Visible sur leur lien de devis — clair et actionnable.",
  "editor.ph.signedNote": "Souvent rempli à l’acceptation. Modifiable ici.",
  "editor.ph.scheduleNote":
    "Langage simple : arrivée des matériaux, début des travaux, dates sur site provisoires.",
  "editor.ph.optionLabel": "Bon / Mieux / Optimal",
  "editor.ph.optionBestFor": "À quoi correspond cette option",
  "editor.ph.optionTotal": "Total de l’option",
  "editor.ph.recommendedSummary": "Résumé recommandé pour le client",
  "editor.ph.tradeoff": "Compromis prix / durabilité / délai",
  "editor.ph.budgetGuidance": "Conseils budget serré",
  "editor.ph.longevityGuidance": "Conseils durabilité",
  "editor.ph.faq": "FAQ, une par ligne : Question | Réponse",
  "editor.ph.comparison": "Comparaison : Libellé | Option | Valeur",
  "editor.ph.confidence": "Pourquoi ce choix est cohérent et rassurant",
  "editor.ph.waitRisk": "Si le client attend : planning, prix, périmètre",
  "editor.ph.trustSignals": "Signaux de confiance, une par ligne",
  "editor.ph.whatNext": "Étapes suivantes, une par ligne",
  "editor.ph.supplierName": "Fournisseur, sous-traitant, entreprise…",
  "editor.ph.costDescription": "Brève description de ce coût",
  "editor.ph.internalNotes": "Interne seulement — pas sur le PDF client",
  "editor.nav.quickNotes": "Notes rapides",
  "editor.quickNotes.title": "Notes rapides",
  "editor.quickNotes.description":
    "Rappels datés pour votre équipe sur ce devis. Invisibles pour le client.",
  "editor.quickNotes.placeholder": "Ajouter un rappel…",
  "editor.quickNotes.add": "Ajouter la note",
  "editor.quickNotes.adding": "Ajout…",
  "editor.quickNotes.empty": "Aucune note rapide pour l’instant.",
  "editor.quickNotes.delete": "Supprimer",
  "editor.quickNotes.deleteTitle": "Retirer cette note",
  "editor.quickNotes.toastAdded": "Enregistré dans les notes internes",
  "editor.quickNotes.toastDeleted": "Note supprimée",
  "editor.quickNotes.toastSaveFailed": "Impossible d’ajouter la note",
  "editor.quickNotes.toastDeleteFailed": "Impossible de supprimer la note",
  "editor.quickNotes.inlinePlaceholder": "Saisissez une note ; Entrée ou Ajouter",
  "editor.quickNotes.removeTitle": "Retirer la note",
  "editor.ph.depositNotSet": "Non défini",
  "editor.ph.warranty": "Garantie main-d’œuvre, fabricant, etc.",
  "editor.ph.finePrint": "Mentions, exclusions ou contexte sur la vue client",
  "editor.invalidDateYmd": "Utilisez AAAA-MM-JJ pour la date prévue.",

  "help.title": "Aide",
  "help.subtitle":
    "Aide générale sur le produit. Il ne voit pas vos devis, clients ni données de compte.",
  "help.contactTitle": "Nous contacter",
  "help.contactBody":
    "Besoin d’un membre de l’équipe ? Utilisez le formulaire ci-dessous.",
  "help.contactButton": "Envoyer le message",
  "help.escalateBanner":
    "Nous n’avons pas pu répondre avec l’aide générale seule. Ouvrez Nous contacter (le formulaire sur cette page).",
  "help.aiUnavailable":
    "L’assistant d’aide n’est pas disponible pour le moment. Utilisez le formulaire ci-dessous.",
  "help.placeholder": "ex. Où j’envoie le devis à mon client ?",
  "help.send": "Demander",
  "help.sending": "Réflexion…",
  "help.clear": "Effacer la discussion",
  "help.you": "Vous",
  "help.assistant": "Assistant d’aide",
  "help.assistantCardDescription":
    "Posez une question sur le fonctionnement du produit ou où trouver une option. Cet assistant n’a que des connaissances générales—il ne voit pas vos devis ni votre compte.",
  "help.assistantTyping": "Réponse en cours…",
  "help.disclaimer":
    "Ne collez pas de mots de passe ni de données confidentielles clients ou paiement ici.",

  "help.bugReport.title": "Signaler un bug",
  "help.bugReport.subtitle":
    "Décrivez ce qui a dysfonctionné et ce que vous faisiez. Des photos facultatives nous aident à reproduire le cas. Nous lisons chaque envoi.",
  "help.bugReport.descriptionLabel": "Ce qui s’est mal passé",
  "help.bugReport.descriptionPlaceholder": "Décrivez ce qui s’est mal passé — plus c’est détaillé, mieux c’est.",
  "help.bugReport.contextLabel": "Que faisiez-vous au moment du problème ?",
  "help.bugReport.contextPlaceholder": "Que faisiez-vous juste avant ? Détaillez les étapes.",
  "help.bugReport.screenshotsLabel": "Photos (facultatif)",
  "help.bugReport.screenshotsHint": "Jusqu’à 4 images (PNG, JPEG, WebP ou GIF), 4 Mo chacune.",
  "help.bugReport.submit": "Envoyer le rapport",
  "help.bugReport.submitting": "Envoi…",
  "help.bugReport.successTitle": "Merci — nous avons bien reçu votre rapport",
  "help.bugReport.successBody":
    "Notre équipe examinera les informations envoyées. Vous n’avez rien d’autre à faire ; nous vous contacterons par e-mail si nous avons besoin de précisions.",
  "help.bugReport.sendAnother": "Envoyer un autre rapport",
  "help.bugReport.descTooShort": "Ajoutez un peu plus de détail (au moins 10 caractères) dans « Ce qui s’est mal passé ».",
  "help.bugReport.contextTooShort": "Précisez un peu plus ce que vous faisiez (au moins 5 caractères).",
  "help.bugReport.fileTooLarge": "Chaque image doit faire 4 Mo ou moins.",
  "help.bugReport.fileTypeInvalid": "Seules les images PNG, JPEG, WebP ou GIF sont acceptées.",
  "help.bugReport.failedTitle": "Impossible d’envoyer le rapport",

  "help.attachments.dropTitle": "Ajouter des photos",
  "help.attachments.dropSubtitle": "Glissez-déposez ou cliquez — PNG, JPEG, WebP ou GIF.",
  "help.attachments.removePhoto": "Retirer la photo",

  "help.contactForm.jumpToForm": "Aller au formulaire de contact",
  "help.contactForm.subjectLabel": "Objet (facultatif)",
  "help.contactForm.subjectPlaceholder": "ex. Facturation / connexion",
  "help.contactForm.messageLabel": "Message",
  "help.contactForm.messagePlaceholder":
    "Soyez aussi précis que possible. Dites-nous exactement ce qui s'est passé juste avant le problème et les étapes que vous avez suivies.",
  "help.contactForm.hint":
    "Nous lisons chaque message. Ajoutez des photos ci-dessous si besoin. Pour un dysfonctionnement vous pouvez aussi utiliser Signaler un bug ci-dessus.",
  "help.contactForm.attachmentsLabel": "Photos (facultatif)",
  "help.contactForm.attachmentsHint": "Jusqu’à 4 images (PNG, JPEG, WebP ou GIF), 4 Mo chacune.",
  "help.contactForm.submit": "Envoyer à l’équipe",
  "help.contactForm.submitting": "Envoi…",
  "help.contactForm.successTitle": "Message envoyé",
  "help.contactForm.successBody":
    "Notre équipe traitera votre message. Si nous avons besoin de précisions, nous répondrons sur l’adresse de votre compte.",
  "help.contactForm.successBodyGuest":
    "Notre équipe traitera votre message. Si nous avons besoin de précisions, nous répondrons à l’adresse e-mail que vous avez saisie ci-dessus.",
  "help.contactForm.sendAnother": "Envoyer un autre message",
  "help.contactForm.failedTitle": "Envoi impossible — réessayez.",

  "contactPage.signedInNote":
    "Vous nous contactez depuis votre compte connecté : {{name}}, {{email}}. Nous répondrons à cette adresse.",
  "contactPage.guestNameLabel": "Votre nom",
  "contactPage.guestEmailLabel": "Votre e-mail",
  "contactPage.guestNamePlaceholder": "Nom complet",
  "contactPage.guestEmailPlaceholder": "vous@exemple.com",
  "marketing.contact.pageTitle": "Nous contacter",
  "marketing.contact.pageSubtitle":
    "Écrivez à l’équipe Jorvio. Nous répondrons sur l’adresse de votre compte ou sur celle indiquée ci-dessous.",
  "help.agencyMailtoIntro":
    "Votre offre inclut un support prioritaire. Pour une réponse rapide, envoyez-nous un e-mail depuis votre messagerie.",
  "help.agencyMailtoCta": "Ouvrir la messagerie pour contacter le support",

  "help.triage.title": "Quel est votre problème ?",
  "help.triage.subtitle": "Répondez à quelques questions — nous proposerons d’abord une solution à essayer.",
  "help.triage.back": "Retour",
  "help.triage.startOver": "Recommencer",
  "help.triage.option.other": "Autre",
  "help.triage.pendingTopicLabel": "Le prochain message inclura le sujet :",
  "help.triage.chatTopicWrap": "[Sujet d’aide : {{topic}}]",
  "help.triage.tryThisTitle": "Essayez d’abord ceci",
  "help.triage.didNotWork": "Ça n’a pas marché — parler à l’assistant",
  "help.triage.contactInstead": "Écrire à l’équipe",
  "help.triage.reportBugInstead": "Ouvrir Signaler un bug avec captures",
  "help.triage.describeIssuePlaceholder":
    "Décrivez le problème : ce que vous avez essayé, ce que vous attendiez et ce qui s’est passé.",
  "help.triage.cta.ai": "Continuer vers l’assistant d’aide",
  "help.triage.cta.contact": "Ouvrir le formulaire de contact",
  "help.triage.cta.bug": "Ouvrir Signaler un bug",

  "help.triage.prompt.root": "Qu’est-ce qui décrit le mieux votre problème ?",
  "help.triage.prompt.account": "Quel sujet de connexion ou de compte correspond le mieux ?",
  "help.triage.prompt.quotes": "Quelle partie des devis ou de l’envoi pose problème ?",
  "help.triage.prompt.payments": "Quel sujet de paiement ?",
  "help.triage.prompt.subscription": "Quel sujet de facturation ?",
  "help.triage.prompt.calendar": "Quel sujet de calendrier ou d’équipe ?",
  "help.triage.prompt.customers": "Quel problème avec les clients ?",
  "help.triage.prompt.settings": "Quelle zone des paramètres ?",
  "help.triage.prompt.portal": "Quel problème avec le portail propriétaire ?",
  "help.triage.prompt.bug": "Quel type de problème ?",

  "help.triage.opt.account": "Connexion, mot de passe ou accès à l’espace",
  "help.triage.opt.quotes": "Créer, modifier ou envoyer des devis",
  "help.triage.opt.payments": "Paiement client ou virements Stripe",
  "help.triage.opt.subscription": "Votre offre ou facture Jorvio",
  "help.triage.opt.calendar": "Calendrier entreprise ou accès équipe",
  "help.triage.opt.customers": "Fiches clients",
  "help.triage.opt.settings": "Profil, marque, Stripe Connect, équipe",
  "help.triage.opt.portal": "Lien de devis ou connexion portail",
  "help.triage.opt.bug": "Quelque chose semble cassé",

  "help.triage.opt.acct.login": "Connexion, lien magique ou e-mail de vérification",
  "help.triage.opt.acct.invite": "Inviter des co-opérateurs ou accès équipe",
  "help.triage.opt.acct.security": "Mot de passe ou sessions / appareil",
  "help.triage.opt.acct.access": "Blocage, mauvais espace ou accès non résolu dans l’app",

  "help.triage.opt.quotes.send": "Envoyer au client, file d’attente ou « non envoyé »",
  "help.triage.opt.quotes.email": "Le client n’a pas reçu l’e-mail du devis",
  "help.triage.opt.quotes.pdf": "PDF ou aperçu incorrect",
  "help.triage.opt.quotes.editor": "Lignes, prix ou enregistrement du brouillon",
  "help.triage.opt.quotes.pay": "Acompte / paiement sur la page publique",

  "help.triage.opt.pay.checkout": "Le propriétaire paie sur le lien (carte)",
  "help.triage.opt.pay.connect": "Connecter Stripe ou terminer l’inscription",
  "help.triage.opt.pay.payout": "Virements, banque ou vérification Stripe",
  "help.triage.opt.pay.dispute": "Remboursement, rétrofacturation ou litige",

  "help.triage.opt.sub.plan": "Changer d’offre, surclasser/rétrograder ou résilier",
  "help.triage.opt.sub.charge": "Prélèvement inattendu ou montant incorrect",
  "help.triage.opt.sub.receipt": "Reçu, facture ou historique",

  "help.triage.opt.cal.crew": "Code équipe, /crew ou accès calendrier",
  "help.triage.opt.cal.events": "Événements, dates ou calendrier incohérent",
  "help.triage.opt.cal.schedule": "Planification sur devis ou calendrier",

  "help.triage.opt.cust.directory": "Ajouter ou modifier des clients",
  "help.triage.opt.cust.wrong": "Infos client incorrectes sur un devis",

  "help.triage.opt.set.stripe": "Stripe Connect, paiements ou réglages",
  "help.triage.opt.set.brand": "Logo, langue ou coordonnées entreprise",
  "help.triage.opt.set.reviews": "E-mails de demande d'avis Google",
  "help.triage.opt.set.team": "Codes co-opérateur, équipe ou invitations",

  "help.triage.opt.port.otp": "Code e-mail du portail ou connexion",
  "help.triage.opt.port.view": "Ce qu’ils voient sur le lien ou le portail",

  "help.triage.opt.bug.crash": "Message d’erreur, écran blanc ou bug évident",
  "help.triage.opt.bug.data": "Montants incorrects ou devis manquants",
  "help.triage.opt.bug.slow": "Chargement lent ou délais",

  "help.triage.summary.other": "Autre problème",
  "help.triage.summary.acctLogin": "Connexion / lien magique / e-mail",
  "help.triage.summary.acctInvite": "Invitations / accès équipe",
  "help.triage.summary.acctSecurity": "Mot de passe / sessions",
  "help.triage.summary.acctAccess": "Accès compte / espace",
  "help.triage.summary.quotesSend": "Envoi au client",
  "help.triage.summary.quotesEmail": "E-mail non reçu",
  "help.triage.summary.quotesPdf": "PDF ou aperçu",
  "help.triage.summary.quotesEditor": "Éditeur / lignes",
  "help.triage.summary.quotesPay": "Paiement lien public",
  "help.triage.summary.payCheckout": "Paiement propriétaire",
  "help.triage.summary.payConnect": "Stripe Connect",
  "help.triage.summary.payPayout": "Virements Stripe / banque",
  "help.triage.summary.payDispute": "Remboursement / litige",
  "help.triage.summary.subPlan": "Changement d’offre / résiliation",
  "help.triage.summary.subCharge": "Prélèvement inattendu",
  "help.triage.summary.subReceipt": "Reçu / facture",
  "help.triage.summary.calCrew": "Équipe / calendrier",
  "help.triage.summary.calEvents": "Événements",
  "help.triage.summary.calSchedule": "Planification",
  "help.triage.summary.custDirectory": "Clients",
  "help.triage.summary.custWrong": "Données incorrectes",
  "help.triage.summary.setStripe": "Stripe / paiements",
  "help.triage.summary.setBrand": "Marque / langue",
  "help.triage.summary.setReviews": "Demandes d'avis Google",
  "help.triage.summary.setTeam": "Équipe / invitations",
  "help.triage.summary.portOtp": "OTP portail",
  "help.triage.summary.portView": "Vue propriétaire",
  "help.triage.summary.bugCrash": "Bug / erreur",
  "help.triage.summary.bugData": "Données incorrectes ou manquantes",
  "help.triage.summary.bugSlow": "Lent / délai",

  "help.triage.solution.other":
    "- Rafraîchissez la page ou essayez un autre navigateur (Chrome ou Edge).\n- Vérifiez que vous êtes dans le bon espace.\n- Notez le **message d’erreur** ou l’URL si vous passez à l’assistant.",
  "help.triage.solution.acctLogin":
    "- Regardez les **indésirables** pour les e-mails Jorvio.\n- Attendez ~60 s entre deux liens magiques.\n- Essayez une fenêtre privée pour isoler les extensions.\n- Utilisez **Mot de passe oublié** seulement si vous utilisez un mot de passe.",
  "help.triage.solution.acctInvite":
    "- Vérifiez que l’e-mail d’invitation correspond à la connexion.\n- Demandez de regarder spam.\n- Renvoyez l’invitation depuis l’équipe si possible.",
  "help.triage.solution.acctSecurity":
    "- Déconnectez les autres sessions dans les réglages si besoin.\n- Changez le mot de passe uniquement via l’app.\n- N’envoyez jamais mots de passe ou codes par message.",
  "help.triage.solution.acctContact":
    "- Confirmez le **bon e-mail** attendu par l’admin.\n- Essayez un autre appareil ou navigateur.\n- Bloqué sans issue en ligne : **Écrire à l’équipe** — pas de mots de passe.",
  "help.triage.solution.quotesSend":
    "- Ouvrez le devis : **brouillon ou envoyé**.\n- Utilisez **Renvoyer** si proposé.\n- Rétablissez le réseau : les envois en file finissent souvent après reconnexion.",
  "help.triage.solution.quotesEmail":
    "- Vérifiez l’orthographe de l’e-mail client.\n- Demandez de chercher dans spam.\n- Renvoyez depuis le devis si possible.",
  "help.triage.solution.quotesPdf":
    "- Utilisez **Imprimer → Enregistrer en PDF** si l’export échoue.\n- Essayez Chrome sur bureau si le mobile est faux.",
  "help.triage.solution.quotesEditor":
    "- Attendez la fin de l’auto-enregistrement avant de quitter.\n- Rafraîchissez si un champ ne tient pas.\n- Dupliquez en nouveau brouillon si données incohérentes.",
  "help.triage.solution.quotesPay":
    "- Vérifiez que **Stripe Connect** est terminé dans Paramètres.\n- Ouvrez le lien public en navigation privée pour reproduire.",
  "help.triage.solution.payCheckout":
    "- Assurez-vous que les paiements sont activés et le lien valide.\n- Essayez autre carte ou navigateur ; copiez le message **Stripe**.",
  "help.triage.solution.payConnect":
    "- Terminez chaque étape d’onboarding Stripe Connect.\n- Consultez le tableau Stripe pour identité ou banque.",
  "help.triage.solution.payPayout":
    "- Vérifiez banque et identité dans Stripe.\n- Vérifiez qu’aucun blocage de virement.\n- Rassemblez dates et montants — **pas** de numéro de carte complet.",
  "help.triage.solution.payDispute":
    "- Notez ID de paiement, date et montant.\n- Pour coordination : **Écrire à l’équipe**.",
  "help.triage.solution.subPlan":
    "- Consultez offre et renouvellement dans Facturation.\n- Précisez si vous voulez changer ou résilier.",
  "help.triage.solution.subCharge":
    "- Comparez au calendrier de facturation et aux changements d’offre (prorata).\n- Cherchez le reçu dans votre boîte.",
  "help.triage.solution.subReceipt":
    "- Historique de facturation dans l’app si disponible.\n- Recherchez « facture » ou « reçu » dans les e-mails.",
  "help.triage.solution.calCrew":
    "- Régénérez ou partagez le code équipe.\n- Confirmez l’URL `/crew` et l’espace.",
  "help.triage.solution.calEvents":
    "- Contrôlez le **fuseau** vs dates du devis.\n- Filtres du calendrier si entrées manquantes.",
  "help.triage.solution.calSchedule":
    "- Comparez dates planifiées au calendrier.\n- Mettez à jour le devis puis rouvrez le calendrier.",
  "help.triage.solution.custDirectory":
    "- Modifiez le client dans **Clients** puis vérifiez le devis.",
  "help.triage.solution.custWrong":
    "- Corrigez la fiche puis rafraîchissez le devis.\n- Nouveau devis depuis la fiche corrigée si ça ne suit pas.",
  "help.triage.solution.setStripe":
    "- Paramètres Paiements / Stripe : résolvez les bandeaux.\n- Rouvrez l’onboarding Connect si incomplet.",
  "help.triage.solution.setBrand":
    "- Enregistrez logo et coordonnées puis forcez l’aperçu du lien public.",
  "help.triage.solution.setReviews":
    "- Ouvrez Paramètres → Avis et ajoutez le lien d'avis Google Business.\n- Réglez le délai et le message ; désactivez par devis dans l'éditeur si besoin.\n- Les e-mails partent uniquement après paiement intégral via checkout.",
  "help.triage.solution.setTeam":
    "- Renvoyez les invitations depuis l’équipe.\n- Révoquez les anciens codes si accès indésirable.",
  "help.triage.solution.portOtp":
    "- Spam pour le code.\n- Attendez entre deux demandes de code.\n- Bon e-mail propriétaire.",
  "help.triage.solution.portView":
    "- Ouvrez le lien en **navigation privée**.\n- Devis toujours publié et non expiré.",
  "help.triage.solution.bugReport":
    "- Rechargement forcé (**Ctrl+Maj+R** / **Cmd+Maj+R**).\n- **Signaler un bug avec captures** si ça persiste.",
  "help.triage.solution.bugWrong":
    "- Export ou capture des montants avec horodatage.\n- Vérifiez filtres avant « données perdues ».\n- **Écrire à l’équipe** avec exemples (pas de secrets).",
  "help.triage.solution.bugSlow":
    "- Wi‑Fi vs mobile, navigateur, moment de la journée.\n- Désactivez extensions lourdes un instant.",

  "owner.bugReports.title": "Rapports de problèmes",
  "owner.bugReports.subtitle": "Envois depuis Aide → Signaler un bug (photos facultatives).",
  "owner.bugReports.when": "Quand",
  "owner.bugReports.from": "De",
  "owner.bugReports.page": "Page",
  "owner.bugReports.issue": "Problème",
  "owner.bugReports.context": "Contexte",
  "owner.bugReports.shots": "Photos",
  "owner.bugReports.openImage": "Ouvrir",
  "owner.bugReports.status": "Statut",
  "owner.bugReports.notes": "Notes",
  "owner.bugReports.save": "Enregistrer",
  "owner.bugReports.empty": "Aucun rapport pour le moment.",
  "owner.bugReports.loadError": "Impossible de charger les rapports",
  "owner.bugReports.saveError": "Impossible d’enregistrer",
  "owner.bugReports.migrationTitle": "Migration requise",
  "owner.bugReports.migrationBody": "Exécutez 057_platform_bug_reports.sql dans l’éditeur SQL Supabase.",

  "owner.issuesInbox.title": "Boîte de réception support et bugs",
  "owner.issuesInbox.subtitle": "Toutes les demandes dans une seule liste (plus récentes en premier). Développez une ligne pour les détails, notes et statut.",
  "owner.issuesInbox.typeBug": "Bug",
  "owner.issuesInbox.typeSupport": "Support",
  "owner.issuesInbox.empty": "Aucun message ni rapport de bug pour l’instant.",
  "owner.issuesInbox.loadError": "Impossible de charger la boîte de réception",

  "owner.supportContact.title": "Messages support",
  "owner.supportContact.subtitle": "Demandes depuis Aide ou Paramètres (également envoyées par e-mail si configuré).",
  "owner.supportContact.when": "Quand",
  "owner.supportContact.from": "De",
  "owner.supportContact.source": "Origine",
  "owner.supportContact.page": "Page",
  "owner.supportContact.subject": "Objet",
  "owner.supportContact.message": "Message",
  "owner.supportContact.attachments": "Photos",
  "owner.supportContact.status": "Statut",
  "owner.supportContact.notes": "Notes",
  "owner.supportContact.save": "Enregistrer",
  "owner.supportContact.empty": "Aucun message pour l’instant.",
  "owner.supportContact.loadError": "Impossible de charger les messages",
  "owner.supportContact.saveError": "Impossible d’enregistrer",
  "owner.supportContact.migrationTitle": "Migration requise",
  "owner.supportContact.migrationBody":
    "Exécutez 060_platform_support_contact.sql et 061_platform_support_message_attachments.sql dans l’éditeur SQL Supabase.",

  "tour.spotlight.proposals.step1Title": "Liste des devis",
  "tour.spotlight.proposals.step1Body":
    "Recherche et tri pour retrouver vite un devis. Les filtres de statut restreignent la liste. Utilisez Nouveau devis pour en créer un autre.",
  "tour.spotlight.jobs.step1Title": "Chantiers",
  "tour.spotlight.jobs.step1Body":
    "Suivez les chantiers liés à un ou plusieurs devis—planning, totaux payés sur les devis liés et achèvement. Onglets Actifs, Terminés ou Archivés.",
  "tour.spotlight.followUps.step1Title": "Suivi et analyses",
  "tour.spotlight.followUps.step1Body":
    "Signaux de relance prioritaires et valeur encore chez les clients. Les analyses complètes nécessitent Pro ou Agency—passez à une offre supérieure dans Facturation si un cadenas s’affiche.",
  "tour.spotlight.customers.step1Title": "Clients",
  "tour.spotlight.customers.step1Body":
    "Recherchez vos contacts enregistrés. Ajoutez-les ici pour les lier lors de la création d’un devis.",
  "tour.spotlight.customers.step2Title": "Ajout rapide",
  "tour.spotlight.customers.step2Body":
    "Le nom et l’e-mail sont obligatoires pour envoyer des devis. Le téléphone est facultatif mais utile sur les documents clients.",
  "tour.spotlight.calendar.step1Title": "Calendrier d’entreprise",
  "tour.spotlight.calendar.step1Body":
    "Consultez chantiers et saisies manuelles ensemble. Ouvrez un jour ou le panneau pour notes internes, livraisons ou visites de sous-traitants.",
  "tour.spotlight.templates.step1Title": "Modèles",
  "tour.spotlight.templates.step1Body":
    "Enregistrez une structure gagnante comme modèle, puis ouvrez un nouveau brouillon en un geste. Modifiez ou supprimez à tout moment.",
  "tour.spotlight.settings.step1Title": "Onglets des paramètres",
  "tour.spotlight.settings.step1Body":
    "Ces liens changent de section : profil et image de marque, facturation et offres, équipe et accès terrain, et votre compte personnel.",
  "tour.spotlight.settings.step2Title": "Profil",
  "tour.spotlight.settings.step2Body": "Langue, coordonnées, logo et valeurs par défaut sur les devis clients.",
  "tour.spotlight.settings.step3Title": "Facturation",
  "tour.spotlight.settings.step3Body":
    "Votre abonnement Jorvio et moyen de paiement. Le versement Stripe sert seulement au paiement par carte sur le lien du devis—vous pouvez envoyer le devis par e-mail ou PDF sans compte de versement.",
  "tour.spotlight.settings.step4Title": "Équipe",
  "tour.spotlight.settings.step4Body": "Inviter des co-opérateurs, accès app équipe et visibilité du planning.",
  "tour.spotlight.settings.step5Title": "Compte",
  "tour.spotlight.settings.step5Body": "Mot de passe, sécurité et suppression du compte — liés à votre connexion.",
  "tour.spotlight.firstQuote.step1Title": "Choisir ou ajouter un client",
  "tour.spotlight.firstQuote.step1Body":
    "Choisissez un client existant ou Nouveau client. Pour un nouveau contact, nom et e-mail (téléphone facultatif), puis décrivez le chantier. Jorvio proposera des lignes que vous pourrez ajuster ensuite.",
  "tour.spotlight.firstQuote.step2Title": "Générer le brouillon",
  "tour.spotlight.firstQuote.step2Body":
    "Quand le formulaire est prêt, générez le devis. Vous arriverez dans l’éditeur pour vérifier les prix, les photos et l’envoi—e-mail ou PDF sans configurer la carte.",
  "tour.spotlight.firstQuoteChooser.step1Title": "Choisir comment deviser",
  "tour.spotlight.firstQuoteChooser.step1Body":
    "Créer avec l’IA, partir de zéro, utiliser un devis rapide sur chantier ou ouvrir un modèle — choisissez ce qui convient à ce travail.",

  "tour.app.next": "Suivant",
  "tour.app.done": "Terminé",
  "tour.app.progress": "Étape {{current}} sur {{total}}",
  "tour.app.step0Title": "Bienvenue",
  "tour.app.step0Body":
    "Une visite complète de chaque zone de la barre latérale, de haut en bas : où vivent les devis, comment chantiers et calendrier se relient, et où trouver paramètres et aide. Utilisez Suivant étape par étape, ou fermez quand vous voulez.",
  "tour.app.intro.welcomeTitle": "Bienvenue",
  "tour.app.intro.welcomeBody":
    "Un aperçu rapide des onglets principaux : Tableau de bord, Devis, Chantiers, Calendrier et Relances. Utilisez Suivant pour continuer, ou fermez pour ignorer.",
  "tour.app.intro.jobsBody":
    "Quand un client accepte un devis, il devient un chantier ici. Suivez le travail en cours, le planning et les paiements au même endroit.",
  "tour.app.intro.calendarBody": "Planning chantier et saisies manuelles au même endroit.",
  "tour.app.intro.followUpsBody": "Devis ouverts et relances prioritaires.",
  "tour.app.navDashboardTitle": "Tableau de bord",
  "tour.app.navDashboardBody": "Votre base : activité, devis ouverts et ce qui demande attention aujourd’hui.",
  "tour.app.full.navDashboardBody":
    "Votre base à la connexion. Voyez l’activité récente, les devis encore en attente du client et un aperçu rapide de ce qui demande attention aujourd’hui. Beaucoup de patrons commencent ici chaque matin avant d’ouvrir Devis ou Relances.",
  "tour.app.navQuotesTitle": "Devis",
  "tour.app.navQuotesBody": "Chaque estimation créée, envoyée ou archivée. Ouvrez-en un pour modifier, envoyer ou relancer.",
  "tour.app.full.navQuotesBody":
    "Chaque estimation créée, envoyée, consultée ou archivée. Filtrez par statut, ouvrez-en un pour modifier les lignes ou renvoyer après changements, ou transformez un devis accepté en chantier. Tout votre historique est ici, dans une seule liste.",
  "tour.app.step1Title": "Nouveau devis",
  "tour.app.step1Body":
    "Démarrez une nouvelle estimation ici. Choisissez l’assistant IA, un devis vierge, un flux rapide sur chantier ou un modèle enregistré. Tout ce que vous créez reste dans Devis jusqu’à l’envoi ou l’acceptation client.",
  "tour.app.step2Title": "Clients",
  "tour.app.step2Body":
    "Votre annuaire clients : noms, e-mails, téléphones et adresses liés aux devis passés. Choisissez un contact en créant un devis pour que estimations et fils de messages restent avec la bonne personne.",
  "tour.app.step3Title": "Chantiers",
  "tour.app.step3Body":
    "Quand un client accepte sur le portail ou que vous marquez un devis accepté, il devient un chantier ici. Suivez le travail actif, le statut, les dates au calendrier et les paiements sans fouiller de vieux PDF.",
  "tour.app.step4Title": "Calendrier d’entreprise",
  "tour.app.step4Body":
    "Planning de toute l’entreprise : chantiers acceptés avec dates, assignés et adresses de chantier, plus les entrées manuelles que vous ajoutez. Servez-vous-en pour voir la semaine et éviter de surbooker les équipes.",
  "tour.app.step5Title": "Relances",
  "tour.app.step5Body":
    "Devis envoyés sans retour, triés pour faire remonter les plus anciens en premier. Utilisez cette vue quand vous voulez une liste de relance sans parcourir tout le tableau Devis.",
  "tour.app.step6Title": "Messages",
  "tour.app.step6Body":
    "Fils du portail propriétaires et conversations liées aux devis dans une seule boîte. Activez les alertes e-mail dans Paramètres si vous voulez un avis quand quelqu’un répond sur un devis envoyé.",
  "tour.app.step7Title": "Paramètres",
  "tour.app.step7Body":
    "Profil d’entreprise, logo et couleurs sur les PDF, facturation et limites du forfait, versements optionnels par carte sur le lien et invitations d’équipe. Les versements restent optionnels : vous pouvez envoyer des devis par e-mail ou PDF sans eux.",
  "tour.app.navTemplatesTitle": "Modèles",
  "tour.app.navTemplatesBody":
    "Enregistrez lignes, sections et formulations que vous réutilisez sur des chantiers similaires. Lancez de nouveaux devis à partir d’un modèle au lieu de retaper le même forfait intérieur ou la même portée de cabinets à chaque fois.",
  "tour.app.navHelpTitle": "Aide",
  "tour.app.navHelpBody":
    "Guides et FAQ quand une fonction vous bloque. Vous pouvez aussi relancer cette visite complète depuis le menu point d’interrogation en bas de la barre latérale.",

  "tour.setup.fabLabel": "Terminer la configuration",
  "tour.setup.title": "Liste de configuration",
  "tour.setup.openSettings": "Ouvrir les paramètres",
  "tour.setup.dismiss": "Masquer le rappel",
  "tour.setup.restartTour": "Visite",
  "tour.setup.required": "À faire en priorité",
  "tour.setup.optional": "Finitions recommandées",
  "tour.setup.coopHint":
    "Les devis et le calendrier utilisent l’espace d’équipe. Complétez votre profil personnel pour être reconnu.",
  "tour.setup.item.companyName": "Ajoutez le nom de l’entreprise",
  "tour.setup.item.contactEmail": "Ajoutez un e-mail de contact sur les devis (ou gardez l’e-mail de connexion)",
  "tour.setup.item.quotePhone": "Ajoutez le téléphone affiché sur les devis clients",
  "tour.setup.item.address": "Complétez l’adresse (rue, ville, état, code postal)",
  "tour.setup.item.logo": "Ajoutez un logo pour les devis clients",
  "tour.setup.item.stripePayout":
    "Facultatif : reliez Stripe pour le paiement par carte sur le lien client. L’e-mail et le PDF fonctionnent sans.",
  "tour.setup.item.coopProfile": "Ajoutez votre nom affiché",
  "tour.setup.item.coopPhone": "Ajoutez un téléphone personnel (facultatif)",

  "tour.quoteEditor.step1Title": "Lignes et tarification",
  "tour.quoteEditor.step1Body":
    "Utilisez Suivant — pas besoin d’ajouter des lignes tout de suite. Ce tableau est ce que le client voit sur le lien, le PDF et la page publique. Ajoutez des lignes sur un vrai devis.",
  "tour.quoteEditor.step2Title": "Assistant et revue IA",
  "tour.quoteEditor.step2Body":
    "Demandez à l’assistant des reformulations ou des ajustements de prix — gardez ce qui convient, le brouillon s’enregistre tout seul. Cliquez sur l’étoile pour une revue IA des prix, du périmètre, des conditions et des objections possibles d’un client qui compare. Les deux sont inclus dans tous les forfaits (quelques actions IA par jour sur Starter et Solo ; illimitées sur Pro et Agency).",
  "tour.quoteEditor.step3Title": "Photos et annotations de plan",
  "tour.quoteEditor.step3Body":
    "Importez des photos et annotez les plans pour que l’équipe et le client partagent le même contexte visuel.",
  "tour.quoteEditor.step4Title": "Frais de carte au client",
  "tour.quoteEditor.step4Body":
    "Lorsque c’est disponible pour votre région, vous pouvez ajouter une ligne estimée de frais de carte au total. Vous respectez les règles locales sur les suppléments.",
  "tour.quoteEditor.step5Title": "Envoyer au client",
  "tour.quoteEditor.step5Body":
    "Envoyez par e-mail, partagez le lien ou téléchargez un PDF—aucun compte de versement requis pour cela. Ajoutez Stripe dans les paramètres seulement si vous voulez le paiement par carte sur la page publique. L’aperçu et le PDF suivent votre dernière sauvegarde.",
  "publicQuote.onlinePaymentsUnavailableTitle": "Le paiement par carte en ligne n’est pas encore disponible sur ce lien",
  "publicQuote.onlinePaymentsUnavailableBody":
    "Pas encore de paiement par carte. Vous pouvez lire le devis et payer autrement — contactez votre artisan. Ils peuvent activer la carte dans leur compte.",
  "publicQuote.acceptWithoutPayHeading": "Accepter ce devis",
  "publicQuote.acceptWithoutPayIntro":
    "Acceptez les conditions ci-dessous pour approuver ce devis.",
  "publicQuote.acceptOnlySignatureTitle": "Votre signature",
  "publicQuote.acceptOnlySignatureIntro": "Tracez votre signature, puis appuyez sur « Utiliser la signature » avant d’accepter.",
  "publicQuote.acceptOnlyNoSignatureNote": "Aucune signature manuscrite n’est requise sur ce devis.",
  "publicQuote.acceptWithoutPayButton": "Accepter le devis",
  "publicQuote.acceptOnlySubmitting": "Envoi…",
  "publicQuote.acceptOnlyFailedTitle": "Impossible d’accepter",
  "publicQuote.acceptOnlySuccessTitle": "Devis accepté",
  "publicQuote.acceptOnlySuccessBody": "Merci — l’entreprise a été informée.",
};
